import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import InputBase from "@mui/material/InputBase";
import SearchIcon from "@mui/icons-material/Search";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import TextField from "@mui/material/TextField";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import AutorenewRoundedIcon from "@mui/icons-material/AutorenewRounded";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import CurrencyRupeeRoundedIcon from "@mui/icons-material/CurrencyRupeeRounded";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import Popover from "@mui/material/Popover";
import Tooltip from "@mui/material/Tooltip";
import useMediaQuery from "@mui/material/useMediaQuery";
import { alpha, styled } from "@mui/material/styles";
import { useAuth } from "../../context/AuthContext";
import { getAllClients, getClientById } from "../../api/clientApi";
import { getAllVendors } from "../../api/vendorApi";
import { getAllPaymentRequests, getVendorPaymentSummary, getProjectPaymentSummary, addPaymentRequest, updatePaymentRequestStatus, updatePaymentRequest, deletePaymentRequest, uploadPaymentRequestImages, deletePaymentRequestImage } from "../../api/paymentRequestApi";
import { getAllApprovalMatrix } from "../../api/approvalMatrixApi";
import LoadingGate from "../LoadingGate";
import "../../styles.css";

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha("#f2f1f1", 1),
  "&:hover": {
    backgroundColor: alpha("#f2f1f1", 0.85),
  },
  marginRight: "16px",
  width: "100%",
  maxWidth: "400px",
  border: "1px solid #39aafc",
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  fontSize: "13px",
  width: "100%",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    width: "100%",
  },
}));

const VendorManagement = ({ accessLevel }) => {
  const { user } = useAuth();
  const isMobile = useMediaQuery("(max-width:900px)");
  const isMainUser = user?.user_type === 'main';
  const isVendorMgmtViewOnly = !isMainUser && accessLevel === 'view_only';
  const vendorAccessOf = (field) => (isMainUser ? 'full_access' : (user?.access_rights?.[field] || 'no_access'));
  // Payment Requesting is a sub-permission of Vendor Management — a view-only
  // parent forces it down to view/no_access already (clamped when saved in
  // Users > Access Rights), so checking the stored value alone is sufficient.
  const canRequestPayment = isMainUser || vendorAccessOf('vendorPaymentRequesting') === 'full_access';
  const hasTabPending = isMainUser || vendorAccessOf('vendorTabPending') !== 'no_access';
  const hasTabApproved = isMainUser || vendorAccessOf('vendorTabApproved') !== 'no_access';
  const hasTabPaid = isMainUser || vendorAccessOf('vendorTabPaid') !== 'no_access';
  const hasTabCorrection = isMainUser || vendorAccessOf('vendorTabCorrection') !== 'no_access';
  const hasTabRejected = isMainUser || vendorAccessOf('vendorTabRejected') !== 'no_access';
  const hasTabAll = isMainUser || vendorAccessOf('vendorTabAll') !== 'no_access';
  const hasTabVendorWise = isMainUser || vendorAccessOf('vendorTabVendorWise') !== 'no_access';
  // Gates the bulk Remove/Reject action itself (checkbox interactivity) —
  // separate from hasTabVendorWise, which only gates whether the tab/view is
  // visible at all. Deliberately its OWN access right, not reusing
  // vendorTabApproved/vendorTabRejected's per-status full_access checks —
  // this is a distinct cross-project bulk-action surface with its own grant.
  const canActVendorWise = isMainUser || vendorAccessOf('vendorTabVendorWise') === 'full_access';
  const hasTabProjectWise = isMainUser || vendorAccessOf('vendorTabProjectWise') !== 'no_access';
  const canActProjectWise = isMainUser || vendorAccessOf('vendorTabProjectWise') === 'full_access';
  // Whether a specific request's status tab grants full interaction (can open
  // Change Status) vs. read-only viewing — used to gate row clicks.
  const canActOnStatus = (status) => isMainUser || vendorAccessOf(
    status === "Correction" ? 'vendorTabCorrection'
    : status === "Pending" ? 'vendorTabPending'
    : status === "Approved" ? 'vendorTabApproved'
    : status === "Paid" ? 'vendorTabPaid'
    : status === "Rejected" ? 'vendorTabRejected'
    : 'vendorTabAll'
  ) === 'full_access';
  // A request sitting in "Pending" because a reviewer sent it back for a fix
  // (it carries a pending_reason) reads as "Correction" everywhere in the UI —
  // distinct from a brand-new request that's simply awaiting its first decision.
  const getDisplayStatus = (request) => (request.approval === "Pending" && request.pendingReason ? "Correction" : request.approval);
  // A request is only ever visible if its own status category has at least
  // View access — otherwise hiding a status tab's pill would still leak those
  // rows through the "All" view. Correction is Pending + a pending_reason.
  const isRowTabVisible = (r) => {
    const isCorrection = r.approval === "Pending" && !!r.pendingReason;
    if (isCorrection) return hasTabCorrection;
    if (r.approval === "Pending") return hasTabPending;
    if (r.approval === "Approved") return hasTabApproved;
    if (r.approval === "Paid") return hasTabPaid;
    if (r.approval === "Rejected") return hasTabRejected;
    return true;
  };
  const [searchQuery, setSearchQuery] = useState("");
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]); // Today's date
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [amount, setAmount] = useState("");
  // Edit mode — only the requester can edit their own request (reuses this same form)
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingRequestId, setEditingRequestId] = useState(null);
  const [editingVendorName, setEditingVendorName] = useState(null);
  // "Add Payments" from inside Vendor-Wise's vendor drill-down — opens this
  // exact same form, but the vendor is pre-filled and frozen (the whole point
  // of that button is raising a payment for THIS vendor specifically); Client
  // stays a normal, freely-selectable field.
  const [vendorLockedForRequest, setVendorLockedForRequest] = useState(false);
  // "Add Payments" from inside Project-Wise's project drill-down — mirror of
  // vendorLockedForRequest above: Client is pre-filled and frozen, Vendor
  // stays freely selectable.
  const [clientLockedForRequest, setClientLockedForRequest] = useState(false);
  // Payment Request form validation errors — shown inline in-context (next to
  // the relevant field) instead of a jarring browser alert() popup.
  const [formError, setFormError] = useState('');
  const [documentError, setDocumentError] = useState('');
  // Desktop delete confirmation dialog (mobile uses window.confirm)
  const [deleteConfirmRequest, setDeleteConfirmRequest] = useState(null);
  const [deleteRequestLoading, setDeleteRequestLoading] = useState(false);

  // Documents attached to the Vendor Payment Request form (web + mobile) —
  // images, PDFs, Word/Excel files, etc. Each entry is either
  // { id, url, file, name, mimetype } — picked locally, not yet uploaded —
  // or { id, url, key, name, mimetype, uploaded: true } — already persisted
  // in R2/MongoDB.
  const [requestImages, setRequestImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const requestImageInputRef = useRef(null);
  const isImageFile = (mimetype) => (mimetype || "").startsWith("image/");
  const handlePickRequestImages = (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    const newImages = files.map((file) => ({
      id: `${Date.now()}_${Math.random()}`,
      url: URL.createObjectURL(file),
      file,
      name: file.name,
      mimetype: file.type,
    }));
    setRequestImages((prev) => [...prev, ...newImages]);
    setDocumentError('');
  };
  const handleRemoveRequestImage = async (img) => {
    if (img.uploaded && img.key) {
      const targetId = editingRequestId;
      if (targetId) {
        try {
          await deletePaymentRequestImage(targetId, img.key);
        } catch (error) {
          console.error("Error deleting image:", error);
          alert("Error deleting image");
          return;
        }
      }
    } else {
      URL.revokeObjectURL(img.url);
    }
    setRequestImages((prev) => prev.filter((i) => i.id !== img.id));
  };
  const clearRequestImages = () => {
    requestImages.forEach((img) => { if (!img.uploaded) URL.revokeObjectURL(img.url); });
    setRequestImages([]);
  };
  // Client picker — server-driven search instead of downloading every client
  // up front: only the first 10 (alphabetical) preload, and typing re-queries
  // the server (debounced), same pattern as Vendor-Wise/Material/Labour lists.
  // Unlike the vendor picker below, nothing else in this file needs the FULL
  // client list for computation, so this one gets the complete treatment.
  const [clientOptions, setClientOptions] = useState([]);
  const [clientSearchInput, setClientSearchInput] = useState('');
  const [clientSearchDebounced, setClientSearchDebounced] = useState('');
  const [clientOptionsLoading, setClientOptionsLoading] = useState(false);
  const CLIENT_OPTIONS_PAGE_SIZE = 10;
  const [vendorOptions, setVendorOptions] = useState([]);
  const [vendorLoading, setVendorLoading] = useState(false);
  // Registered vendor -> category/sub-category lookup (company-wide, not tied
  // to a client) plus this client's Category/Sub-Category budgets — together
  // these resolve which shared budget pool a selected vendor draws from.
  const [materialVendorsMeta, setMaterialVendorsMeta] = useState([]);
  const [labourVendorsMeta, setLabourVendorsMeta] = useState([]);
  const [materialCategoryBudgets, setMaterialCategoryBudgets] = useState({});
  const [materialSubCategoryBudgets, setMaterialSubCategoryBudgets] = useState({});
  const [labourCategoryBudgets, setLabourCategoryBudgets] = useState({});
  // Additional Works' shared budget pool is never manually set — it's always
  // 70% of this client's Additional Work Value Breakup total, mirroring the
  // exact same formula client_balance_sheet.js uses.
  const [additionalWorkValueTotal, setAdditionalWorkValueTotal] = useState(0);
  const [tabValue, setTabValue] = useState(0);
  const [selectedApprovalFilter, setSelectedApprovalFilter] = useState(null);
  // Desktop-only status pipeline tab (All / Pending / Approved / Paid / Rejected).
  // Independent of tabValue + selectedApprovalFilter, which the mobile UI owns.
  const [deskStatusTab, setDeskStatusTab] = useState("all");
  // If the current pipeline tab isn't accessible (starting default is "all",
  // which may itself be hidden), fall back to the first one that is —
  // otherwise the ledger would silently keep showing an unreachable tab's data.
  useEffect(() => {
    const order = [
      ["all", hasTabAll], ["Pending", hasTabPending], ["Approved", hasTabApproved],
      ["Paid", hasTabPaid], ["Correction", hasTabCorrection], ["Rejected", hasTabRejected],
      ["VendorWise", hasTabVendorWise], ["ProjectWise", hasTabProjectWise],
    ];
    const currentVisible = order.find(([k]) => k === deskStatusTab)?.[1];
    if (!currentVisible) {
      const firstVisible = order.find(([, visible]) => visible)?.[0];
      if (firstVisible) setDeskStatusTab(firstVisible);
    }
    // eslint-disable-next-line
  }, [hasTabAll, hasTabPending, hasTabApproved, hasTabPaid, hasTabCorrection, hasTabRejected, hasTabVendorWise, hasTabProjectWise]);
  // Vendor-Wise tab: browse every vendor's payments across ALL projects.
  // Drilling into a vendor, filtering by status, and the bulk selection are
  // all independent of the status pipeline above.
  //
  // Deliberately does NOT reuse the big, already-fetched `paymentRequests`
  // array the other pipeline tabs share — with 100+ vendors and thousands of
  // requests, deriving vendor stats from that in-memory array on every render
  // was the actual "why is this slow" complaint. Instead: a lightweight
  // server-side aggregation (vendor name + Approved/Pending count+value) for
  // the un-drilled list, paginated + searched server-side; and a fresh,
  // vendor-scoped fetch only once a specific vendor is clicked.
  const VENDOR_WISE_PAGE_SIZE = 50;
  const [vendorWiseSelectedVendor, setVendorWiseSelectedVendor] = useState(null);
  const [vendorWiseStatusFilter, setVendorWiseStatusFilter] = useState("Approved");
  const [vendorWiseSelectedIds, setVendorWiseSelectedIds] = useState(() => new Set());
  const [vendorWiseRejectDialogOpen, setVendorWiseRejectDialogOpen] = useState(false);
  const [vendorWiseRejectReason, setVendorWiseRejectReason] = useState("");
  const [vendorWiseRemoveConfirmOpen, setVendorWiseRemoveConfirmOpen] = useState(false);
  const [vendorWiseActionLoading, setVendorWiseActionLoading] = useState(false);
  // Un-drilled vendor list — one summary row per vendor, server-paginated + searched.
  const [vwSummary, setVwSummary] = useState([]);
  const [vwSummaryTotal, setVwSummaryTotal] = useState(0);
  const [vwSummaryPage, setVwSummaryPage] = useState(1);
  const [vwGrandTotals, setVwGrandTotals] = useState({ approvedCount: 0, approvedValue: 0, pendingCount: 0, pendingValue: 0, totalCount: 0 });
  const [vwSummaryLoading, setVwSummaryLoading] = useState(false);
  // Drilled-into vendor — that vendor's own payment list, fetched fresh (not
  // sliced out of paymentRequests), paginated client-side (a single vendor's
  // own history is always small enough that this endpoint doesn't need its
  // own skip/limit params).
  const [vwVendorPayments, setVwVendorPayments] = useState([]);
  const [vwVendorLoading, setVwVendorLoading] = useState(false);
  const [vwDrillPage, setVwDrillPage] = useState(1);
  // Search debounced specifically for the vendor-summary server call — the
  // shared `searchQuery` box still updates instantly, this just paces the
  // network request behind it.
  const [vwDebouncedSearch, setVwDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setVwDebouncedSearch(searchQuery.trim()), 350);
    return () => clearTimeout(t);
  }, [searchQuery]);
  useEffect(() => {
    setVwSummaryPage(1);
  }, [vwDebouncedSearch]);
  useEffect(() => {
    setVwDrillPage(1);
  }, [vendorWiseSelectedVendor, vendorWiseStatusFilter]);
  // Vendor-Wise top-level filter — status + date range, applied across the
  // WHOLE cross-vendor view (grand totals, every vendor row's displayed
  // value, and the status/date a drilled-into vendor opens to). "Applied"
  // values drive the actual fetch/display; "Draft" values are what the
  // popover edits before Apply commits them, same two-tier pattern as any
  // typical filter panel.
  const VENDOR_WISE_FILTER_STATUSES = ["All", "Approved", "Pending", "Correction", "Paid", "Rejected"];
  const [vwFilterAnchor, setVwFilterAnchor] = useState(null);
  const [vwFilterStatus, setVwFilterStatus] = useState("All");
  const [vwFilterDateFrom, setVwFilterDateFrom] = useState("");
  const [vwFilterDateTo, setVwFilterDateTo] = useState("");
  const [vwFilterDraftStatus, setVwFilterDraftStatus] = useState("All");
  const [vwFilterDraftFrom, setVwFilterDraftFrom] = useState("");
  const [vwFilterDraftTo, setVwFilterDraftTo] = useState("");
  const vwFilterActive = vwFilterStatus !== "All" || !!vwFilterDateFrom || !!vwFilterDateTo;
  const openVwFilter = (e) => {
    setVwFilterDraftStatus(vwFilterStatus);
    setVwFilterDraftFrom(vwFilterDateFrom);
    setVwFilterDraftTo(vwFilterDateTo);
    setVwFilterAnchor(e.currentTarget);
  };
  const closeVwFilter = () => setVwFilterAnchor(null);
  const applyVwFilter = () => {
    setVwFilterStatus(vwFilterDraftStatus);
    setVwFilterDateFrom(vwFilterDraftFrom);
    setVwFilterDateTo(vwFilterDraftTo);
    setVwSummaryPage(1);
    setVwFilterAnchor(null);
  };
  const clearVwFilter = () => {
    setVwFilterStatus("All");
    setVwFilterDateFrom("");
    setVwFilterDateTo("");
    setVwFilterDraftStatus("All");
    setVwFilterDraftFrom("");
    setVwFilterDraftTo("");
    setVwSummaryPage(1);
    setVwFilterAnchor(null);
  };
  // Which field of a vwSummary row / vwGrandTotals holds the value for the
  // currently-selected status filter — "All" reads totalValue (every status
  // combined), same idea as Vendor-Wise's own VENDOR_WISE_FILTERS chips.
  const VW_STATUS_FIELD = { All: "totalValue", Approved: "approvedValue", Pending: "pendingValue", Correction: "correctionValue", Paid: "paidValue", Rejected: "rejectedValue" };

  // Project-Wise tab: the same cross-vendor browsing model as Vendor-Wise
  // above, just grouped by Client ("Project") instead of by Vendor — a
  // lightweight server-side per-client summary for the un-drilled list, and
  // a fresh, client-scoped fetch (reusing getAllPaymentRequests's existing
  // client_id filter — no new endpoint needed for the drill-down itself)
  // once a specific project is clicked.
  const PROJECT_WISE_PAGE_SIZE = 50;
  const [projectWiseSelectedProject, setProjectWiseSelectedProject] = useState(null); // { id, name }
  const [projectWiseStatusFilter, setProjectWiseStatusFilter] = useState("Approved");
  const [projectWiseSelectedIds, setProjectWiseSelectedIds] = useState(() => new Set());
  const [projectWiseRejectDialogOpen, setProjectWiseRejectDialogOpen] = useState(false);
  const [projectWiseRejectReason, setProjectWiseRejectReason] = useState("");
  const [projectWiseRemoveConfirmOpen, setProjectWiseRemoveConfirmOpen] = useState(false);
  const [projectWiseActionLoading, setProjectWiseActionLoading] = useState(false);
  const [pwSummary, setPwSummary] = useState([]);
  const [pwSummaryTotal, setPwSummaryTotal] = useState(0);
  const [pwSummaryPage, setPwSummaryPage] = useState(1);
  const [pwGrandTotals, setPwGrandTotals] = useState({ approvedCount: 0, approvedValue: 0, pendingCount: 0, pendingValue: 0, totalCount: 0 });
  const [pwSummaryLoading, setPwSummaryLoading] = useState(false);
  const [pwProjectPayments, setPwProjectPayments] = useState([]);
  const [pwProjectLoading, setPwProjectLoading] = useState(false);
  const [pwDrillPage, setPwDrillPage] = useState(1);
  const [pwDebouncedSearch, setPwDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setPwDebouncedSearch(searchQuery.trim()), 350);
    return () => clearTimeout(t);
  }, [searchQuery]);
  useEffect(() => {
    setPwSummaryPage(1);
  }, [pwDebouncedSearch]);
  useEffect(() => {
    setPwDrillPage(1);
  }, [projectWiseSelectedProject, projectWiseStatusFilter]);
  const toggleProjectWiseSelect = (id) => {
    setProjectWiseSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearProjectWiseSelection = () => setProjectWiseSelectedIds(new Set());
  const openProjectWiseProject = (id, name) => {
    setProjectWiseSelectedProject({ id, name });
    setProjectWiseStatusFilter("Approved");
    clearProjectWiseSelection();
  };
  const closeProjectWiseProject = () => {
    setProjectWiseSelectedProject(null);
    clearProjectWiseSelection();
  };
  const handleProjectWiseBulkReject = async () => {
    if (!projectWiseRejectReason.trim()) {
      alert("Please enter a reason for rejection");
      return;
    }
    const ids = Array.from(projectWiseSelectedIds);
    setProjectWiseActionLoading(true);
    try {
      await Promise.all(ids.map((id) => updatePaymentRequestStatus(id, { approval_status: "Rejected", rejection_reason: projectWiseRejectReason })));
      const applyRejected = (r) => (ids.includes(r.id) ? { ...r, approval: "Rejected", rejectionReason: projectWiseRejectReason } : r);
      setPwProjectPayments((prev) => prev.map(applyRejected));
      setPaymentRequests((prev) => prev.map(applyRejected));
      clearProjectWiseSelection();
      setProjectWiseRejectDialogOpen(false);
      setProjectWiseRejectReason("");
    } catch (error) {
      console.error("Error bulk-rejecting payment requests:", error);
      alert("Error rejecting the selected payment requests");
    } finally {
      setProjectWiseActionLoading(false);
    }
  };
  const handleProjectWiseBulkRemove = async () => {
    const ids = Array.from(projectWiseSelectedIds);
    setProjectWiseActionLoading(true);
    try {
      await Promise.all(ids.map((id) => deletePaymentRequest(id)));
      setPwProjectPayments((prev) => prev.filter((r) => !ids.includes(r.id)));
      setPaymentRequests((prev) => prev.filter((r) => !ids.includes(r.id)));
      clearProjectWiseSelection();
      setProjectWiseRemoveConfirmOpen(false);
    } catch (error) {
      console.error("Error bulk-removing payment requests:", error);
      alert("Error removing the selected payment requests");
    } finally {
      setProjectWiseActionLoading(false);
    }
  };
  // Mobile pagination — cap each tab's list at 20, "Load More" reveals the rest
  const PAGE_SIZE = 20;
  const [mVisibleCount, setMVisibleCount] = useState(PAGE_SIZE);
  useEffect(() => {
    setMVisibleCount(PAGE_SIZE);
  }, [tabValue, selectedApprovalFilter, searchQuery]);

  // Desktop unified ledger pagination — rendering every matching row
  // unpaginated (potentially hundreds+ of rich TableRows) was the actual
  // cause of the "site feels slow" complaint, not the search itself (search
  // already filters the in-memory paymentRequests array instantly, no
  // network round-trip). Capped at 50 rows/page; resets to page 1 whenever
  // the pipeline tab or the search query changes, so a fresh filter always
  // starts from the top instead of landing on a now out-of-range page.
  const DESK_PAGE_SIZE = 50;
  const [deskPage, setDeskPage] = useState(1);
  useEffect(() => {
    setDeskPage(1);
  }, [deskStatusTab, searchQuery]);

  // Ledger Filter — Financial Year + Month-within-FY + a plain custom date
  // range, shared by every status pipeline tab (All/Pending/Approved/Paid/
  // Correction/Rejected all render through the same `deskRows` list below).
  // Deliberately NOT shown on Vendor-Wise/Project-Wise, which already have
  // their own, differently-shaped views.
  const LEDGER_FY_OPTIONS = ["2023-24", "2024-25", "2025-26", "2026-27"];
  const [ledgerFilterAnchor, setLedgerFilterAnchor] = useState(null);
  const [ledgerFilterFY, setLedgerFilterFY] = useState("");
  const [ledgerFilterMonth, setLedgerFilterMonth] = useState("");
  const [ledgerFilterFrom, setLedgerFilterFrom] = useState("");
  const [ledgerFilterTo, setLedgerFilterTo] = useState("");
  const [ledgerDraftFY, setLedgerDraftFY] = useState("");
  const [ledgerDraftMonth, setLedgerDraftMonth] = useState("");
  const [ledgerDraftFrom, setLedgerDraftFrom] = useState("");
  const [ledgerDraftTo, setLedgerDraftTo] = useState("");
  const ledgerFilterActive = !!ledgerFilterFY;
  const openLedgerFilter = (e) => {
    setLedgerDraftFY(ledgerFilterFY);
    setLedgerDraftMonth(ledgerFilterMonth);
    setLedgerDraftFrom(ledgerFilterFrom);
    setLedgerDraftTo(ledgerFilterTo);
    setLedgerFilterAnchor(e.currentTarget);
  };
  const closeLedgerFilter = () => setLedgerFilterAnchor(null);
  const applyLedgerFilter = () => {
    setLedgerFilterFY(ledgerDraftFY);
    setLedgerFilterMonth(ledgerDraftFY === "Custom Date" ? "" : ledgerDraftMonth);
    setLedgerFilterFrom(ledgerDraftFrom);
    setLedgerFilterTo(ledgerDraftTo);
    setDeskPage(1);
    setLedgerFilterAnchor(null);
  };
  const clearLedgerFilter = () => {
    setLedgerFilterFY(""); setLedgerFilterMonth(""); setLedgerFilterFrom(""); setLedgerFilterTo("");
    setLedgerDraftFY(""); setLedgerDraftMonth(""); setLedgerDraftFrom(""); setLedgerDraftTo("");
    setDeskPage(1);
    setLedgerFilterAnchor(null);
  };
  // The 12 months of a "YYYY-YY" financial year, in April-first order —
  // relative index 0 = April of the FY's start year, 11 = March of the end year.
  const monthsOfFY = (fy) => {
    if (!fy || fy === "Custom Date") return [];
    const startYear = parseInt(fy.split("-")[0], 10);
    return Array.from({ length: 12 }, (_, i) => {
      const month = (3 + i) % 12; // 3 = April
      const year = startYear + Math.floor((3 + i) / 12);
      return { rel: i, year, month, label: new Date(year, month, 1).toLocaleDateString("en-IN", { month: "short", year: "numeric" }) };
    });
  };
  const ledgerDateRange = (() => {
    if (!ledgerFilterFY) return null;
    if (ledgerFilterFY === "Custom Date") {
      if (!ledgerFilterFrom && !ledgerFilterTo) return null;
      return { from: ledgerFilterFrom || null, to: ledgerFilterTo || null };
    }
    const months = monthsOfFY(ledgerFilterFY);
    if (ledgerFilterMonth !== "") {
      const m = months[parseInt(ledgerFilterMonth, 10)];
      if (!m) return null;
      return { from: new Date(m.year, m.month, 1), to: new Date(m.year, m.month + 1, 0) };
    }
    const first = months[0], last = months[11];
    return { from: new Date(first.year, first.month, 1), to: new Date(last.year, last.month + 1, 0) };
  })();
  const withinLedgerFilter = (dateStr) => {
    if (!ledgerDateRange) return true;
    if (!dateStr) return false;
    const d = new Date(dateStr).getTime();
    if (ledgerDateRange.from && d < new Date(ledgerDateRange.from).getTime()) return false;
    if (ledgerDateRange.to && d > new Date(ledgerDateRange.to).getTime() + 86399999) return false;
    return true;
  };
  const ledgerFilterLabel = !ledgerFilterFY
    ? "Filter"
    : ledgerFilterFY === "Custom Date"
    ? "Custom"
    : ledgerFilterMonth !== ""
    ? monthsOfFY(ledgerFilterFY)[parseInt(ledgerFilterMonth, 10)]?.label || `FY ${ledgerFilterFY}`
    : `FY ${ledgerFilterFY}`;

  // Status change dialog states
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [paymentDate, setPaymentDate] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [pendingReason, setPendingReason] = useState("");
  const [approvalDate, setApprovalDate] = useState("");
  const [approvedBy, setApprovedBy] = useState("");
  // "Mark as Paid" confirmation popup — date pre-filled to today, separate from
  // the main Update Status sheet so paying is a deliberate, explicit final step
  const [markPaidDialogOpen, setMarkPaidDialogOpen] = useState(false);

  // Update Status dialog — budget context for the request's own category/
  // sub-category pool. Deliberately NOT reusing getPoolBudgetAvailable/
  // getPoolMemberNames etc. above: those are all scoped to the Add-Payment
  // form's OWN selectedClient, which has nothing to do with whichever
  // request/client the user just clicked "Update Status" on (could be any
  // client, from any tab). This fetches fresh, scoped to selectedRequest's
  // own client_id, independent of that form's state.
  const [statusBudgetInfo, setStatusBudgetInfo] = useState(null);
  const [statusBudgetLoading, setStatusBudgetLoading] = useState(false);
  useEffect(() => {
    if (!statusDialogOpen || !selectedRequest) {
      setStatusBudgetInfo(null);
      return;
    }
    const fetchBudgetContext = async () => {
      try {
        setStatusBudgetLoading(true);
        const meta = findVendorMeta(selectedRequest.vendorName);
        if (!meta || !meta.vendor_category) {
          setStatusBudgetInfo(null);
          return;
        }
        const isMat = meta.vendor_type === 'material_vendor';
        const category = meta.vendor_category;
        const subCategory = meta.vendor_sub_category || null;

        const [clientRes, reqRes] = await Promise.all([
          getClientById(selectedRequest.clientId),
          getAllPaymentRequests({ client_id: selectedRequest.clientId, show_all: 'true' }),
        ]);

        const bs = clientRes?.success ? clientRes.data?.client_balance_sheet : null;
        let budgetAmount = 0;
        if (bs) {
          if (subCategory) {
            const key = `${category}::${subCategory}`;
            budgetAmount = parseFloat(bs.material_sub_category_budgets?.[key]?.budget_amount) || 0;
          } else {
            const catBudgets = isMat ? bs.material_category_budgets : bs.labour_category_budgets;
            budgetAmount = parseFloat(catBudgets?.[category]?.budget_amount) || 0;
          }
        }

        // Every OTHER vendor (company-wide) that resolves to this exact pool —
        // same rule as resolvePool: a sub-category pool only matches vendors
        // with that exact sub-category, never falls back to the category.
        const poolVendorNames = new Set();
        [...materialVendorsMeta, ...labourVendorsMeta].forEach((v) => {
          if ((v.vendor_type === 'material_vendor') !== isMat) return;
          if (v.vendor_category !== category) return;
          const sub = v.vendor_sub_category || null;
          if (subCategory ? sub === subCategory : !sub) poolVendorNames.add((v.vendor_name || '').trim().toLowerCase());
        });

        const allRequests = (reqRes?.success ? reqRes.data : []).map(mapPaymentRequestFromApi);
        const poolRequests = allRequests.filter((r) => poolVendorNames.has((r.vendorName || '').trim().toLowerCase()));
        const othersInPool = poolRequests.filter((r) => r.id !== selectedRequest.id);

        const paidTillDate = poolRequests.filter((r) => r.approval === 'Paid').reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
        const pendingSum = othersInPool.filter((r) => getDisplayStatus(r) === 'Pending').reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
        const approvedSum = othersInPool.filter((r) => r.approval === 'Approved').reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
        const available = budgetAmount - paidTillDate - pendingSum - approvedSum;

        const previousPayments = othersInPool
          .map((r) => ({ id: r.id, vendorName: r.vendorName, amount: parseFloat(r.amount) || 0, status: getDisplayStatus(r), date: r.paymentDate || r.date }))
          .sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

        setStatusBudgetInfo({
          poolLabel: subCategory ? `${category} → ${subCategory}` : category,
          budgetAmount,
          paidTillDate,
          pendingSum,
          approvedSum,
          available,
          previousPayments,
        });
      } catch (error) {
        console.error('Error fetching budget context for status dialog:', error);
        setStatusBudgetInfo(null);
      } finally {
        setStatusBudgetLoading(false);
      }
    };
    fetchBudgetContext();
  }, [statusDialogOpen, selectedRequest?.id]);

  // Project Financial Insight — Approvals (Pending) view only: pulls the
  // client's live balance sheet to show current margin and what it would
  // become if this request is approved. Never shown for Payments-tab cards.
  const [insight, setInsight] = useState(null);
  const [insightLoading, setInsightLoading] = useState(false);
  useEffect(() => {
    const loadInsight = async () => {
      if (!isMobile || !statusDialogOpen || selectedRequest?.approval !== "Pending" || !selectedRequest?.clientId) {
        setInsight(null);
        return;
      }
      try {
        setInsightLoading(true);
        const res = await getClientById(selectedRequest.clientId);
        const bs = res?.success ? res.data?.client_balance_sheet : null;
        if (bs) {
          const contractValue =
            (parseFloat(bs.summary_details?.income_related?.["Initial Contract Value"]) || 0) +
            (parseFloat(bs.summary_details?.income_related?.["Additional Work Value"]) || 0);
          // "Till date" cash actually paid out — shown as-is in the card
          const matPaid = Object.values(bs.material_purchases || {}).reduce((s, v) => s + (parseFloat(v.amount_paid) || 0), 0);
          const labPaid = Object.values(bs.labour_charges || {}).reduce((s, v) => s + (parseFloat(v.amount_paid) || 0), 0);
          const totalExpenses = matPaid + labPaid;
          // Current Margin uses the full COMMITTED cost (paid + still-available
          // budget for every vendor) — i.e. every vendor's quotation value —
          // since that money is already contracted and will be spent regardless
          // of this specific approval.
          const matQuoted = Object.values(bs.material_purchases || {}).reduce((s, v) => s + (parseFloat(v.quotation_value) || 0), 0);
          const labQuoted = Object.values(bs.labour_charges || {}).reduce((s, v) => s + (parseFloat(v.quotation_value) || 0), 0);
          const totalCommitted = matQuoted + labQuoted;
          const currentMarginPct = contractValue > 0 ? ((contractValue - totalCommitted) / contractValue) * 100 : 0;
          // Approving only creates NEW cost impact if this request exceeds the
          // vendor's already-quoted budget — the in-budget portion is already
          // reflected in Current Margin above.
          const overage = Math.max(0, (parseFloat(selectedRequest.amount) || 0) - (parseFloat(selectedRequest.budgetAvailable) || 0));
          const projectedMarginPct = contractValue > 0 ? ((contractValue - (totalCommitted + overage)) / contractValue) * 100 : 0;
          setInsight({ contractValue, totalExpenses, currentMarginPct, projectedMarginPct });
        } else {
          setInsight(null);
        }
      } catch (error) {
        console.error("Error loading project insight:", error);
        setInsight(null);
      } finally {
        setInsightLoading(false);
      }
    };
    loadInsight();
  }, [isMobile, statusDialogOpen, selectedRequest?.approval, selectedRequest?.clientId, selectedRequest?.amount]);

  // Over budget logic states
  const [isOverBudget, setIsOverBudget] = useState(false);
  const [overBudgetReason, setOverBudgetReason] = useState("");
  const [personIncharge, setPersonIncharge] = useState(null);
  const [loading, setLoading] = useState(true);

  // Check if logged-in user is a Person Incharge for anyone
  const [isUserPersonIncharge, setIsUserPersonIncharge] = useState(false);

  // While a bottom sheet is open on mobile, hide the floating pill nav
  useEffect(() => {
    const open = isMobile && (openDialog || statusDialogOpen);
    document.body.classList.toggle("arcdx-sheet-open", open);
    return () => document.body.classList.remove("arcdx-sheet-open");
  }, [isMobile, openDialog, statusDialogOpen]);

  // Approval filter options
  const approvalFilterOptions = [
    { value: "all", label: "All", color: "#FFFFFF", show: hasTabAll },
    { value: "Pending", label: "Pending", color: "#E8DE61", show: hasTabPending },
    { value: "Approved", label: "Approved", color: "#59AC77", show: hasTabApproved },
    { value: "Paid", label: "Paid", color: "#60BEF7", show: hasTabPaid },
    { value: "Correction", label: "Correction", color: "#F79009", show: hasTabCorrection },
    { value: "Rejected", label: "Rejected", color: "#FF8570", show: hasTabRejected },
  ].filter((o) => o.show);

  // Status change dialog handlers
  const handleStatusCellClick = (request) => {
    setSelectedRequest(request);
    // Opening a request you can act on defaults straight to the expected next
    // action — Pending → Approve, Approved → Paid — on both web and mobile.
    // The approval date is silently stamped with today; no date picker shown.
    const isPersonInchargeForThis = String(request.personInchargeId) === String(user?._id);
    const defaultsToApprove = request.approval === "Pending" && (isMainUser || isPersonInchargeForThis || vendorAccessOf('vendorTabApproved') === 'full_access');
    const defaultsToPaid = request.approval === "Approved" && (isMainUser || isPersonInchargeForThis || vendorAccessOf('vendorTabPaid') === 'full_access');
    const todayStr = new Date().toISOString().split('T')[0];
    setNewStatus(defaultsToApprove ? "Approved" : defaultsToPaid ? "Paid" : request.approval);
    // Pre-fill existing values if available
    setPaymentDate(defaultsToPaid ? todayStr : (request.paymentDate || ""));
    setRejectionReason(request.rejectionReason || "");
    // For pending reason, use the original over-budget reason if no pending_reason exists
    setPendingReason(request.pendingReason || request.reason || "");
    setApprovalDate(defaultsToApprove ? todayStr : (request.approvalDate || ""));
    setApprovedBy(request.approvedBy || "");
    setStatusDialogOpen(true);
  };

  const handleStatusDialogClose = () => {
    setStatusDialogOpen(false);
    setSelectedRequest(null);
    setNewStatus("");
    setPaymentDate("");
    setRejectionReason("");
    setPendingReason("");
    setApprovalDate("");
    setApprovedBy("");
  };

  const handleStatusSave = async () => {
    if (!selectedRequest || !newStatus) return;

    // Validate required fields based on status
    if (newStatus === "Paid" && !paymentDate) {
      alert("Please enter Date of Payment");
      return;
    }
    if (newStatus === "Rejected" && !rejectionReason) {
      alert("Please enter Reason for Rejection");
      return;
    }

    try {
      // Prepare status data for API
      const statusData = {
        approval_status: newStatus,
        payment_date: newStatus === "Paid" ? paymentDate : undefined,
        rejection_reason: newStatus === "Rejected" ? rejectionReason : undefined,
        pending_reason: newStatus === "Pending" ? pendingReason : undefined,
        approval_date: newStatus === "Approved" ? approvalDate : undefined
      };

      // Save to database
      const response = await updatePaymentRequestStatus(selectedRequest.id, statusData);

      if (response.success) {
        // Update local state with the response from database — applied to
        // every local copy of this request: the main ledger AND Vendor-Wise /
        // Project-Wise's own drilled-in lists (statuses can now be changed
        // from inside those two tabs as well, not just the status pipeline
        // tabs), so whichever view you're looking at reflects it instantly.
        const applyUpdate = (req) =>
          req.id === selectedRequest.id
            ? {
                ...req,
                approval: newStatus,
                paymentDate: newStatus === "Paid" ? paymentDate : req.paymentDate,
                rejectionReason: newStatus === "Rejected" ? rejectionReason : req.rejectionReason,
                pendingReason: newStatus === "Pending" ? pendingReason : req.pendingReason,
                approvalDate: newStatus === "Approved" ? approvalDate : req.approvalDate,
                approvedById: newStatus === "Approved" ? response.data.approved_by_id : req.approvedById,
                approvedByName: newStatus === "Approved" ? response.data.approved_by_name : req.approvedByName
              }
            : req;
        setPaymentRequests((prev) => prev.map(applyUpdate));
        setVwVendorPayments((prev) => prev.map(applyUpdate));
        setPwProjectPayments((prev) => prev.map(applyUpdate));
        handleStatusDialogClose();
      }
    } catch (error) {
      console.error("Error updating status:", error);
      alert("Error updating payment request status");
    }
  };

  // Only the person who raised a request can delete it — removes it for everyone
  // Desktop: opens a proper styled warning dialog. Mobile: keeps the native confirm.
  const handleDeletePaymentRequest = (request) => {
    if (isMobile) {
      if (window.confirm("Delete this payment request? This cannot be undone.")) {
        executeDeletePaymentRequest(request);
      }
      return;
    }
    setDeleteConfirmRequest(request);
  };

  const executeDeletePaymentRequest = async (request) => {
    try {
      setDeleteRequestLoading(true);
      const response = await deletePaymentRequest(request.id);
      if (response.success) {
        setPaymentRequests((prev) => prev.filter((r) => r.id !== request.id));
        if (selectedRequest?.id === request.id) handleStatusDialogClose();
      }
      setDeleteConfirmRequest(null);
    } catch (error) {
      console.error("Error deleting payment request:", error);
      alert("Error deleting payment request");
    } finally {
      setDeleteRequestLoading(false);
    }
  };

  // Debounce the client search box so typing doesn't fire a request per keystroke
  useEffect(() => {
    const t = setTimeout(() => setClientSearchDebounced(clientSearchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [clientSearchInput]);

  // Fetch clients from database — top 10 alphabetically by default, or
  // server-matched results once the user types into the Client field.
  useEffect(() => {
    const fetchClients = async () => {
      try {
        if (!user?.company_id) return;
        setClientOptionsLoading(true);

        const params = {
          company_id: user.company_id,
          page: 1,
          limit: CLIENT_OPTIONS_PAGE_SIZE,
        };
        if (clientSearchDebounced) params.search = clientSearchDebounced;

        const response = await getAllClients(params);
        if (response.success) {
          const clients = response.data.map(client => ({
            id: client._id,
            name: client.client_name
          }));
          setClientOptions(clients);
        }
      } catch (error) {
        console.error("Error fetching clients:", error);
      } finally {
        setClientOptionsLoading(false);
      }
    };

    fetchClients();
  }, [user?.company_id, clientSearchDebounced]);

  // The budget check must never trust selectedVendor.amountPaid alone — that
  // figure comes from the client balance sheet's manually-typed "Amount Paid"
  // field, which nothing keeps in sync with actual Paid VendorPaymentRequest
  // transactions raised through this exact screen. Mark a request "Paid" here
  // and that balance-sheet number stays frozen forever unless someone
  // remembers to go edit it by hand — which is exactly how "Already Paid"
  // ends up showing 0 (or stale) even though real payments exist.
  //
  // Budget now lives on the Category/Sub-Category, shared by every vendor
  // that resolves to the same pool — so this fetch is CLIENT-wide (every
  // vendor's requests, not just one), fetched whenever the client changes,
  // and filtered down to a specific pool's member vendor names at read time.
  const [clientBudgetTxns, setClientBudgetTxns] = useState([]);
  useEffect(() => {
    const fetchClientBudgetTxns = async () => {
      if (!selectedClient) {
        setClientBudgetTxns([]);
        return;
      }
      try {
        const response = await getAllPaymentRequests({
          client_id: selectedClient.id,
          show_all: 'true',
        });
        if (response.success) {
          setClientBudgetTxns(
            response.data.map((r) => ({
              id: r._id,
              vendorName: (r.vendor_name || '').trim().toLowerCase(),
              approval: r.approval_status,
              amount: parseFloat(r.amount) || 0,
            }))
          );
        }
      } catch (error) {
        console.error("Error fetching client budget transactions:", error);
        setClientBudgetTxns([]);
      }
    };
    fetchClientBudgetTxns();
  }, [selectedClient?.id]);

  // Look up ANY vendor name's registered Category/Sub-Category against the
  // company-wide Vendor master — deliberately searches ALL vendors (active
  // AND inactive), not just the active ones offered in the picker below, so
  // an older request raised for a vendor since deactivated still resolves to
  // its correct budget pool instead of silently losing its category.
  // O(1) lookup instead of scanning both vendor lists on every call — this is
  // called from inside getPoolMemberNames' loops over every balance-sheet row
  // AND every payment request for the client, which itself reruns on every
  // keystroke in the Amount field (handleAmountChange). With ~200+ vendors
  // and a client's full request history, the old .find()-based version made
  // typing feel like it froze for a moment on each character. Same inputs,
  // same outputs — only the lookup mechanism changed.
  const vendorMetaByName = useMemo(() => {
    const map = new Map();
    [...materialVendorsMeta, ...labourVendorsMeta].forEach((m) => {
      const key = (m.vendor_name || '').trim().toLowerCase();
      if (key && !map.has(key)) map.set(key, m);
    });
    return map;
  }, [materialVendorsMeta, labourVendorsMeta]);

  const findVendorMeta = (vendorName) => {
    const nameLower = (vendorName || '').trim().toLowerCase();
    if (!nameLower) return null;
    return vendorMetaByName.get(nameLower) || null;
  };

  // The picker itself: every ACTIVE vendor company-wide, regardless of
  // whether they've ever been added to any client's balance sheet — vendor
  // selection is now purely "which pool does this identify," not a budget
  // entity of its own. Memoized so typing in an unrelated field (Amount,
  // Reason, ...) doesn't rebuild ~200+ fresh objects on every keystroke just
  // to hand MUI's Autocomplete a new array with identical contents.
  const categorizedVendorOptions = useMemo(() => [
    ...materialVendorsMeta.filter((m) => m.vendor_status === 'active').map((m) => ({
      id: `material_meta_${m._id}`, name: m.vendor_name, type: 'Material',
      category: m.vendor_category || null, subCategory: m.vendor_sub_category || null,
    })),
    ...labourVendorsMeta.filter((m) => m.vendor_status === 'active').map((m) => ({
      id: `labour_meta_${m._id}`, name: m.vendor_name, type: 'Labour',
      category: m.vendor_category || null, subCategory: m.vendor_sub_category || null,
    })),
  ], [materialVendorsMeta, labourVendorsMeta]);

  // Some real vendors are legitimately registered twice under the exact same
  // name — once as a Material vendor, once as a Labour vendor (they supply
  // material AND do the labour), or occasionally a true accidental double
  // entry under the same type. Either way, a name appearing more than once
  // in the picker is correct data, not a rendering bug — this just tags the
  // dropdown row with its type so it's clear WHY the same name shows twice,
  // instead of looking like an unexplained duplicate.
  const vendorNameCollisionCounts = useMemo(() => {
    const counts = new Map();
    categorizedVendorOptions.forEach((o) => {
      const key = o.name.trim().toLowerCase();
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return counts;
  }, [categorizedVendorOptions]);
  const renderVendorOption = (props, option) => {
    const collides = (vendorNameCollisionCounts.get(option.name.trim().toLowerCase()) || 0) > 1;
    return (
      <li {...props} key={option.id}>
        {option.name}
        {collides && (
          <span style={{ marginLeft: 6, fontSize: "11px", fontWeight: 700, color: "#98a2b3" }}>
            ({option.type})
          </span>
        )}
      </li>
    );
  };

  // Vendor names that START WITH what you typed rank above ones that merely
  // CONTAIN it — so typing "Poor" puts "Poornima" at the very top instead of
  // wherever it happens to fall in fetch order. Built on top of MUI's OWN
  // default filter (not reimplemented matching logic) so exactly the same
  // set of options matches as the Client field, which already works
  // correctly — this only ever re-sorts that already-correct result.
  const defaultAutocompleteFilter = createFilterOptions();
  const vendorFilterOptions = (options, state) => {
    const filtered = defaultAutocompleteFilter(options, state);
    const q = state.inputValue.trim().toLowerCase();
    if (!q) return filtered;
    const starts = [];
    const rest = [];
    filtered.forEach((o) => {
      ((o.name || '').toLowerCase().startsWith(q) ? starts : rest).push(o);
    });
    return [...starts, ...rest];
  };

  // Resolve which budget pool a vendor draws from: its sub-category if it has
  // one — even if that sub-category has no budget configured yet (unbudgeted
  // just means ₹0 available, not "fall back to the category") — otherwise
  // its category.
  const resolvePool = (vendor) => {
    if (!vendor || !vendor.category) return null;
    // Additional Works is a cross-type pool — a Material OR Labour vendor
    // tagged with it shares ONE pool, with a budget that's always 70% of
    // this client's Additional Work Value, never manually set.
    if (vendor.category === 'Additional Works') {
      return { isMat: null, crossType: true, category: 'Additional Works', subCategory: null, budget: { budget_amount: additionalWorkValueTotal * 0.70 } };
    }
    const isMat = vendor.type === 'Material';
    if (vendor.subCategory) {
      const key = `${vendor.category}::${vendor.subCategory}`;
      return { isMat, category: vendor.category, subCategory: vendor.subCategory, budget: materialSubCategoryBudgets[key] };
    }
    const budget = isMat ? materialCategoryBudgets[vendor.category] : labourCategoryBudgets[vendor.category];
    return { isMat, category: vendor.category, subCategory: null, budget };
  };

  // Every distinct vendor NAME with recorded activity on THIS client that
  // resolves into a given pool — from two sources, both already scoped to
  // selectedClient: (1) this client's balance-sheet rows (legacy vendors
  // added the old way), and (2) this client's Payment Request history (any
  // vendor ever paid on this project, even one never added to the balance
  // sheet). A vendor's category is looked up company-wide, but its spend
  // only ever counts against THIS client's budget — never bleeding in
  // whatever that same vendor was paid on a different client's project.
  const getPoolMemberNames = (pool) => {
    if (!pool) return [];
    const names = new Set();
    // crossType (Additional Works) pools span BOTH vendor types — skip the
    // type check entirely instead of requiring it to match pool.isMat.
    vendorOptions.forEach((v) => {
      if (!pool.crossType && (v.type === 'Material') !== pool.isMat) return;
      const meta = findVendorMeta(v.name);
      if (!meta || meta.vendor_category !== pool.category) return;
      const sub = meta.vendor_sub_category || null;
      if (pool.subCategory ? sub === pool.subCategory : !sub) names.add(v.name.trim().toLowerCase());
    });
    clientBudgetTxns.forEach((t) => {
      const meta = findVendorMeta(t.vendorName);
      if (!meta || meta.vendor_category !== pool.category) return;
      if (!pool.crossType && (meta.vendor_type === 'material_vendor') !== pool.isMat) return;
      const sub = meta.vendor_sub_category || null;
      if (pool.subCategory ? sub === pool.subCategory : !sub) names.add(t.vendorName);
    });
    return Array.from(names);
  };

  // Real Paid transactions can only ever push a vendor's "already paid" up
  // from whatever the balance sheet's manual figure says, never down — same
  // rule as before, just summed across every member name in the pool.
  const getPoolEffectivePaid = (pool) => getPoolMemberNames(pool).reduce((sum, nameLower) => {
    const bsAmount = vendorOptions.find((v) => v.name.trim().toLowerCase() === nameLower)?.amountPaid || 0;
    const txnPaid = clientBudgetTxns.filter((r) => r.approval === "Paid" && r.vendorName === nameLower).reduce((s, r) => s + r.amount, 0);
    return sum + Math.max(bsAmount, txnPaid);
  }, 0);

  // Other Pending/Approved-but-not-Paid requests from ANY vendor in the pool
  // already claim part of the shared budget — must be subtracted so the same
  // budget can't be committed twice over. Excludes the request being edited.
  const getPoolOutstanding = (pool) => {
    const names = getPoolMemberNames(pool);
    const matches = clientBudgetTxns.filter((r) => r.id !== editingRequestId && names.includes(r.vendorName) && (r.approval === "Pending" || r.approval === "Approved"));
    const pendingSum = matches.filter((r) => r.approval === "Pending").reduce((s, r) => s + r.amount, 0);
    const approvedSum = matches.filter((r) => r.approval === "Approved").reduce((s, r) => s + r.amount, 0);
    return { pendingSum, approvedSum };
  };

  const getPoolBudgetAvailable = (vendor) => {
    if (!vendor) return 0;
    const pool = resolvePool(vendor);
    if (!pool) return 0;
    const budgetAmount = parseFloat(pool.budget?.budget_amount) || 0;
    const effectivePaid = getPoolEffectivePaid(pool);
    const { pendingSum, approvedSum } = getPoolOutstanding(pool);
    return budgetAmount - effectivePaid - pendingSum - approvedSum;
  };

  // Fetch registered vendors' category/sub-category — company-wide, not tied
  // to a specific client — so any vendor name can be resolved to its
  // Category/Sub-Category budget pool. Deliberately fetches EVERY vendor
  // (active and inactive), not just active ones — an older request raised
  // for a vendor since deactivated still needs to resolve correctly. The
  // picker itself (categorizedVendorOptions) filters to active-only from
  // this same list, so inactive vendors never become newly selectable.
  useEffect(() => {
    const fetchVendorMeta = async () => {
      if (!user?.company_id) return;
      try {
        // Fetched in parallel, not one-after-another — waiting on material
        // before even starting labour meant the picker could still be missing
        // half its vendors for a moment right after opening the form.
        const [matRes, labRes] = await Promise.all([
          getAllVendors({ company_id: user.company_id, vendor_type: 'material_vendor' }),
          getAllVendors({ company_id: user.company_id, vendor_type: 'labour_work' }),
        ]);
        if (matRes.success) setMaterialVendorsMeta(matRes.data);
        if (labRes.success) setLabourVendorsMeta(labRes.data);
      } catch (error) {
        console.error("Error fetching vendor category metadata:", error);
      }
    };
    fetchVendorMeta();
  }, [user]);

  // Fetch vendors when a client is selected
  useEffect(() => {
    const fetchVendors = async () => {
      if (!selectedClient) {
        setVendorOptions([]);
        return;
      }

      try {
        setVendorLoading(true);
        const response = await getClientById(selectedClient.id);

        if (response.success && response.data) {
          const client = response.data;
          const balanceSheet = client.client_balance_sheet || {};
          setMaterialCategoryBudgets(balanceSheet.material_category_budgets || {});
          setMaterialSubCategoryBudgets(balanceSheet.material_sub_category_budgets || {});
          setLabourCategoryBudgets(balanceSheet.labour_category_budgets || {});
          // Same formula as additionalBreakupTotal in client_balance_sheet.js:
          // sum of (Amount to be Receivable: Bank + Cash) across every milestone row.
          const additionalWorkMap = balanceSheet["Additional Work Value"] || {};
          const additionalWorkTotal = Object.values(additionalWorkMap).reduce((sum, entry) => {
            const rcv = entry?.["Amount to be Receivable"] || {};
            return sum + (parseFloat(rcv.Bank) || 0) + (parseFloat(rcv.Cash) || 0);
          }, 0);
          setAdditionalWorkValueTotal(additionalWorkTotal);
          const vendors = [];

          // Extract vendors from Material Purchases
          if (balanceSheet.material_purchases) {
            Object.values(balanceSheet.material_purchases).forEach((item) => {
              if (item.vendor_name && item.vendor_name.trim() !== '') {
                vendors.push({
                  id: `material_${vendors.length}`,
                  name: item.vendor_name,
                  type: 'Material',
                  quotationValue: parseFloat(item.quotation_value) || 0,
                  amountPaid: parseFloat(item.amount_paid) || 0
                });
              }
            });
          }

          // Extract vendors from Labour Charges
          if (balanceSheet.labour_charges) {
            Object.values(balanceSheet.labour_charges).forEach((item) => {
              if (item.vendor_name && item.vendor_name.trim() !== '') {
                vendors.push({
                  id: `labour_${vendors.length}`,
                  name: item.vendor_name,
                  type: 'Labour',
                  quotationValue: parseFloat(item.quotation_value) || 0,
                  amountPaid: parseFloat(item.amount_paid) || 0
                });
              }
            });
          }

          // Remove duplicates based on vendor name and combine their values
          const uniqueVendors = [];
          vendors.forEach((vendor) => {
            const existingVendor = uniqueVendors.find(
              (v) => v.name.toLowerCase() === vendor.name.toLowerCase()
            );
            if (existingVendor) {
              // Add values if same vendor appears multiple times
              existingVendor.quotationValue += vendor.quotationValue;
              existingVendor.amountPaid += vendor.amountPaid;
            } else {
              uniqueVendors.push({ ...vendor });
            }
          });

          setVendorOptions(uniqueVendors);
        }
      } catch (error) {
        console.error("Error fetching vendors:", error);
        setVendorOptions([]);
      } finally {
        setVendorLoading(false);
      }
    };

    fetchVendors();
  }, [selectedClient]);

  // Edit mode: once the vendor master loads, select the matching vendor so
  // its live budget numbers (not stale historical ones) show. Matches against
  // categorizedVendorOptions (the active picker list) first; if the request's
  // vendor has since gone inactive, falls back to findVendorMeta (active +
  // inactive) so the edit form still resolves the right budget pool instead
  // of showing a blank vendor field. Depends on the underlying state (stable
  // references) rather than the derived array itself, which is recomputed
  // fresh every render.
  useEffect(() => {
    if (!editingVendorName || (materialVendorsMeta.length === 0 && labourVendorsMeta.length === 0)) return;
    const match = categorizedVendorOptions.find((v) => v.name.toLowerCase() === editingVendorName.toLowerCase());
    if (match) {
      setSelectedVendor(match);
      setEditingVendorName(null);
      return;
    }
    const meta = findVendorMeta(editingVendorName);
    if (meta) {
      setSelectedVendor({
        id: `inactive_${meta._id}`,
        name: meta.vendor_name,
        type: meta.vendor_type === 'material_vendor' ? 'Material' : 'Labour',
        category: meta.vendor_category || null,
        subCategory: meta.vendor_sub_category || null,
      });
      setEditingVendorName(null);
    }
  }, [editingVendorName, materialVendorsMeta, labourVendorsMeta]);

  // Payment requests data from database
  const [paymentRequests, setPaymentRequests] = useState([]);

  // ===== Vendor-Wise: every vendor's payments across ALL projects =====
  // Distinct vendor names from whatever this user is already allowed to see
  // (isRowTabVisible composes with each row's OWN status tab access, so e.g.
  // Paid rows still stay hidden here for someone without Paid tab access,
  // even though Vendor-Wise itself might be visible to them). vwSummary
  // already arrives ranked by Approved-tab activity (server-side sort) and
  // already paginated + searched — nothing further to derive here.
  const vwSummaryTotalPages = Math.max(1, Math.ceil(vwSummaryTotal / VENDOR_WISE_PAGE_SIZE));

  // A drilled-into vendor's own fetch always returns its FULL history (no
  // date params sent) — the date bound from the top-level filter is applied
  // here, client-side, same place the status filter already is, so "Apply"
  // up top and the drill-down in-sync immediately without a second fetch.
  const withinVwDateRange = (r) => {
    if (!r.date) return !vwFilterDateFrom && !vwFilterDateTo;
    const d = new Date(r.date).getTime();
    if (vwFilterDateFrom && d < new Date(vwFilterDateFrom).getTime()) return false;
    if (vwFilterDateTo && d > new Date(vwFilterDateTo).getTime() + 86399999) return false;
    return true;
  };
  const vendorWisePayments = vwVendorPayments;
  const vendorWiseFilteredPayments = vendorWisePayments
    .filter((r) => vendorWiseStatusFilter === "All" || getDisplayStatus(r) === vendorWiseStatusFilter)
    .filter(withinVwDateRange);
  // The header inside a drilled-into vendor reads "{Filter} · ₹X" — the total
  // value of exactly the list shown below, not the vendor's all-time total.
  const vendorWiseFilteredTotal = vendorWiseFilteredPayments.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const vwDrillTotalPages = Math.max(1, Math.ceil(vendorWiseFilteredPayments.length / VENDOR_WISE_PAGE_SIZE));
  const vwDrillPageClamped = Math.min(vwDrillPage, vwDrillTotalPages);
  const vwDrillStart = (vwDrillPageClamped - 1) * VENDOR_WISE_PAGE_SIZE;
  const vendorWisePagedPayments = vendorWiseFilteredPayments.slice(vwDrillStart, vwDrillStart + VENDOR_WISE_PAGE_SIZE);

  // Feeds the bulk Reject/Remove confirmation dialogs' summary card
  const vendorWiseSelectedRequests = vendorWisePayments.filter((r) => vendorWiseSelectedIds.has(r.id));
  const vendorWiseSelectedTotal = vendorWiseSelectedRequests.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);

  const toggleVendorWiseSelect = (id) => {
    setVendorWiseSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearVendorWiseSelection = () => setVendorWiseSelectedIds(new Set());

  // Project-Wise drilled-into-project derived lists — same shape as
  // Vendor-Wise's own above, plus a free-text search (vendor name/purpose)
  // since the project drill-down's row header is a Vendor, not a Project.
  const pwq = (searchQuery || "").trim().toLowerCase();
  const projectWisePayments = pwProjectPayments;
  const projectWiseFilteredPayments = projectWisePayments
    .filter((r) => projectWiseStatusFilter === "All" || getDisplayStatus(r) === projectWiseStatusFilter)
    .filter((r) => !pwq || (r.vendorName || "").toLowerCase().includes(pwq) || (r.paymentPurpose || "").toLowerCase().includes(pwq));
  const projectWiseFilteredTotal = projectWiseFilteredPayments.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const pwDrillTotalPages = Math.max(1, Math.ceil(projectWiseFilteredPayments.length / PROJECT_WISE_PAGE_SIZE));
  const pwDrillPageClamped = Math.min(pwDrillPage, pwDrillTotalPages);
  const pwDrillStart = (pwDrillPageClamped - 1) * PROJECT_WISE_PAGE_SIZE;
  const projectWisePagedPayments = projectWiseFilteredPayments.slice(pwDrillStart, pwDrillStart + PROJECT_WISE_PAGE_SIZE);
  const projectWiseSelectedRequests = projectWisePayments.filter((r) => projectWiseSelectedIds.has(r.id));
  const projectWiseSelectedTotal = projectWiseSelectedRequests.reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const pwSummaryTotalPages = Math.max(1, Math.ceil(pwSummaryTotal / PROJECT_WISE_PAGE_SIZE));

  // Selecting a different vendor, or leaving the tab, must never carry a
  // stale selection into a completely different list of rows.
  const openVendorWiseVendor = (name) => {
    setVendorWiseSelectedVendor(name);
    // Carries the top-level filter's status in — untouched default
    // ("Approved") when no filter has actually been applied yet.
    setVendorWiseStatusFilter(vwFilterActive ? vwFilterStatus : "Approved");
    clearVendorWiseSelection();
  };
  const closeVendorWiseVendor = () => {
    setVendorWiseSelectedVendor(null);
    clearVendorWiseSelection();
  };

  const handleVendorWiseBulkReject = async () => {
    if (!vendorWiseRejectReason.trim()) {
      alert("Please enter a reason for rejection");
      return;
    }
    const ids = Array.from(vendorWiseSelectedIds);
    setVendorWiseActionLoading(true);
    try {
      await Promise.all(ids.map((id) => updatePaymentRequestStatus(id, { approval_status: "Rejected", rejection_reason: vendorWiseRejectReason })));
      const applyRejected = (r) => (ids.includes(r.id) ? { ...r, approval: "Rejected", rejectionReason: vendorWiseRejectReason } : r);
      setVwVendorPayments((prev) => prev.map(applyRejected));
      setPaymentRequests((prev) => prev.map(applyRejected));
      clearVendorWiseSelection();
      setVendorWiseRejectDialogOpen(false);
      setVendorWiseRejectReason("");
    } catch (error) {
      console.error("Error bulk-rejecting payment requests:", error);
      alert("Error rejecting the selected payment requests");
    } finally {
      setVendorWiseActionLoading(false);
    }
  };

  const handleVendorWiseBulkRemove = async () => {
    const ids = Array.from(vendorWiseSelectedIds);
    setVendorWiseActionLoading(true);
    try {
      await Promise.all(ids.map((id) => deletePaymentRequest(id)));
      setVwVendorPayments((prev) => prev.filter((r) => !ids.includes(r.id)));
      setPaymentRequests((prev) => prev.filter((r) => !ids.includes(r.id)));
      clearVendorWiseSelection();
      setVendorWiseRemoveConfirmOpen(false);
    } catch (error) {
      console.error("Error bulk-removing payment requests:", error);
      alert("Error removing the selected payment requests");
    } finally {
      setVendorWiseActionLoading(false);
    }
  };

  // Shared API-row -> local-shape mapper, used by both the bulk fetch below
  // and the Vendor-Wise drill-down's own scoped fetch — kept in one place so
  // the two never silently drift apart.
  const mapPaymentRequestFromApi = (req) => ({
    id: req._id,
    userId: req.user_id?._id || req.user_id,
    requestedByName: req.user_id?.fullname || '',
    date: req.date,
    vendorName: req.vendor_name,
    clientName: req.client_name,
    clientId: req.client_id,
    paymentPurpose: req.payment_purpose,
    amount: req.amount,
    budgetAvailable: req.budget_available,
    isOverBudget: req.is_over_budget,
    reason: req.reason,
    approval: req.approval_status,
    personInchargeId: req.person_incharge_id,
    personInchargeName: req.person_incharge_name,
    paymentDate: req.payment_date,
    rejectionReason: req.rejection_reason,
    pendingReason: req.pending_reason,
    approvalDate: req.approval_date,
    approvedById: req.approved_by_id,
    approvedByName: req.approved_by_name,
    images: req.images || []
  });

  // Fetch payment requests from database
  useEffect(() => {
    // Vendor-Wise fetches its own lightweight summary (below) instead of this
    // full company-wide list — landing on it (or switching between vendors
    // within it) must never trigger this heavy fetch.
    const onVendorWiseTab = isMobile ? tabValue === 3 : deskStatusTab === "VendorWise";
    if (onVendorWiseTab) return;

    const fetchPaymentRequests = async () => {
      try {
        if (!user?.company_id) return;
        setLoading(true);

        const isMainUser = user?.user_type === 'main';
        // Main user OR Person Incharge should see all requests for proper filtering.
        // Transactions Visibility (access_rights.vendorTransactionScope) lets an
        // admin explicitly grant "All Transactions" to someone who isn't a
        // person incharge too — additive, doesn't take away the existing rule.
        const shouldShowAll = isMainUser || isUserPersonIncharge || user?.access_rights?.vendorTransactionScope === 'all';

        const response = await getAllPaymentRequests({
          company_id: user.company_id,
          user_id: user._id,
          show_all: shouldShowAll ? 'true' : 'false'
        });

        if (response.success) {
          setPaymentRequests(response.data.map(mapPaymentRequestFromApi));
        }
      } catch (error) {
        console.error("Error fetching payment requests:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentRequests();
    // tabValue/deskStatusTab are included so switching tabs (Approvals/Payments/All
    // on mobile, the status pipeline on desktop) — which otherwise just re-filters
    // one already-fetched array client-side — pulls fresh data from the server
    // every time, instead of showing whatever was loaded when the page first mounted.
  }, [user?.company_id, user?._id, user?.user_type, isUserPersonIncharge, tabValue, deskStatusTab, isMobile]);

  // Vendor-Wise: lightweight per-vendor summary (Approved/Pending count +
  // value), server-paginated + searched — never depends on the big
  // paymentRequests array above.
  useEffect(() => {
    const onVendorWiseTab = isMobile ? tabValue === 3 : deskStatusTab === "VendorWise";
    if (!onVendorWiseTab || vendorWiseSelectedVendor) return;

    const fetchSummary = async () => {
      try {
        if (!user?.company_id) return;
        setVwSummaryLoading(true);
        const isMainUserLocal = user?.user_type === 'main';
        const shouldShowAll = isMainUserLocal || isUserPersonIncharge || user?.access_rights?.vendorTransactionScope === 'all';
        const params = {
          company_id: user.company_id,
          user_id: user._id,
          show_all: shouldShowAll ? 'true' : 'false',
          page: vwSummaryPage,
          limit: VENDOR_WISE_PAGE_SIZE,
        };
        if (vwDebouncedSearch) params.search = vwDebouncedSearch;
        if (vwFilterDateFrom) params.date_from = vwFilterDateFrom;
        if (vwFilterDateTo) params.date_to = vwFilterDateTo;
        const response = await getVendorPaymentSummary(params);
        if (response.success) {
          setVwSummary(response.data);
          setVwSummaryTotal(response.total ?? response.data.length);
          if (response.grandTotals) setVwGrandTotals(response.grandTotals);
        }
      } catch (error) {
        console.error("Error fetching vendor payment summary:", error);
      } finally {
        setVwSummaryLoading(false);
      }
    };
    fetchSummary();
  }, [isMobile, tabValue, deskStatusTab, vendorWiseSelectedVendor, user?.company_id, user?._id, isUserPersonIncharge, vwSummaryPage, vwDebouncedSearch, vwFilterDateFrom, vwFilterDateTo]);

  // Vendor-Wise: once a specific vendor is clicked, fetch THAT vendor's own
  // payment history fresh — scoped server-side by vendor_name, so this never
  // needs the big cross-vendor paymentRequests array either.
  useEffect(() => {
    if (!vendorWiseSelectedVendor) {
      setVwVendorPayments([]);
      return;
    }
    const fetchVendorPayments = async () => {
      try {
        if (!user?.company_id) return;
        setVwVendorLoading(true);
        const isMainUserLocal = user?.user_type === 'main';
        const shouldShowAll = isMainUserLocal || isUserPersonIncharge || user?.access_rights?.vendorTransactionScope === 'all';
        const response = await getAllPaymentRequests({
          company_id: user.company_id,
          user_id: user._id,
          vendor_name: vendorWiseSelectedVendor,
          show_all: shouldShowAll ? 'true' : 'false',
        });
        if (response.success) {
          setVwVendorPayments(response.data.map(mapPaymentRequestFromApi).filter(isRowTabVisible));
        }
      } catch (error) {
        console.error("Error fetching vendor's payment history:", error);
        setVwVendorPayments([]);
      } finally {
        setVwVendorLoading(false);
      }
    };
    fetchVendorPayments();
  }, [vendorWiseSelectedVendor, user?.company_id, user?._id, isUserPersonIncharge]);

  // Project-Wise: lightweight per-client ("Project") summary, server-
  // paginated + searched — mirrors the Vendor-Wise summary fetch above.
  useEffect(() => {
    const onProjectWiseTab = isMobile ? false : deskStatusTab === "ProjectWise";
    if (!onProjectWiseTab || projectWiseSelectedProject) return;

    const fetchSummary = async () => {
      try {
        if (!user?.company_id) return;
        setPwSummaryLoading(true);
        const isMainUserLocal = user?.user_type === 'main';
        const shouldShowAll = isMainUserLocal || isUserPersonIncharge || user?.access_rights?.vendorTransactionScope === 'all';
        const params = {
          company_id: user.company_id,
          user_id: user._id,
          show_all: shouldShowAll ? 'true' : 'false',
          page: pwSummaryPage,
          limit: PROJECT_WISE_PAGE_SIZE,
        };
        if (pwDebouncedSearch) params.search = pwDebouncedSearch;
        const response = await getProjectPaymentSummary(params);
        if (response.success) {
          setPwSummary(response.data);
          setPwSummaryTotal(response.total ?? response.data.length);
          if (response.grandTotals) setPwGrandTotals(response.grandTotals);
        }
      } catch (error) {
        console.error("Error fetching project payment summary:", error);
      } finally {
        setPwSummaryLoading(false);
      }
    };
    fetchSummary();
  }, [isMobile, deskStatusTab, projectWiseSelectedProject, user?.company_id, user?._id, isUserPersonIncharge, pwSummaryPage, pwDebouncedSearch]);

  // Project-Wise: once a specific project (client) is clicked, fetch THAT
  // client's own payment history fresh — reuses getAllPaymentRequests's
  // existing client_id filter (the same one client_balance_sheet.js's own
  // vendor ledger already relies on), so no new endpoint is needed here.
  useEffect(() => {
    if (!projectWiseSelectedProject) {
      setPwProjectPayments([]);
      return;
    }
    const fetchProjectPayments = async () => {
      try {
        if (!user?.company_id) return;
        setPwProjectLoading(true);
        const isMainUserLocal = user?.user_type === 'main';
        const shouldShowAll = isMainUserLocal || isUserPersonIncharge || user?.access_rights?.vendorTransactionScope === 'all';
        const response = await getAllPaymentRequests({
          company_id: user.company_id,
          user_id: user._id,
          client_id: projectWiseSelectedProject.id,
          show_all: shouldShowAll ? 'true' : 'false',
        });
        if (response.success) {
          setPwProjectPayments(response.data.map(mapPaymentRequestFromApi).filter(isRowTabVisible));
        }
      } catch (error) {
        console.error("Error fetching project's payment history:", error);
        setPwProjectPayments([]);
      } finally {
        setPwProjectLoading(false);
      }
    };
    fetchProjectPayments();
  }, [projectWiseSelectedProject, user?.company_id, user?._id, isUserPersonIncharge]);

  // Fetch person incharge for logged-in user from approval matrix
  // Also check if logged-in user is a Person Incharge for anyone
  useEffect(() => {
    const fetchApprovalMatrixData = async () => {
      try {
        if (!user?.company_id || !user?._id) return;

        const response = await getAllApprovalMatrix({
          company_id: user.company_id
        });

        console.log("=== DEBUG: Approval Matrix ===");
        console.log("Logged in user:", user.name, "| User ID:", user._id);
        console.log("Approval Matrix Data:", response.data);

        if (response.success) {
          // Find if logged-in user is in the approval matrix as an employee
          const matrixEntry = response.data.find(
            entry => entry.employee_id === user._id ||
                     String(entry.employee_id) === String(user._id)
          );
          if (matrixEntry) {
            setPersonIncharge({
              id: matrixEntry.person_incharge_id,
              name: matrixEntry.person_incharge
            });
            console.log("User's Person Incharge:", matrixEntry.person_incharge);
          }

          // Check if logged-in user is a Person Incharge for anyone
          const isPersonInchargeForSomeone = response.data.some(
            entry => entry.person_incharge_id === user._id ||
                     String(entry.person_incharge_id) === String(user._id)
          );
          console.log("Is user a Person Incharge for someone?", isPersonInchargeForSomeone);

          // Log each matrix entry comparison for debugging
          response.data.forEach(entry => {
            console.log(`Matrix Entry: ${entry.employee_name} → ${entry.person_incharge}`);
            console.log(`  person_incharge_id: ${entry.person_incharge_id}`);
            console.log(`  user._id: ${user._id}`);
            console.log(`  Match: ${String(entry.person_incharge_id) === String(user._id)}`);
          });

          setIsUserPersonIncharge(isPersonInchargeForSomeone);
        }
      } catch (error) {
        console.error("Error fetching approval matrix data:", error);
      }
    };

    fetchApprovalMatrixData();
  }, [user?.company_id, user?._id]);

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
  };

  const handlePaymentRequest = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedDate(new Date().toISOString().split('T')[0]); // Reset to today's date
    setSelectedClient(null);
    setSelectedVendor(null);
    setAmount("");
    setIsOverBudget(false);
    setOverBudgetReason("");
    setIsEditMode(false);
    setEditingRequestId(null);
    setEditingVendorName(null);
    setVendorLockedForRequest(false);
    setClientLockedForRequest(false);
    setFormError('');
    setDocumentError('');
    setClientSearchInput('');
    clearRequestImages();
  };

  // "Add Payments" — Vendor-Wise's drill-down header button. Opens the same
  // Payment Request form pre-filled with this vendor, locked so it can't be
  // swapped for a different one; Client is left blank for the user to pick.
  const handleAddPaymentForVendor = (vendorName) => {
    const match = categorizedVendorOptions.find((v) => v.name.toLowerCase() === (vendorName || '').toLowerCase());
    const vendor = match || (() => {
      const meta = findVendorMeta(vendorName);
      return meta ? {
        id: `inactive_${meta._id}`,
        name: meta.vendor_name,
        type: meta.vendor_type === 'material_vendor' ? 'Material' : 'Labour',
        category: meta.vendor_category || null,
        subCategory: meta.vendor_sub_category || null,
      } : null;
    })();
    if (!vendor) {
      alert("Couldn't find this vendor's details. Please try again.");
      return;
    }
    setSelectedDate(new Date().toISOString().split('T')[0]);
    setSelectedClient(null);
    setSelectedVendor(vendor);
    setAmount("");
    setIsOverBudget(false);
    setOverBudgetReason("");
    setIsEditMode(false);
    setEditingRequestId(null);
    setEditingVendorName(null);
    clearRequestImages();
    setVendorLockedForRequest(true);
    setClientLockedForRequest(false);
    setFormError('');
    setDocumentError('');
    setOpenDialog(true);
  };

  // "Add Payments" — Project-Wise's drill-down header button. Mirror of
  // handleAddPaymentForVendor above: Client is pre-filled and frozen, Vendor
  // is left blank for the user to pick.
  const handleAddPaymentForProject = (clientId, clientName) => {
    setSelectedDate(new Date().toISOString().split('T')[0]);
    setSelectedClient({ id: clientId, name: clientName });
    setSelectedVendor(null);
    setAmount("");
    setIsOverBudget(false);
    setOverBudgetReason("");
    setIsEditMode(false);
    setEditingRequestId(null);
    setEditingVendorName(null);
    clearRequestImages();
    setVendorLockedForRequest(false);
    setClientLockedForRequest(true);
    setFormError('');
    setDocumentError('');
    setOpenDialog(true);
  };

  // Only the person who raised a request can edit it — reuses this same form
  const handleEditPaymentRequest = (request) => {
    setIsEditMode(true);
    setEditingRequestId(request.id);
    setSelectedDate(request.date ? new Date(request.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]);
    setSelectedClient({ id: request.clientId, name: request.clientName });
    setSelectedVendor(null);
    setEditingVendorName(request.vendorName); // resolved to the live vendor object once vendorOptions loads
    setAmount(String(request.amount ?? ""));
    setIsOverBudget(!!request.isOverBudget);
    setOverBudgetReason(request.reason || "");
    setRequestImages(
      (request.images || []).map((img) => ({ id: img.key, url: img.url, key: img.key, name: img.name, mimetype: img.mimetype, uploaded: true }))
    );
    setFormError('');
    setDocumentError('');
    setStatusDialogOpen(false);
    setOpenDialog(true);
  };

  // Check if amount exceeds budget when amount changes
  const handleAmountChange = (value) => {
    setAmount(value);
    if (selectedVendor && value) {
      const budgetAvailable = getPoolBudgetAvailable(selectedVendor);
      const enteredAmount = parseFloat(value) || 0;
      setIsOverBudget(enteredAmount > budgetAvailable);
    } else {
      setIsOverBudget(false);
    }
  };

  const handleAddRequest = async () => {
    setFormError('');
    setDocumentError('');

    if (!selectedClient || !selectedVendor || !amount) {
      setFormError("Please fill all fields");
      return;
    }

    // A supporting document (image, PDF, or Word/Excel file) is mandatory for
    // Material vendors; optional for Labour, since labour payments often have
    // no invoice/bill to attach.
    if (selectedVendor.type === 'Material' && requestImages.length === 0) {
      setDocumentError("Document uploading is compulsory for Material vendors — please attach at least one supporting document");
      return;
    }

    // If over budget, reason is required
    if (isOverBudget && !overBudgetReason.trim()) {
      setFormError("Please enter reason for over-budget request");
      return;
    }

    // If over budget, Person Incharge must be assigned in Approval Matrix
    if (isOverBudget && !personIncharge) {
      setFormError("Over-budget requests require a Person Incharge. Please ensure you are added to the Approval Matrix with an assigned Person Incharge.");
      return;
    }

    try {
      const budgetAvailable = getPoolBudgetAvailable(selectedVendor);
      const enteredAmount = parseFloat(amount);

      // Debug log to verify person_incharge_id
      console.log("Creating request with person_incharge_id:", personIncharge?.id);

      const requestData = {
        date: selectedDate,
        client_id: selectedClient.id,
        client_name: selectedClient.name,
        vendor_name: selectedVendor.name,
        payment_purpose: "Payment Request",
        amount: enteredAmount,
        budget_available: budgetAvailable,
        is_over_budget: isOverBudget,
        reason: isOverBudget ? overBudgetReason : "",
        approval_status: isOverBudget ? "Pending" : "Approved",
        person_incharge_id: isOverBudget ? personIncharge.id : null,
        person_incharge_name: isOverBudget ? personIncharge.name : ""
      };

      if (isEditMode && editingRequestId) {
        const response = await updatePaymentRequest(editingRequestId, requestData);
        if (response.success) {
          setPaymentRequests((prev) =>
            prev.map((r) =>
              r.id === editingRequestId
                ? {
                    ...r,
                    date: response.data.date,
                    vendorName: response.data.vendor_name,
                    clientName: response.data.client_name,
                    clientId: response.data.client_id,
                    amount: response.data.amount,
                    budgetAvailable: response.data.budget_available,
                    isOverBudget: response.data.is_over_budget,
                    reason: response.data.reason,
                    approval: response.data.approval_status,
                    personInchargeId: response.data.person_incharge_id,
                    personInchargeName: response.data.person_incharge_name,
                    images: response.data.images || r.images || []
                  }
                : r
            )
          );
          await uploadPendingRequestImages(editingRequestId);
          handleCloseDialog();
        }
        return;
      }

      const response = await addPaymentRequest(requestData);

      if (response.success) {
        // Add to local state
        const newRequest = {
          id: response.data._id,
          userId: response.data.user_id,
          requestedByName: user?.fullname || '',
          date: response.data.date,
          vendorName: response.data.vendor_name,
          clientName: response.data.client_name,
          clientId: response.data.client_id,
          paymentPurpose: response.data.payment_purpose,
          amount: response.data.amount,
          budgetAvailable: response.data.budget_available,
          isOverBudget: response.data.is_over_budget,
          reason: response.data.reason,
          approval: response.data.approval_status,
          personInchargeId: response.data.person_incharge_id,
          personInchargeName: response.data.person_incharge_name,
          images: response.data.images || []
        };
        setPaymentRequests([newRequest, ...paymentRequests]);
        await uploadPendingRequestImages(response.data._id);
        handleCloseDialog();
      }
    } catch (error) {
      console.error(isEditMode ? "Error updating payment request:" : "Error adding payment request:", error);
      alert(isEditMode ? "Error updating payment request" : "Error adding payment request");
    }
  };

  // Uploads any locally-picked (not-yet-persisted) images once the request has an ID
  const uploadPendingRequestImages = async (requestId) => {
    const pending = requestImages.filter((img) => !img.uploaded && img.file);
    if (pending.length === 0) return;
    try {
      setUploadingImages(true);
      const response = await uploadPaymentRequestImages(requestId, pending.map((img) => img.file));
      if (response.success) {
        setPaymentRequests((prev) => prev.map((r) => (r.id === requestId ? { ...r, images: response.data } : r)));
      }
    } catch (error) {
      console.error("Error uploading images:", error);
      alert("The payment request was saved, but uploading the images failed. You can add them again by editing the request.");
    } finally {
      setUploadingImages(false);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === 0) return "₹0";
    return `₹${parseFloat(value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
  };

  const getApprovalColor = (status) => {
    switch(status) {
      case "Approved":
        return "#59AC77"; // Green
      case "Pending":
        return "#E8DE61"; // Yellow
      case "Correction":
        return "#F79009"; // Amber — sent back for correction (Pending + a pending_reason)
      case "Rejected":
        return "#FF8570"; // Red
      case "Paid":
        return "#60BEF7"; // Blue
      default:
        return "#FFFFFF";
    }
  };

  // ===== Mobile derived data (mirrors the desktop tab/filter logic exactly) =====
  // Being a registered Person Incharge has always granted the Approvals view
  // (it's their own approval queue) — Tabs Access adds an explicit, admin-
  // controlled way to grant it too, without taking away that existing route.
  const canSeePendingTabM = user?.user_type === "main" || isUserPersonIncharge;
  // Fixed slots — 0=Approvals, 1=Payments, 2=All. Pills without access stay
  // visible (frozen, unclickable) instead of disappearing, so it's clear
  // there's more to this section than what's currently unlocked.
  const approvalsIdxM = 0, paymentsIdxM = 1, allIdxM = 2, vendorWiseIdxM = 3;
  const canAccessApprovalsM = hasTabPending || canSeePendingTabM;
  const canAccessPaymentsM = hasTabApproved;
  const canAccessAllM = hasTabAll;
  const mOnVendorWiseTab = tabValue === vendorWiseIdxM;
  // If the current tabValue points at a frozen pill (e.g. on first load, or a
  // permission was just revoked), fall back to the first unlocked one.
  // Vendor-Wise sits last — it only becomes the fallback if the other three
  // are all frozen, matching its position at the end of the desktop pipeline.
  useEffect(() => {
    const slots = [[approvalsIdxM, canAccessApprovalsM], [paymentsIdxM, canAccessPaymentsM], [allIdxM, canAccessAllM], [vendorWiseIdxM, hasTabVendorWise]];
    const currentOk = slots.find(([idx]) => idx === tabValue)?.[1];
    if (!currentOk) {
      const first = slots.find(([, ok]) => ok)?.[0];
      if (first !== undefined) setTabValue(first);
    }
    // eslint-disable-next-line
  }, [canAccessApprovalsM, canAccessPaymentsM, canAccessAllM, hasTabVendorWise]);

  // Was previously restricted to "assigned to me as person incharge" only —
  // that made sense back when the Approvals pill was exclusively for personal
  // approval queues, but now Tabs Access can explicitly grant broader (even
  // just View) access to Pending, which should show every pending request the
  // fetch already scoped in, exactly like desktop's Pending tab — not just
  // ones personally assigned. isRowTabVisible is what actually gates that.
  const pendingForMe = paymentRequests.filter((r) => r.approval === "Pending" && !r.pendingReason && isRowTabVisible(r));
  // Approved but not yet paid out — the "next step" after approval
  const paymentPending = paymentRequests.filter((r) => r.approval === "Approved");
  // Mobile views driven by the fixed pill slots above — a frozen (no-access)
  // slot never leaks its data, even if tabValue is technically still pointed
  // at it (e.g. every pill is frozen and there's nowhere left to fall back to).
  const mobileBase =
    tabValue === approvalsIdxM
      ? (canAccessApprovalsM ? pendingForMe : [])
      : tabValue === paymentsIdxM
        ? (canAccessPaymentsM ? paymentPending : [])
        : (canAccessAllM
            ? (selectedApprovalFilter
                ? paymentRequests.filter(isRowTabVisible).filter((r) => {
                    if (selectedApprovalFilter === "Correction") return r.approval === "Pending" && !!r.pendingReason;
                    if (selectedApprovalFilter === "Pending") return r.approval === "Pending" && !r.pendingReason;
                    return r.approval === selectedApprovalFilter;
                  })
                : paymentRequests.filter(isRowTabVisible))
            : []);
  const mSearch = searchQuery.trim().toLowerCase();
  const mobileList = mSearch
    ? mobileBase.filter(
        (r) =>
          (r.vendorName || "").toLowerCase().includes(mSearch) ||
          (r.clientName || "").toLowerCase().includes(mSearch)
      )
    : mobileBase;
  const mVisibleList = mobileList.slice(0, mVisibleCount);
  const mHasMore = mobileList.length > mVisibleCount;
  const mOnAllTab = tabValue === allIdxM;

  return (
    <Box>
      {loading && <LoadingGate icon={CurrencyRupeeRoundedIcon} message="Loading vendor payments…" />}

      {/* ===== MOBILE: payments command center ===== */}
      <Box sx={{ display: { xs: "block", md: "none" } }}>
        {/* sub-tabs: Approval Pending first, Payment Pending middle, All last */}
        <div className="arcdx-v-pills" style={{ marginBottom: 10 }}>
          <div
            className={`arcdx-v-pill${tabValue === approvalsIdxM && canAccessApprovalsM ? " active" : ""}${canAccessApprovalsM ? "" : " frozen"}`}
            onClick={() => canAccessApprovalsM && setTabValue(approvalsIdxM)}
            style={{ flex: 1.15 }}
          >
            Approvals{canAccessApprovalsM && pendingForMe.length > 0 ? ` (${pendingForMe.length})` : ""}
          </div>
          <div
            className={`arcdx-v-pill${tabValue === paymentsIdxM && canAccessPaymentsM ? " active" : ""}${canAccessPaymentsM ? "" : " frozen"}`}
            onClick={() => canAccessPaymentsM && setTabValue(paymentsIdxM)}
            style={{ flex: 1.15 }}
          >
            Payments{canAccessPaymentsM && paymentPending.length > 0 ? ` (${paymentPending.length})` : ""}
          </div>
          <div
            className={`arcdx-v-pill${mOnAllTab && canAccessAllM ? " active" : ""}${canAccessAllM ? "" : " frozen"}`}
            onClick={() => canAccessAllM && setTabValue(allIdxM)}
            style={{ flex: 0.7 }}
          >
            All
          </div>
          {hasTabVendorWise && (
            <div
              className={`arcdx-v-pill${mOnVendorWiseTab ? " active" : ""}`}
              onClick={() => setTabValue(vendorWiseIdxM)}
              style={{ flex: 1 }}
            >
              Vendor-Wise
            </div>
          )}
        </div>

        {mOnVendorWiseTab ? (() => {
          const rupee = (n) => `₹ ${Math.round(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
          const VENDOR_WISE_FILTERS = ["Approved", "Pending", "Correction", "Paid", "Rejected", "All"];
          const nSelected = vendorWiseSelectedIds.size;
          const pageNav = (page, totalPages, setPage) => (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1.5, mt: 1, mb: 0.5 }}>
              <Button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} sx={{ textTransform: "none", fontWeight: 700, fontSize: "12px", color: "#344054", background: "#f2f4f7", borderRadius: "9px", px: 1.4, py: "5px", minWidth: 0, "&.Mui-disabled": { color: "#c7ccd4", background: "#f7f8fa" } }}>Previous</Button>
              <Typography sx={{ fontSize: "12px", fontWeight: 700, color: "#344054" }}>Page {page} of {totalPages}</Typography>
              <Button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} sx={{ textTransform: "none", fontWeight: 700, fontSize: "12px", color: "#344054", background: "#f2f4f7", borderRadius: "9px", px: 1.4, py: "5px", minWidth: 0, "&.Mui-disabled": { color: "#c7ccd4", background: "#f7f8fa" } }}>Next</Button>
            </Box>
          );

          if (!vendorWiseSelectedVendor) {
            return (
              <Box sx={{ mt: 1 }}>
                <div className="arcdx-v-search" style={{ marginBottom: 10 }}>
                  <SearchIcon sx={{ fontSize: 20, color: "#98a2b3" }} />
                  <input placeholder="Search vendor…" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
                </div>
                <Box sx={{ display: "flex", gap: "8px", mb: 1.25 }}>
                  <Box sx={{ flex: 1, background: "#ecfdf3", borderRadius: "12px", p: "8px 10px" }}>
                    <Box sx={{ fontSize: "9.5px", color: "#067647", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.04em" }}>Total Approved</Box>
                    <Box sx={{ fontSize: "13.5px", fontWeight: 800, color: "#101828" }}>{rupee(vwGrandTotals.approvedValue)}</Box>
                  </Box>
                  <Box sx={{ flex: 1, background: "#fffaeb", borderRadius: "12px", p: "8px 10px" }}>
                    <Box sx={{ fontSize: "9.5px", color: "#b54708", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.04em" }}>Total Pending</Box>
                    <Box sx={{ fontSize: "13.5px", fontWeight: 800, color: "#101828" }}>{rupee(vwGrandTotals.pendingValue)}</Box>
                  </Box>
                </Box>
                {vwSummaryLoading ? (
                  <Box sx={{ textAlign: "center", py: 4, color: "#98a2b3", fontSize: "12.5px" }}>Loading vendors…</Box>
                ) : vwSummary.length === 0 ? (
                  <div className="arcdx-v-empty">
                    <div className="big">🧾</div>
                    No vendors with payment activity yet
                  </div>
                ) : (
                  <>
                    {vwSummary.map((v) => (
                      <div key={v.vendor_name} className="arcdx-v-card" onClick={() => openVendorWiseVendor(v.vendor_name)} style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <Box sx={{ fontSize: "15px", fontWeight: 700, color: "#101828", minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", flex: 1, mr: 1 }}>{v.vendor_name}</Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
                          <Box sx={{ textAlign: "right" }}>
                            <Box sx={{ fontSize: "9px", color: "#059669", fontWeight: 800, textTransform: "uppercase" }}>Approved</Box>
                            <Box sx={{ fontSize: "11.5px", fontWeight: 800, color: "#101828" }}>{rupee(v.approvedValue)}</Box>
                          </Box>
                          <Box sx={{ textAlign: "right" }}>
                            <Box sx={{ fontSize: "9px", color: "#b54708", fontWeight: 800, textTransform: "uppercase" }}>Pending</Box>
                            <Box sx={{ fontSize: "11.5px", fontWeight: 800, color: "#101828" }}>{rupee(v.pendingValue)}</Box>
                          </Box>
                          <ChevronRightIcon sx={{ fontSize: 18, color: "#c7ccd4" }} />
                        </Box>
                      </div>
                    ))}
                    {vwSummaryTotalPages > 1 && pageNav(vwSummaryPage, vwSummaryTotalPages, setVwSummaryPage)}
                  </>
                )}
              </Box>
            );
          }

          return (
            <Box sx={{ mt: 1 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: "2px" }}>
                <IconButton onClick={closeVendorWiseVendor} size="small" sx={{ background: "#f2f4f7", flexShrink: 0 }}><ArrowBackIcon sx={{ fontSize: 18 }} /></IconButton>
                <Box sx={{ fontSize: "16px", fontWeight: 800, color: "#101828", flex: 1, minWidth: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{vendorWiseSelectedVendor}</Box>
                {canRequestPayment && (
                  <Button
                    onClick={() => handleAddPaymentForVendor(vendorWiseSelectedVendor)}
                    startIcon={<AddIcon sx={{ fontSize: "16px !important" }} />}
                    sx={{ textTransform: "none", fontWeight: 700, fontSize: "11.5px", color: "#fff", background: "linear-gradient(135deg,#39aafc,#1f8fe8)", borderRadius: "9px", px: 1.25, py: "5px", boxShadow: "0 6px 16px rgba(31,143,232,0.3)", flexShrink: 0, mr: "2px" }}
                  >
                    Add Payments
                  </Button>
                )}
              </Box>
              <Box sx={{ fontSize: "12px", color: "#667085", fontWeight: 700, mb: 1.25, ml: "40px" }}>
                {vendorWiseStatusFilter} · {rupee(vendorWiseFilteredTotal)} <span style={{ color: "#98a2b3", fontWeight: 600 }}>({vendorWiseFilteredPayments.length})</span>
              </Box>
              <div className="arcdx-v-chiprow">
                {VENDOR_WISE_FILTERS.map((status) => (
                  <div key={status} className={`arcdx-v-chip${vendorWiseStatusFilter === status ? " active" : ""}`} onClick={() => setVendorWiseStatusFilter(status)}>
                    {status}
                  </div>
                ))}
              </div>
              <Box sx={{ mt: 1 }}>
                {vwVendorLoading ? (
                  <Box sx={{ textAlign: "center", py: 4, color: "#98a2b3", fontSize: "12.5px" }}>Loading payments…</Box>
                ) : vendorWiseFilteredPayments.length === 0 ? (
                  <div className="arcdx-v-empty">
                    <div className="big">🧾</div>
                    No {vendorWiseStatusFilter === "All" ? "" : vendorWiseStatusFilter.toLowerCase()} payments for this vendor
                  </div>
                ) : (
                  <>
                    {vendorWisePagedPayments.map((request) => {
                      const status = getDisplayStatus(request);
                      const checkable = canActVendorWise && status === "Approved";
                      const checked = vendorWiseSelectedIds.has(request.id);
                      return (
                        <div key={request.id} className="arcdx-v-card" style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={!checkable}
                            onChange={() => checkable && toggleVendorWiseSelect(request.id)}
                            style={{ marginTop: "4px", width: 18, height: 18, flexShrink: 0, opacity: checkable ? 1 : 0.35 }}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "4px" }}>
                              <Box sx={{ fontSize: "13px", fontWeight: 700, color: "#4f46e5", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{request.clientName}</Box>
                              <span className="arcdx-v-status" style={{ background: getApprovalColor(status) + "55" }}>
                                <span style={{ width: 7, height: 7, borderRadius: "50%", background: getApprovalColor(status) }} />
                                {status}
                              </span>
                            </Box>
                            <Box sx={{ fontSize: "11px", color: "#98a2b3", fontWeight: 600 }}>{request.date ? new Date(request.date).toLocaleDateString("en-IN") : "—"}</Box>
                            <Box sx={{ fontSize: "16px", fontWeight: 800, color: "#101828", mt: "3px" }}>{rupee(request.amount)}</Box>
                          </Box>
                        </div>
                      );
                    })}
                    {vwDrillTotalPages > 1 && pageNav(vwDrillPageClamped, vwDrillTotalPages, setVwDrillPage)}
                  </>
                )}
              </Box>
              {nSelected > 0 && ReactDOM.createPortal(
                // Portalled straight to document.body — this component's mobile
                // content lives inside .app-shell's scrolling .main-content, and
                // iOS Safari (plus some Android browsers) confine position:fixed
                // descendants of a scrolling ancestor to that container instead
                // of the real viewport, which silently hid this bar. Same fix
                // already used for the other floating sheets in this app.
                <Box sx={{ position: "fixed", left: 12, right: 12, bottom: "calc(max(12px, env(safe-area-inset-bottom, 0px) - 12px) + 88px)", zIndex: 1300, background: "linear-gradient(135deg, #1d2939 0%, #0b0f19 100%)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "20px", p: "10px 10px 10px 14px", display: "flex", alignItems: "center", gap: "8px", boxShadow: "0 14px 34px rgba(16,24,40,0.45)" }}>
                  <Box sx={{ width: 26, height: 26, borderRadius: "50%", background: "rgba(57,170,252,0.25)", color: "#7dd3fc", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: 800, flexShrink: 0 }}>
                    {nSelected}
                  </Box>
                  <Box sx={{ color: "#fff", fontWeight: 700, fontSize: "12.5px", flex: 1 }}>Selected</Box>
                  <IconButton onClick={clearVendorWiseSelection} size="small" sx={{ color: "rgba(255,255,255,0.5)" }}>
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                  <Button
                    onClick={() => setVendorWiseRejectDialogOpen(true)}
                    startIcon={<CancelRoundedIcon sx={{ fontSize: "16px !important" }} />}
                    sx={{ textTransform: "none", color: "#fff", background: "rgba(255,133,112,0.18)", border: "1px solid rgba(255,133,112,0.4)", fontWeight: 700, fontSize: "12px", borderRadius: "999px", px: 1.4, py: "5px", minWidth: 0 }}
                  >
                    Reject
                  </Button>
                  <Button
                    onClick={() => setVendorWiseRemoveConfirmOpen(true)}
                    startIcon={<DeleteIcon sx={{ fontSize: "16px !important" }} />}
                    sx={{ textTransform: "none", color: "#fff", background: "linear-gradient(135deg,#f87171,#dc2626)", fontWeight: 700, fontSize: "12px", borderRadius: "999px", px: 1.4, py: "5px", minWidth: 0, boxShadow: "0 6px 16px rgba(220,38,38,0.4)" }}
                  >
                    Remove
                  </Button>
                </Box>,
                document.body
              )}
            </Box>
          );
        })() : (
        <>
        {/* search — filters the visible list by vendor or client */}
        <div className="arcdx-v-search" style={{ marginBottom: 10 }}>
          <SearchIcon sx={{ fontSize: 20, color: "#98a2b3" }} />
          <input placeholder="Search vendor or client…" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} />
        </div>

        {/* status filter chips (All tab, same filter state as desktop) */}
        {mOnAllTab && canAccessAllM && (
          <div className="arcdx-v-chiprow">
            {approvalFilterOptions.map((opt) => {
              const active = opt.value === "all" ? !selectedApprovalFilter : selectedApprovalFilter === opt.value;
              const visibleRequests = paymentRequests.filter(isRowTabVisible);
              const count =
                opt.value === "all"
                  ? visibleRequests.length
                  : opt.value === "Correction"
                    ? visibleRequests.filter((r) => r.approval === "Pending" && !!r.pendingReason).length
                    : opt.value === "Pending"
                      ? visibleRequests.filter((r) => r.approval === "Pending" && !r.pendingReason).length
                      : visibleRequests.filter((r) => r.approval === opt.value).length;
              return (
                <div
                  key={opt.value}
                  className={`arcdx-v-chip${active ? " active" : ""}`}
                  onClick={() => setSelectedApprovalFilter(opt.value === "all" ? null : opt.value)}
                >
                  {opt.value !== "all" && <span className="dot" style={{ background: opt.color }} />}
                  {opt.label} · {count}
                </div>
              );
            })}
          </div>
        )}

        {/* request cards */}
        <Box sx={{ mt: 1 }}>
          {mobileList.length === 0 ? (
            <div className="arcdx-v-empty">
              <div className="big">🧾</div>
              {mSearch
                ? "No matches for your search"
                : (tabValue === approvalsIdxM && !canAccessApprovalsM) || (tabValue === paymentsIdxM && !canAccessPaymentsM) || (tabValue === allIdxM && !canAccessAllM)
                  ? "You don't have access to this section"
                  : tabValue === approvalsIdxM
                    ? "Nothing waiting for your approval"
                    : tabValue === paymentsIdxM
                      ? "Nothing waiting to be paid"
                      : "No payment requests yet"}
              <Box sx={{ mt: 0.5, fontSize: "12px", color: "#b9c0cc" }}>
                Tap the + button to raise a vendor payment
              </Box>
            </div>
          ) : (
            mVisibleList.map((request) => {
              const isApprovalsView = tabValue === approvalsIdxM;
              const isPaymentsView = tabValue === paymentsIdxM;
              return (
              <div key={request.id} className="arcdx-v-card" onClick={() => canActOnStatus(getDisplayStatus(request)) && handleStatusCellClick(request)}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "6px" }}>
                  <Box sx={{ fontSize: "11px", color: "#98a2b3", fontWeight: 600 }}>
                    {request.date ? new Date(request.date).toLocaleDateString("en-IN") : "—"}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
                    {(isApprovalsView || isPaymentsView) && request.isOverBudget && (
                      <Box sx={{ fontSize: "10px", fontWeight: 700, color: "#b54708", background: "#fff7e6", border: "1px solid #f4c790", px: "7px", py: "2px", borderRadius: "999px" }}>
                        Over budget
                      </Box>
                    )}
                    <span className="arcdx-v-status" style={{ background: getApprovalColor(getDisplayStatus(request)) + "55" }}>
                      <span style={{ width: 7, height: 7, borderRadius: "50%", background: getApprovalColor(getDisplayStatus(request)) }} />
                      {getDisplayStatus(request)}
                    </span>
                  </Box>
                </Box>
                <Box sx={{ fontSize: "15px", fontWeight: 700, color: "#101828", lineHeight: 1.3 }}>
                  {request.vendorName}
                </Box>
                {isApprovalsView ? (
                  <>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, mt: "3px" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "5px", minWidth: 0, flex: "1 1 70%" }}>
                        <Box sx={{ fontSize: "11px", color: "#98a2b3", fontWeight: 600, flexShrink: 0 }}>Client:</Box>
                        <Box sx={{ fontSize: "12.5px", fontWeight: 700, color: "#4f46e5", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {request.clientName}
                        </Box>
                      </Box>
                      {/* "By:" (person incharge) hidden for now
                      {request.personInchargeName && (
                        <Box sx={{ display: "flex", alignItems: "center", gap: "4px", minWidth: 0, flex: "0 1 30%", justifyContent: "flex-end" }}>
                          <Box sx={{ fontSize: "11px", color: "#98a2b3", fontWeight: 600, flexShrink: 0 }}>By:</Box>
                          <Box sx={{ fontSize: "12.5px", fontWeight: 700, color: "#b54708", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {request.personInchargeName}
                          </Box>
                        </Box>
                      )}
                      */}
                    </Box>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mt: "10px" }}>
                      <Box>
                        <Box sx={{ fontSize: "10px", color: "#98a2b3", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>Available Budget</Box>
                        <Box sx={{ fontSize: "14.5px", fontWeight: 800, color: "#101828" }}>{formatCurrency(request.budgetAvailable)}</Box>
                      </Box>
                      <Box>
                        <Box sx={{ fontSize: "10px", color: "#98a2b3", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>Amount Requested</Box>
                        <Box sx={{ fontSize: "14.5px", fontWeight: 800, color: request.isOverBudget ? "#d92d20" : "#101828" }}>{formatCurrency(request.amount)}</Box>
                      </Box>
                    </Box>
                  </>
                ) : isPaymentsView ? (
                  <>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "5px", mt: "3px", minWidth: 0 }}>
                      <Box sx={{ fontSize: "11px", color: "#98a2b3", fontWeight: 600, flexShrink: 0 }}>Client:</Box>
                      <Box sx={{ fontSize: "12.5px", fontWeight: 700, color: "#4f46e5", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                        {request.clientName}
                      </Box>
                    </Box>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mt: "10px" }}>
                      <Box>
                        <Box sx={{ fontSize: "10px", color: "#98a2b3", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>Available Budget</Box>
                        <Box sx={{ fontSize: "14.5px", fontWeight: 800, color: "#101828" }}>{formatCurrency(request.budgetAvailable)}</Box>
                      </Box>
                      <Box>
                        <Box sx={{ fontSize: "10px", color: "#98a2b3", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>Amount Approved</Box>
                        <Box sx={{ fontSize: "14.5px", fontWeight: 800, color: request.isOverBudget ? "#d92d20" : "#059669" }}>{formatCurrency(request.amount)}</Box>
                      </Box>
                    </Box>
                    {/* Who actually approved it — real name if a human clicked Approve, else auto-approved */}
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: "10px", pt: "8px", borderTop: "1px solid #f2f4f7" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "5px", minWidth: 0 }}>
                        <Box sx={{ fontSize: "11px", color: "#98a2b3", fontWeight: 600, flexShrink: 0 }}>Approved By:</Box>
                        <Box sx={{ fontSize: "12.5px", fontWeight: 700, color: "#059669", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {request.approvedByName || "Auto-approved"}
                        </Box>
                      </Box>
                      {request.approvalDate && (
                        <Box sx={{ fontSize: "10.5px", color: "#98a2b3", fontWeight: 600, flexShrink: 0 }}>
                          {new Date(request.approvalDate).toLocaleDateString("en-IN")}
                        </Box>
                      )}
                    </Box>
                  </>
                ) : (
                  <>
                    <Box sx={{ fontSize: "12px", color: "#667085", mt: "1px" }}>{request.clientName}</Box>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: "10px" }}>
                      <Box sx={{ fontSize: "17px", fontWeight: 800, color: "#101828", letterSpacing: "-0.01em" }}>
                        {formatCurrency(request.amount)}
                      </Box>
                      {request.isOverBudget && (
                        <Box sx={{ fontSize: "10.5px", fontWeight: 700, color: "#b54708", background: "#fff7e6", border: "1px solid #f4c790", px: "8px", py: "3px", borderRadius: "999px" }}>
                          Over budget
                        </Box>
                      )}
                    </Box>
                    {/* Requester's view: show who the request went to for approval */}
                    {request.approval === "Pending" && request.personInchargeName && (
                      <Box sx={{ fontSize: "11px", color: "#667085", mt: "7px" }}>
                        Approval Sent to {request.personInchargeName}
                      </Box>
                    )}
                  </>
                )}
              </div>
              );
            })
          )}
          {mHasMore && (
            <Box
              onClick={() => setMVisibleCount((c) => c + PAGE_SIZE)}
              sx={{ textAlign: "center", py: 1.4, mt: 1, borderRadius: "14px", border: "1.5px dashed #b8dffb", color: "#1565C0", fontWeight: 700, fontSize: "13.5px", cursor: "pointer", "&:active": { background: "#f2f8ff" } }}
            >
              Load More ({mobileList.length - mVisibleList.length} remaining)
            </Box>
          )}
        </Box>
        </>
        )}

        {/* Floating Raise Payment — indigo-violet with a breathing glow.
            Hidden on Vendor-Wise: raising a new request isn't this view's
            purpose, and it would sit right where the bulk action bar appears. */}
        {canRequestPayment && !mOnVendorWiseTab && (
          <button type="button" className="arcdx-v-fab payment" onClick={handlePaymentRequest} aria-label="Add vendor payment">
            <CurrencyRupeeRoundedIcon sx={{ fontSize: 26 }} />
          </button>
        )}
      </Box>

      {/* Desktop status pipeline — All / Pending / Approved / Paid / Rejected with live counts */}
      <Box
        sx={{
          display: { xs: "none", md: "inline-flex" },
          gap: "4px",
          background: "#f2f4f7",
          borderRadius: "13px",
          p: "4px",
          marginBottom: "20px",
        }}
      >
        {[
          { key: "all", label: "All", dot: null, count: paymentRequests.filter(isRowTabVisible).length, show: hasTabAll },
          { key: "Pending", label: "Pending", dot: "#E8DE61", count: paymentRequests.filter((r) => r.approval === "Pending" && !r.pendingReason).length, show: hasTabPending },
          { key: "Approved", label: "Approved", dot: "#59AC77", count: paymentRequests.filter((r) => r.approval === "Approved").length, show: hasTabApproved },
          { key: "Paid", label: "Paid", dot: "#60BEF7", count: paymentRequests.filter((r) => r.approval === "Paid").length, show: hasTabPaid },
          { key: "Correction", label: "Correction", dot: "#F79009", count: paymentRequests.filter((r) => r.approval === "Pending" && r.pendingReason).length, show: hasTabCorrection },
          { key: "Rejected", label: "Rejected", dot: "#FF8570", count: paymentRequests.filter((r) => r.approval === "Rejected").length, show: hasTabRejected },
          { key: "VendorWise", label: "Vendor-Wise", dot: "#7c3aed", count: vwSummaryTotal, show: hasTabVendorWise },
          { key: "ProjectWise", label: "Project-Wise", dot: "#0d9488", count: pwSummaryTotal, show: hasTabProjectWise },
        ].filter((t) => t.show).map((t) => {
          const active = deskStatusTab === t.key;
          return (
            <Box
              key={t.key}
              onClick={() => setDeskStatusTab(t.key)}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                px: 1.75,
                py: "8px",
                borderRadius: "10px",
                fontSize: "13px",
                fontWeight: active ? 700 : 600,
                fontFamily: "Roboto, Arial, sans-serif",
                color: active ? "#1565C0" : "#667085",
                background: active ? "#ffffff" : "transparent",
                boxShadow: active ? "0 1px 3px rgba(16,24,40,0.10)" : "none",
                cursor: "pointer",
                transition: "all 0.2s ease",
                userSelect: "none",
                whiteSpace: "nowrap",
                "&:hover": { color: "#1565C0" },
              }}
            >
              {t.dot && <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: t.dot, flexShrink: 0 }} />}
              {t.label}
              <Box
                sx={{
                  fontSize: "10.5px",
                  fontWeight: 800,
                  px: "6px",
                  py: "1px",
                  borderRadius: "999px",
                  background: active ? "rgba(57,170,252,0.12)" : "#e4e7ec",
                  color: active ? "#1565C0" : "#667085",
                  lineHeight: 1.6,
                }}
              >
                {t.count}
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Desktop unified ledger — one premium table driven by the status pipeline.
          Data visibility is unchanged: the server already scopes paymentRequests
          via show_all (main/incharge see all, others see their own + assigned). */}
      {(() => {
        const q = (searchQuery || "").trim().toLowerCase();

        // Vendor-Wise: a completely different shape (vendor list -> drill into
        // one vendor's payments across every project) instead of the flat,
        // status-filtered ledger every other pipeline tab shares below.
        if (deskStatusTab === "VendorWise") {
          const rupee = (n) => `₹ ${Math.round(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
          const VENDOR_WISE_FILTERS = ["Approved", "Pending", "Correction", "Paid", "Rejected", "All"];
          const nSelected = vendorWiseSelectedIds.size;
          const totalsChip = (label, value, bg, fg) => (
            <Box sx={{ display: "flex", alignItems: "baseline", gap: "5px", px: 1.25, py: "5px", borderRadius: "999px", background: bg }}>
              <Typography sx={{ fontSize: "10.5px", fontWeight: 800, color: fg, textTransform: "uppercase", letterSpacing: "0.03em" }}>{label}</Typography>
              <Typography sx={{ fontSize: "12px", fontWeight: 800, color: "#101828", fontFamily: "Roboto, Arial, sans-serif" }}>{rupee(value)}</Typography>
            </Box>
          );
          const pageNav = (page, totalPages, setPage) => (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, padding: "14px 20px", borderTop: "1px solid #eef2f7" }}>
              <Typography sx={{ fontSize: "12.5px", color: "#98a2b3", fontWeight: 600 }}>Page {page} of {totalPages}</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} sx={{ textTransform: "none", fontWeight: 700, fontSize: "12.5px", color: "#344054", background: "#f2f4f7", borderRadius: "9px", px: 1.5, py: "6px", minWidth: 0, "&.Mui-disabled": { color: "#c7ccd4", background: "#f7f8fa" } }}>Previous</Button>
                <Button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} sx={{ textTransform: "none", fontWeight: 700, fontSize: "12.5px", color: "#344054", background: "#f2f4f7", borderRadius: "9px", px: 1.5, py: "6px", minWidth: 0, "&.Mui-disabled": { color: "#c7ccd4", background: "#f7f8fa" } }}>Next</Button>
              </Box>
            </Box>
          );

          return (
            <Box sx={{ display: { xs: "none", md: "block" }, background: "#ffffff", border: "1px solid #e9eef5", borderRadius: "18px", boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 8px 24px rgba(13,43,92,0.05)", overflow: "hidden" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: "16px 20px", borderBottom: "1px solid #eef2f7", flexWrap: "wrap" }}>
                {vendorWiseSelectedVendor && (
                  <IconButton onClick={closeVendorWiseVendor} size="small" sx={{ background: "#f2f4f7" }}><ArrowBackIcon sx={{ fontSize: 18 }} /></IconButton>
                )}
                <Typography sx={{ fontSize: "15.5px", fontWeight: 800, color: "#101828", letterSpacing: "-0.01em", fontFamily: "Roboto, Arial, sans-serif" }}>
                  {vendorWiseSelectedVendor || "Vendor-Wise"}
                </Typography>
                <Box sx={{ px: 1.25, py: "3px", borderRadius: "999px", background: "rgba(124,58,237,0.10)", color: "#7c3aed", fontSize: "11.5px", fontWeight: 800, fontFamily: "Roboto, Arial, sans-serif" }}>
                  {vendorWiseSelectedVendor ? vendorWiseFilteredPayments.length : vwSummaryTotal}
                </Box>
                {vendorWiseSelectedVendor
                  ? totalsChip(vendorWiseStatusFilter, vendorWiseFilteredTotal, "rgba(124,58,237,0.08)", "#7c3aed")
                  : vwFilterActive
                  ? totalsChip(`Total ${vwFilterStatus}`, vwGrandTotals[VW_STATUS_FIELD[vwFilterStatus]], "rgba(124,58,237,0.08)", "#7c3aed")
                  : (
                    <>
                      {totalsChip("Total Approved", vwGrandTotals.approvedValue, "#ecfdf3", "#067647")}
                      {totalsChip("Total Pending", vwGrandTotals.pendingValue, "#fffaeb", "#b54708")}
                    </>
                  )}
                <Tooltip title={vwFilterActive ? "Filter active — click to change" : "Filter by status / date"}>
                  <IconButton
                    onClick={openVwFilter}
                    size="small"
                    sx={{
                      width: 34, height: 34, borderRadius: "10px",
                      border: vwFilterActive ? "1px solid transparent" : "1px solid #e4e7ec",
                      color: vwFilterActive ? "#ffffff" : "#5f6f86",
                      background: vwFilterActive ? "linear-gradient(135deg, #4db2fc, #1565C0)" : "#ffffff",
                      boxShadow: vwFilterActive ? "0 6px 16px rgba(21,101,192,0.32)" : "none",
                      "&:hover": vwFilterActive
                        ? { background: "linear-gradient(135deg, #39aafc, #0d47a1)" }
                        : { borderColor: "#39aafc", color: "#1565C0", background: "rgba(57,170,252,0.08)" },
                    }}
                  >
                    <FilterAltIcon sx={{ fontSize: 17 }} />
                  </IconButton>
                </Tooltip>
                <Box sx={{ flex: 1 }} />
                {!vendorWiseSelectedVendor && (
                  <Search sx={{ backgroundColor: "#ffffff", border: "1px solid #e4e7ec", borderRadius: "12px", marginRight: 0, "&:hover": { backgroundColor: "#ffffff" }, "&:focus-within": { borderColor: "#39aafc", boxShadow: "0 0 0 4px rgba(57,170,252,0.12)" } }}>
                    <SearchIconWrapper><SearchIcon sx={{ fontSize: 19, color: "#98a2b3" }} /></SearchIconWrapper>
                    <StyledInputBase placeholder="Search vendor…" inputProps={{ "aria-label": "search" }} value={searchQuery} onChange={handleSearchChange} />
                  </Search>
                )}
                {vendorWiseSelectedVendor && canRequestPayment && (
                  <Button
                    onClick={() => handleAddPaymentForVendor(vendorWiseSelectedVendor)}
                    startIcon={<AddIcon sx={{ fontSize: "18px !important" }} />}
                    sx={{ textTransform: "none", fontWeight: 700, fontSize: "12.5px", color: "#fff", background: "linear-gradient(135deg,#39aafc,#1f8fe8)", borderRadius: "10px", px: 1.75, py: "6px", boxShadow: "0 6px 16px rgba(31,143,232,0.3)", mr: 0.5 }}
                  >
                    Add Payments
                  </Button>
                )}
              </Box>

              <Popover
                open={Boolean(vwFilterAnchor)}
                anchorEl={vwFilterAnchor}
                onClose={closeVwFilter}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{ sx: { borderRadius: "16px", border: "1px solid #e9eef5", boxShadow: "0 12px 32px rgba(16,24,40,0.14)", mt: 0.75, p: "18px", width: "300px" } }}
              >
                <Typography sx={{ fontSize: "12px", fontWeight: 800, color: "#101828", textTransform: "uppercase", letterSpacing: "0.04em", mb: 1 }}>Status</Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mb: 2 }}>
                  {VENDOR_WISE_FILTER_STATUSES.map((status) => {
                    const active = vwFilterDraftStatus === status;
                    return (
                      <Box
                        key={status}
                        onClick={() => setVwFilterDraftStatus(status)}
                        sx={{ px: 1.5, py: "6px", borderRadius: "999px", fontSize: "12.5px", fontWeight: 700, cursor: "pointer", color: active ? "#7c3aed" : "#667085", background: active ? "rgba(124,58,237,0.10)" : "#f2f4f7" }}
                      >
                        {status}
                      </Box>
                    );
                  })}
                </Box>
                <Typography sx={{ fontSize: "12px", fontWeight: 800, color: "#101828", textTransform: "uppercase", letterSpacing: "0.04em", mb: 1 }}>Date Range</Typography>
                <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                  <TextField
                    label="From" type="date" size="small" fullWidth
                    value={vwFilterDraftFrom}
                    onChange={(e) => setVwFilterDraftFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: "12.5px" } }}
                  />
                  <TextField
                    label="To" type="date" size="small" fullWidth
                    value={vwFilterDraftTo}
                    onChange={(e) => setVwFilterDraftTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: "12.5px" } }}
                  />
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button onClick={clearVwFilter} sx={{ flex: 1, textTransform: "none", fontWeight: 700, fontSize: "12.5px", color: "#667085", background: "#f2f4f7", borderRadius: "10px", py: "8px" }}>Clear</Button>
                  <Button onClick={applyVwFilter} sx={{ flex: 1, textTransform: "none", fontWeight: 700, fontSize: "12.5px", color: "#fff", background: "linear-gradient(135deg,#39aafc,#1565C0)", borderRadius: "10px", py: "8px" }}>Apply</Button>
                </Box>
              </Popover>

              {!vendorWiseSelectedVendor ? (
                vwSummaryLoading ? (
                  <Box sx={{ p: "40px 20px", textAlign: "center", color: "#98a2b3", fontSize: "13px" }}>Loading vendors…</Box>
                ) : vwSummary.length === 0 ? (
                  <Box sx={{ p: "40px 20px", textAlign: "center", color: "#98a2b3", fontSize: "13px" }}>No vendors with payment activity yet</Box>
                ) : (
                  <>
                    {vwSummary.map((v, i) => (
                      <Box
                        key={v.vendor_name}
                        onClick={() => openVendorWiseVendor(v.vendor_name)}
                        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: "13px 20px", borderBottom: i < vwSummary.length - 1 ? "1px solid #f1f4f9" : "none", cursor: "pointer", "&:hover": { background: "#fafbfc" } }}
                      >
                        <Typography sx={{ fontSize: "13.5px", fontWeight: 700, color: "#101828" }}>{v.vendor_name}</Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
                          {vwFilterActive ? (
                            <Box sx={{ textAlign: "right", minWidth: "90px" }}>
                              <Typography sx={{ fontSize: "9.5px", color: "#7c3aed", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.03em" }}>{vwFilterStatus}</Typography>
                              <Typography sx={{ fontSize: "12.5px", fontWeight: 800, color: "#101828" }}>{rupee(v[VW_STATUS_FIELD[vwFilterStatus]])}</Typography>
                            </Box>
                          ) : (
                            <>
                              <Box sx={{ textAlign: "right", minWidth: "90px" }}>
                                <Typography sx={{ fontSize: "9.5px", color: "#059669", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.03em" }}>Approved</Typography>
                                <Typography sx={{ fontSize: "12.5px", fontWeight: 800, color: "#101828" }}>{rupee(v.approvedValue)}</Typography>
                              </Box>
                              <Box sx={{ textAlign: "right", minWidth: "90px" }}>
                                <Typography sx={{ fontSize: "9.5px", color: "#b54708", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.03em" }}>Pending</Typography>
                                <Typography sx={{ fontSize: "12.5px", fontWeight: 800, color: "#101828" }}>{rupee(v.pendingValue)}</Typography>
                              </Box>
                            </>
                          )}
                          <ChevronRightIcon sx={{ fontSize: 18, color: "#c7ccd4" }} />
                        </Box>
                      </Box>
                    ))}
                    {vwSummaryTotalPages > 1 && pageNav(vwSummaryPage, vwSummaryTotalPages, setVwSummaryPage)}
                  </>
                )
              ) : (
                <>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "6px", p: "12px 20px", borderBottom: "1px solid #f1f4f9", flexWrap: "wrap" }}>
                    {VENDOR_WISE_FILTERS.map((status) => {
                      const active = vendorWiseStatusFilter === status;
                      return (
                        <Box
                          key={status}
                          onClick={() => setVendorWiseStatusFilter(status)}
                          sx={{ px: 1.5, py: "6px", borderRadius: "999px", fontSize: "12.5px", fontWeight: 700, cursor: "pointer", color: active ? "#7c3aed" : "#667085", background: active ? "rgba(124,58,237,0.10)" : "#f2f4f7" }}
                        >
                          {status}
                        </Box>
                      );
                    })}
                  </Box>

                  {nSelected > 0 && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, p: "10px 20px", background: "#fef3f2", borderBottom: "1px solid #fecdca" }}>
                      <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: "#b42318", flex: 1 }}>{nSelected} selected</Typography>
                      <Button onClick={() => setVendorWiseRejectDialogOpen(true)} sx={{ textTransform: "none", fontWeight: 700, fontSize: "12.5px", color: "#dc2626", background: "#fff", border: "1px solid #fecdca", borderRadius: "9px", px: 1.5, py: "4px" }}>Reject Selected</Button>
                      <Button onClick={() => setVendorWiseRemoveConfirmOpen(true)} sx={{ textTransform: "none", fontWeight: 700, fontSize: "12.5px", color: "#fff", background: "#dc2626", borderRadius: "9px", px: 1.5, py: "4px" }}>Remove Selected</Button>
                    </Box>
                  )}

                  <Box sx={{ display: "flex", alignItems: "center", padding: "9px 20px", background: "#fafbfc", borderBottom: "1px solid #f1f4f9" }}>
                    <Box sx={{ width: "32px" }} />
                    <Typography sx={{ flex: 2, fontSize: "10.5px", fontWeight: 700, color: "#98a2b3", textTransform: "uppercase", letterSpacing: "0.06em" }}>Project</Typography>
                    <Typography sx={{ flex: 1, fontSize: "10.5px", fontWeight: 700, color: "#98a2b3", textTransform: "uppercase", letterSpacing: "0.06em", textAlign: "right" }}>Value</Typography>
                    <Typography sx={{ flex: 1, fontSize: "10.5px", fontWeight: 700, color: "#98a2b3", textTransform: "uppercase", letterSpacing: "0.06em", textAlign: "right" }}>Status</Typography>
                    <Box sx={{ width: "100px" }} />
                  </Box>

                  {vwVendorLoading ? (
                    <Box sx={{ p: "40px 20px", textAlign: "center", color: "#98a2b3", fontSize: "13px" }}>Loading payments…</Box>
                  ) : vendorWiseFilteredPayments.length === 0 ? (
                    <Box sx={{ p: "40px 20px", textAlign: "center", color: "#98a2b3", fontSize: "13px" }}>
                      No {vendorWiseStatusFilter === "All" ? "" : vendorWiseStatusFilter.toLowerCase()} payments for this vendor
                    </Box>
                  ) : (
                    <>
                      {vendorWisePagedPayments.map((request, i) => {
                        const status = getDisplayStatus(request);
                        const checkable = canActVendorWise && status === "Approved";
                        const checked = vendorWiseSelectedIds.has(request.id);
                        return (
                          <Box key={request.id} sx={{ display: "flex", alignItems: "center", padding: "12px 20px", borderBottom: i < vendorWisePagedPayments.length - 1 ? "1px solid #f1f4f9" : "none" }}>
                            <Box sx={{ width: "32px" }}>
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={!checkable}
                                onChange={() => checkable && toggleVendorWiseSelect(request.id)}
                                style={{ width: 16, height: 16, opacity: checkable ? 1 : 0.35, cursor: checkable ? "pointer" : "not-allowed" }}
                              />
                            </Box>
                            <Typography sx={{ flex: 2, fontSize: "13.5px", fontWeight: 700, color: "#101828" }}>{request.clientName}</Typography>
                            <Typography sx={{ flex: 1, fontSize: "13.5px", fontWeight: 800, color: "#101828", textAlign: "right" }}>{rupee(request.amount)}</Typography>
                            <Box sx={{ flex: 1, textAlign: "right" }}>
                              <Box
                                onClick={() => canActOnStatus(status) && handleStatusCellClick(request)}
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.75,
                                  px: 1.25,
                                  py: "5px",
                                  borderRadius: "999px",
                                  background: `${getApprovalColor(status)}2b`,
                                  whiteSpace: "nowrap",
                                  cursor: canActOnStatus(status) ? "pointer" : "default",
                                }}
                              >
                                <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: getApprovalColor(status), flexShrink: 0 }} />
                                <Typography sx={{ fontSize: "11.5px", fontWeight: 700, color: "#344054", fontFamily: "Roboto, Arial, sans-serif" }}>
                                  {status}
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={{ width: "100px", textAlign: "right", fontSize: "11px", color: "#98a2b3", fontWeight: 600 }}>
                              {request.date ? new Date(request.date).toLocaleDateString("en-IN") : "—"}
                            </Box>
                          </Box>
                        );
                      })}
                      {vwDrillTotalPages > 1 && pageNav(vwDrillPageClamped, vwDrillTotalPages, setVwDrillPage)}
                    </>
                  )}
                </>
              )}
            </Box>
          );
        }

        // Project-Wise: the same cross-vendor browsing model as Vendor-Wise
        // above, grouped by Client ("Project") instead — drilling into one
        // shows that project's vendor-wise payments, with the same Add
        // Payments / bulk Reject-Remove affordances, plus its own search bar
        // (vendor name/purpose) next to the status chips.
        if (deskStatusTab === "ProjectWise") {
          const rupee = (n) => `₹ ${Math.round(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
          const PROJECT_WISE_FILTERS = ["Approved", "Pending", "Correction", "Paid", "Rejected", "All"];
          const nSelected = projectWiseSelectedIds.size;
          const totalsChip = (label, value, bg, fg) => (
            <Box sx={{ display: "flex", alignItems: "baseline", gap: "5px", px: 1.25, py: "5px", borderRadius: "999px", background: bg }}>
              <Typography sx={{ fontSize: "10.5px", fontWeight: 800, color: fg, textTransform: "uppercase", letterSpacing: "0.03em" }}>{label}</Typography>
              <Typography sx={{ fontSize: "12px", fontWeight: 800, color: "#101828", fontFamily: "Roboto, Arial, sans-serif" }}>{rupee(value)}</Typography>
            </Box>
          );
          const pageNav = (page, totalPages, setPage) => (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, padding: "14px 20px", borderTop: "1px solid #eef2f7" }}>
              <Typography sx={{ fontSize: "12.5px", color: "#98a2b3", fontWeight: 600 }}>Page {page} of {totalPages}</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} sx={{ textTransform: "none", fontWeight: 700, fontSize: "12.5px", color: "#344054", background: "#f2f4f7", borderRadius: "9px", px: 1.5, py: "6px", minWidth: 0, "&.Mui-disabled": { color: "#c7ccd4", background: "#f7f8fa" } }}>Previous</Button>
                <Button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page >= totalPages} sx={{ textTransform: "none", fontWeight: 700, fontSize: "12.5px", color: "#344054", background: "#f2f4f7", borderRadius: "9px", px: 1.5, py: "6px", minWidth: 0, "&.Mui-disabled": { color: "#c7ccd4", background: "#f7f8fa" } }}>Next</Button>
              </Box>
            </Box>
          );

          return (
            <Box sx={{ display: { xs: "none", md: "block" }, background: "#ffffff", border: "1px solid #e9eef5", borderRadius: "18px", boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 8px 24px rgba(13,43,92,0.05)", overflow: "hidden" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: "16px 20px", borderBottom: "1px solid #eef2f7", flexWrap: "wrap" }}>
                {projectWiseSelectedProject && (
                  <IconButton onClick={closeProjectWiseProject} size="small" sx={{ background: "#f2f4f7" }}><ArrowBackIcon sx={{ fontSize: 18 }} /></IconButton>
                )}
                <Typography sx={{ fontSize: "15.5px", fontWeight: 800, color: "#101828", letterSpacing: "-0.01em", fontFamily: "Roboto, Arial, sans-serif" }}>
                  {projectWiseSelectedProject?.name || "Project-Wise"}
                </Typography>
                <Box sx={{ px: 1.25, py: "3px", borderRadius: "999px", background: "rgba(13,148,136,0.10)", color: "#0d9488", fontSize: "11.5px", fontWeight: 800, fontFamily: "Roboto, Arial, sans-serif" }}>
                  {projectWiseSelectedProject ? projectWiseFilteredPayments.length : pwSummaryTotal}
                </Box>
                {projectWiseSelectedProject
                  ? totalsChip(projectWiseStatusFilter, projectWiseFilteredTotal, "rgba(13,148,136,0.08)", "#0d9488")
                  : (
                    <>
                      {totalsChip("Total Approved", pwGrandTotals.approvedValue, "#ecfdf3", "#067647")}
                      {totalsChip("Total Pending", pwGrandTotals.pendingValue, "#fffaeb", "#b54708")}
                    </>
                  )}
                <Box sx={{ flex: 1 }} />
                {!projectWiseSelectedProject && (
                  <Search sx={{ backgroundColor: "#ffffff", border: "1px solid #e4e7ec", borderRadius: "12px", marginRight: 0, "&:hover": { backgroundColor: "#ffffff" }, "&:focus-within": { borderColor: "#39aafc", boxShadow: "0 0 0 4px rgba(57,170,252,0.12)" } }}>
                    <SearchIconWrapper><SearchIcon sx={{ fontSize: 19, color: "#98a2b3" }} /></SearchIconWrapper>
                    <StyledInputBase placeholder="Search project…" inputProps={{ "aria-label": "search" }} value={searchQuery} onChange={handleSearchChange} />
                  </Search>
                )}
                {projectWiseSelectedProject && canRequestPayment && (
                  <Button
                    onClick={() => handleAddPaymentForProject(projectWiseSelectedProject.id, projectWiseSelectedProject.name)}
                    startIcon={<AddIcon sx={{ fontSize: "18px !important" }} />}
                    sx={{ textTransform: "none", fontWeight: 700, fontSize: "12.5px", color: "#fff", background: "linear-gradient(135deg,#39aafc,#1f8fe8)", borderRadius: "10px", px: 1.75, py: "6px", boxShadow: "0 6px 16px rgba(31,143,232,0.3)", mr: 0.5 }}
                  >
                    Add Payments
                  </Button>
                )}
              </Box>

              {!projectWiseSelectedProject ? (
                pwSummaryLoading ? (
                  <Box sx={{ p: "40px 20px", textAlign: "center", color: "#98a2b3", fontSize: "13px" }}>Loading projects…</Box>
                ) : pwSummary.length === 0 ? (
                  <Box sx={{ p: "40px 20px", textAlign: "center", color: "#98a2b3", fontSize: "13px" }}>No projects with payment activity yet</Box>
                ) : (
                  <>
                    {pwSummary.map((v, i) => (
                      <Box
                        key={v.client_id}
                        onClick={() => openProjectWiseProject(v.client_id, v.client_name)}
                        sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: "13px 20px", borderBottom: i < pwSummary.length - 1 ? "1px solid #f1f4f9" : "none", cursor: "pointer", "&:hover": { background: "#fafbfc" } }}
                      >
                        <Typography sx={{ fontSize: "13.5px", fontWeight: 700, color: "#101828" }}>{v.client_name}</Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2.5 }}>
                          <Box sx={{ textAlign: "right", minWidth: "90px" }}>
                            <Typography sx={{ fontSize: "9.5px", color: "#059669", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.03em" }}>Approved</Typography>
                            <Typography sx={{ fontSize: "12.5px", fontWeight: 800, color: "#101828" }}>{rupee(v.approvedValue)}</Typography>
                          </Box>
                          <Box sx={{ textAlign: "right", minWidth: "90px" }}>
                            <Typography sx={{ fontSize: "9.5px", color: "#b54708", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.03em" }}>Pending</Typography>
                            <Typography sx={{ fontSize: "12.5px", fontWeight: 800, color: "#101828" }}>{rupee(v.pendingValue)}</Typography>
                          </Box>
                          <ChevronRightIcon sx={{ fontSize: 18, color: "#c7ccd4" }} />
                        </Box>
                      </Box>
                    ))}
                    {pwSummaryTotalPages > 1 && pageNav(pwSummaryPage, pwSummaryTotalPages, setPwSummaryPage)}
                  </>
                )
              ) : (
                <>
                  <Box sx={{ display: "flex", alignItems: "center", gap: "6px", p: "12px 20px", borderBottom: "1px solid #f1f4f9", flexWrap: "wrap" }}>
                    {PROJECT_WISE_FILTERS.map((status) => {
                      const active = projectWiseStatusFilter === status;
                      return (
                        <Box
                          key={status}
                          onClick={() => setProjectWiseStatusFilter(status)}
                          sx={{ px: 1.5, py: "6px", borderRadius: "999px", fontSize: "12.5px", fontWeight: 700, cursor: "pointer", color: active ? "#0d9488" : "#667085", background: active ? "rgba(13,148,136,0.10)" : "#f2f4f7" }}
                        >
                          {status}
                        </Box>
                      );
                    })}
                    <Box sx={{ flex: 1 }} />
                    <Search sx={{ backgroundColor: "#fff", border: "1px solid #e4e7ec", borderRadius: "12px", marginRight: 0, minWidth: "220px", "&:hover": { backgroundColor: "#ffffff" }, "&:focus-within": { borderColor: "#39aafc", boxShadow: "0 0 0 4px rgba(57,170,252,0.12)" } }}>
                      <SearchIconWrapper><SearchIcon sx={{ fontSize: 19, color: "#98a2b3" }} /></SearchIconWrapper>
                      <StyledInputBase placeholder="Search vendor or purpose…" inputProps={{ "aria-label": "search" }} value={searchQuery} onChange={handleSearchChange} />
                    </Search>
                  </Box>

                  {nSelected > 0 && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, p: "10px 20px", background: "#fef3f2", borderBottom: "1px solid #fecdca" }}>
                      <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: "#b42318", flex: 1 }}>{nSelected} selected</Typography>
                      <Button onClick={() => setProjectWiseRejectDialogOpen(true)} sx={{ textTransform: "none", fontWeight: 700, fontSize: "12.5px", color: "#dc2626", background: "#fff", border: "1px solid #fecdca", borderRadius: "9px", px: 1.5, py: "4px" }}>Reject Selected</Button>
                      <Button onClick={() => setProjectWiseRemoveConfirmOpen(true)} sx={{ textTransform: "none", fontWeight: 700, fontSize: "12.5px", color: "#fff", background: "#dc2626", borderRadius: "9px", px: 1.5, py: "4px" }}>Remove Selected</Button>
                    </Box>
                  )}

                  <Box sx={{ display: "flex", alignItems: "center", padding: "9px 20px", background: "#fafbfc", borderBottom: "1px solid #f1f4f9" }}>
                    <Box sx={{ width: "32px" }} />
                    <Typography sx={{ flex: 2, fontSize: "10.5px", fontWeight: 700, color: "#98a2b3", textTransform: "uppercase", letterSpacing: "0.06em" }}>Vendor</Typography>
                    <Typography sx={{ flex: 1, fontSize: "10.5px", fontWeight: 700, color: "#98a2b3", textTransform: "uppercase", letterSpacing: "0.06em", textAlign: "right" }}>Value</Typography>
                    <Typography sx={{ flex: 1, fontSize: "10.5px", fontWeight: 700, color: "#98a2b3", textTransform: "uppercase", letterSpacing: "0.06em", textAlign: "right" }}>Status</Typography>
                    <Box sx={{ width: "100px" }} />
                  </Box>

                  {pwProjectLoading ? (
                    <Box sx={{ p: "40px 20px", textAlign: "center", color: "#98a2b3", fontSize: "13px" }}>Loading payments…</Box>
                  ) : projectWiseFilteredPayments.length === 0 ? (
                    <Box sx={{ p: "40px 20px", textAlign: "center", color: "#98a2b3", fontSize: "13px" }}>
                      No {projectWiseStatusFilter === "All" ? "" : projectWiseStatusFilter.toLowerCase()} payments for this project
                    </Box>
                  ) : (
                    <>
                      {projectWisePagedPayments.map((request, i) => {
                        const status = getDisplayStatus(request);
                        const checkable = canActProjectWise && status === "Approved";
                        const checked = projectWiseSelectedIds.has(request.id);
                        return (
                          <Box key={request.id} sx={{ display: "flex", alignItems: "center", padding: "12px 20px", borderBottom: i < projectWisePagedPayments.length - 1 ? "1px solid #f1f4f9" : "none" }}>
                            <Box sx={{ width: "32px" }}>
                              <input
                                type="checkbox"
                                checked={checked}
                                disabled={!checkable}
                                onChange={() => checkable && toggleProjectWiseSelect(request.id)}
                                style={{ width: 16, height: 16, opacity: checkable ? 1 : 0.35, cursor: checkable ? "pointer" : "not-allowed" }}
                              />
                            </Box>
                            <Typography sx={{ flex: 2, fontSize: "13.5px", fontWeight: 700, color: "#101828" }}>{request.vendorName}</Typography>
                            <Typography sx={{ flex: 1, fontSize: "13.5px", fontWeight: 800, color: "#101828", textAlign: "right" }}>{rupee(request.amount)}</Typography>
                            <Box sx={{ flex: 1, textAlign: "right" }}>
                              <Box
                                onClick={() => canActOnStatus(status) && handleStatusCellClick(request)}
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 0.75,
                                  px: 1.25,
                                  py: "5px",
                                  borderRadius: "999px",
                                  background: `${getApprovalColor(status)}2b`,
                                  whiteSpace: "nowrap",
                                  cursor: canActOnStatus(status) ? "pointer" : "default",
                                }}
                              >
                                <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: getApprovalColor(status), flexShrink: 0 }} />
                                <Typography sx={{ fontSize: "11.5px", fontWeight: 700, color: "#344054", fontFamily: "Roboto, Arial, sans-serif" }}>
                                  {status}
                                </Typography>
                              </Box>
                            </Box>
                            <Box sx={{ width: "100px", textAlign: "right", fontSize: "11px", color: "#98a2b3", fontWeight: 600 }}>
                              {request.date ? new Date(request.date).toLocaleDateString("en-IN") : "—"}
                            </Box>
                          </Box>
                        );
                      })}
                      {pwDrillTotalPages > 1 && pageNav(pwDrillPageClamped, pwDrillTotalPages, setPwDrillPage)}
                    </>
                  )}
                </>
              )}
            </Box>
          );
        }

        const deskRows = paymentRequests
          .filter(isRowTabVisible)
          .filter((r) => {
            if (deskStatusTab === "all") return true;
            if (deskStatusTab === "Correction") return r.approval === "Pending" && !!r.pendingReason;
            if (deskStatusTab === "Pending") return r.approval === "Pending" && !r.pendingReason;
            return r.approval === deskStatusTab;
          })
          .filter(
            (r) =>
              !q ||
              (r.vendorName || "").toLowerCase().includes(q) ||
              (r.clientName || "").toLowerCase().includes(q) ||
              (r.paymentPurpose || "").toLowerCase().includes(q)
          )
          .filter((r) => withinLedgerFilter(r.date));
        const deskTotalPages = Math.max(1, Math.ceil(deskRows.length / DESK_PAGE_SIZE));
        const deskPageClamped = Math.min(deskPage, deskTotalPages);
        const deskPageStart = (deskPageClamped - 1) * DESK_PAGE_SIZE;
        const pagedDeskRows = deskRows.slice(deskPageStart, deskPageStart + DESK_PAGE_SIZE);
        const emptyCopy =
          deskStatusTab === "all"
            ? "No payment requests yet"
            : `No ${deskStatusTab.toLowerCase()} requests`;
        return (
          <Box
            sx={{
              display: { xs: "none", md: "block" },
              background: "#ffffff",
              border: "1px solid #e9eef5",
              borderRadius: "18px",
              boxShadow: "0 1px 2px rgba(16,24,40,0.04), 0 8px 24px rgba(13,43,92,0.05)",
              overflow: "hidden",
            }}
          >
            {/* Header zone: identity left, tools right */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, p: "16px 20px", borderBottom: "1px solid #eef2f7" }}>
              <Typography sx={{ fontSize: "15.5px", fontWeight: 800, color: "#101828", letterSpacing: "-0.01em", fontFamily: "Roboto, Arial, sans-serif" }}>
                Payment Requests
              </Typography>
              <Box sx={{ px: 1.25, py: "3px", borderRadius: "999px", background: "rgba(57,170,252,0.10)", color: "#1565C0", fontSize: "11.5px", fontWeight: 800, fontFamily: "Roboto, Arial, sans-serif" }}>
                {deskRows.length}
              </Box>
              <Box sx={{ flex: 1 }} />
              <Search
                sx={{
                  backgroundColor: "#ffffff",
                  border: "1px solid #e4e7ec",
                  borderRadius: "12px",
                  marginRight: 0,
                  transition: "border-color 0.18s ease, box-shadow 0.18s ease",
                  "&:hover": { backgroundColor: "#ffffff" },
                  "&:focus-within": { borderColor: "#39aafc", boxShadow: "0 0 0 4px rgba(57,170,252,0.12)" },
                }}
              >
                <SearchIconWrapper>
                  <SearchIcon sx={{ fontSize: 19, color: "#98a2b3" }} />
                </SearchIconWrapper>
                <StyledInputBase
                  placeholder="Search vendor, client or purpose…"
                  inputProps={{ "aria-label": "search" }}
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
              </Search>
              <Tooltip title={ledgerFilterActive ? `Filter: ${ledgerFilterLabel} — click to change` : "Filter by financial year, month or date range"}>
                <Box
                  onClick={openLedgerFilter}
                  sx={{
                    display: "inline-flex", alignItems: "center", gap: "6px", height: "38px", px: 1.5,
                    borderRadius: "12px", cursor: "pointer", whiteSpace: "nowrap",
                    border: ledgerFilterActive ? "1px solid transparent" : "1px solid #e4e7ec",
                    color: ledgerFilterActive ? "#ffffff" : "#5f6f86",
                    background: ledgerFilterActive ? "linear-gradient(135deg, #4db2fc, #1565C0)" : "#ffffff",
                    boxShadow: ledgerFilterActive ? "0 6px 16px rgba(21,101,192,0.32)" : "none",
                    "&:hover": ledgerFilterActive
                      ? { background: "linear-gradient(135deg, #39aafc, #0d47a1)" }
                      : { borderColor: "#39aafc", color: "#1565C0", background: "rgba(57,170,252,0.08)" },
                  }}
                >
                  <FilterAltIcon sx={{ fontSize: 17 }} />
                  <Typography sx={{ fontSize: "12.5px", fontWeight: 700, fontFamily: "Roboto, Arial, sans-serif" }}>{ledgerFilterLabel}</Typography>
                </Box>
              </Tooltip>
              <Button
                variant="contained"
                onClick={handlePaymentRequest}
                startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                sx={{
                  background: "linear-gradient(135deg, #4db2fc, #1565C0)",
                  color: "white",
                  textTransform: "none",
                  fontSize: "13px",
                  fontWeight: 700,
                  minWidth: "158px",
                  height: "38px",
                  borderRadius: "12px",
                  boxShadow: "0 8px 20px rgba(21, 101, 192, 0.28)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #39aafc, #0d47a1)",
                    boxShadow: "0 10px 24px rgba(21, 101, 192, 0.38)",
                  },
                }}
              >
                Payment Request
              </Button>
            </Box>

            <Popover
              open={Boolean(ledgerFilterAnchor)}
              anchorEl={ledgerFilterAnchor}
              onClose={closeLedgerFilter}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              PaperProps={{ sx: { borderRadius: "16px", border: "1px solid #e9eef5", boxShadow: "0 12px 32px rgba(16,24,40,0.14)", mt: 0.75, p: "18px", width: "320px" } }}
            >
              <Typography sx={{ fontSize: "12px", fontWeight: 800, color: "#101828", textTransform: "uppercase", letterSpacing: "0.04em", mb: 1 }}>Financial Year</Typography>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mb: 2 }}>
                {["All Years", ...LEDGER_FY_OPTIONS, "Custom Date"].map((opt) => {
                  const value = opt === "All Years" ? "" : opt;
                  const active = ledgerDraftFY === value;
                  return (
                    <Box
                      key={opt}
                      onClick={() => { setLedgerDraftFY(value); setLedgerDraftMonth(""); }}
                      sx={{ px: 1.5, py: "6px", borderRadius: "999px", fontSize: "12.5px", fontWeight: 700, cursor: "pointer", color: active ? "#1565C0" : "#667085", background: active ? "rgba(57,170,252,0.12)" : "#f2f4f7" }}
                    >
                      {opt === "Custom Date" ? "Custom Date…" : opt === "All Years" ? opt : `FY ${opt}`}
                    </Box>
                  );
                })}
              </Box>

              {ledgerDraftFY && ledgerDraftFY !== "Custom Date" && (
                <>
                  <Typography sx={{ fontSize: "12px", fontWeight: 800, color: "#101828", textTransform: "uppercase", letterSpacing: "0.04em", mb: 1 }}>Month (optional)</Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px", mb: 2, maxHeight: "140px", overflowY: "auto" }}>
                    <Box
                      onClick={() => setLedgerDraftMonth("")}
                      sx={{ px: 1.5, py: "6px", borderRadius: "999px", fontSize: "12.5px", fontWeight: 700, cursor: "pointer", color: ledgerDraftMonth === "" ? "#1565C0" : "#667085", background: ledgerDraftMonth === "" ? "rgba(57,170,252,0.12)" : "#f2f4f7" }}
                    >
                      Whole Year
                    </Box>
                    {monthsOfFY(ledgerDraftFY).map((m) => {
                      const active = String(ledgerDraftMonth) === String(m.rel);
                      return (
                        <Box
                          key={m.rel}
                          onClick={() => setLedgerDraftMonth(m.rel)}
                          sx={{ px: 1.5, py: "6px", borderRadius: "999px", fontSize: "12.5px", fontWeight: 700, cursor: "pointer", color: active ? "#1565C0" : "#667085", background: active ? "rgba(57,170,252,0.12)" : "#f2f4f7" }}
                        >
                          {m.label}
                        </Box>
                      );
                    })}
                  </Box>
                </>
              )}

              {ledgerDraftFY === "Custom Date" && (
                <>
                  <Typography sx={{ fontSize: "12px", fontWeight: 800, color: "#101828", textTransform: "uppercase", letterSpacing: "0.04em", mb: 1 }}>Date Range</Typography>
                  <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                    <TextField
                      label="From" type="date" size="small" fullWidth
                      value={ledgerDraftFrom}
                      onChange={(e) => setLedgerDraftFrom(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: "12.5px" } }}
                    />
                    <TextField
                      label="To" type="date" size="small" fullWidth
                      value={ledgerDraftTo}
                      onChange={(e) => setLedgerDraftTo(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px", fontSize: "12.5px" } }}
                    />
                  </Box>
                </>
              )}

              <Box sx={{ display: "flex", gap: 1 }}>
                <Button onClick={clearLedgerFilter} sx={{ flex: 1, textTransform: "none", fontWeight: 700, fontSize: "12.5px", color: "#667085", background: "#f2f4f7", borderRadius: "10px", py: "8px" }}>Clear</Button>
                <Button onClick={applyLedgerFilter} sx={{ flex: 1, textTransform: "none", fontWeight: 700, fontSize: "12.5px", color: "#fff", background: "linear-gradient(135deg,#39aafc,#1565C0)", borderRadius: "10px", py: "8px" }}>Apply</Button>
              </Box>
            </Popover>

          <TableContainer>
            <Table
              sx={{
                minWidth: 650,
                "& tbody tr": { transition: "background-color 0.15s ease" },
                "& tbody tr:hover": { backgroundColor: "#f8fbff" },
              }}
              aria-label="payment requests table"
            >
              <TableHead>
                <TableRow>
                  <TableCell className="table-head-cell" sx={{ width: "9%", paddingLeft: "20px !important" }}>
                    Date
                  </TableCell>
                  <TableCell className="table-head-cell" sx={{ width: "24%" }}>
                    Vendor · Purpose
                  </TableCell>
                  <TableCell className="table-head-cell" sx={{ width: "15%" }}>
                    Vendor Category
                  </TableCell>
                  <TableCell className="table-head-cell" sx={{ width: "17%" }}>
                    Client
                  </TableCell>
                  <TableCell className="table-head-cell" sx={{ width: "12%", textAlign: "right" }}>
                    Amount
                  </TableCell>
                  <TableCell className="table-head-cell" sx={{ width: "13%" }}>
                    Status
                  </TableCell>
                  <TableCell className="table-head-cell" sx={{ width: "10%", textAlign: "center", paddingRight: "20px !important" }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {deskRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: "center", padding: "48px", fontSize: "13px", color: "#98a2b3", borderBottom: "none" }}>
                      {emptyCopy}
                    </TableCell>
                  </TableRow>
                ) : (
                  pagedDeskRows.map((request, index) => (
                    <TableRow key={request.id}>
                      <TableCell className="table-body-cell" sx={{ whiteSpace: "nowrap", color: "#667085", paddingLeft: "20px !important", fontVariantNumeric: "tabular-nums" }}>
                        {request.date ? new Date(request.date).toLocaleDateString('en-IN') : '-'}
                      </TableCell>
                      {/* Vendor — identity + purpose stacked (primary + meta).
                          Clicking it jumps to Vendor-Wise, drilled straight
                          into this vendor — same cross-project view the tab
                          itself opens, just pre-navigated. */}
                      <TableCell
                        className="table-body-cell"
                        sx={{ paddingTop: "11px", paddingBottom: "11px", cursor: hasTabVendorWise ? "pointer" : "default" }}
                        onClick={() => { if (hasTabVendorWise) { setDeskStatusTab("VendorWise"); openVendorWiseVendor(request.vendorName); } }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0, "&:hover .arcdx-vw-link": hasTabVendorWise ? { textDecoration: "underline" } : {} }}>
                          <Box
                            sx={{
                              width: 38,
                              height: 38,
                              borderRadius: "12px",
                              flexShrink: 0,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "14.5px",
                              fontWeight: 800,
                              color: "#1565C0",
                              background: "rgba(57, 170, 252, 0.12)",
                            }}
                          >
                            {(request.vendorName || "V").charAt(0).toUpperCase()}
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography noWrap className="arcdx-vw-link" sx={{ fontSize: "13.5px", fontWeight: 700, color: "#101828", fontFamily: "Roboto, Arial, sans-serif", lineHeight: 1.3 }}>
                              {request.vendorName}
                            </Typography>
                            <Typography noWrap sx={{ fontSize: "11.5px", color: "#9aa7b8", fontFamily: "Roboto, Arial, sans-serif", mt: "2px" }}>
                              {request.paymentPurpose}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      {/* Vendor Category — looked up company-wide by vendor
                          name, same source as the Payment Request vendor
                          picker's budget-pool resolution; sub-category (if
                          the vendor has one) shown nested beneath via the
                          same parent→child chip pattern used elsewhere. */}
                      <TableCell className="table-body-cell">
                        {(() => {
                          const meta = findVendorMeta(request.vendorName);
                          if (!meta || !meta.vendor_category) {
                            return <span style={{ color: "#98a2b3", fontSize: "12px" }}>—</span>;
                          }
                          return (
                            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: "4px" }}>
                              <span className="arcdx-v-status" style={{ background: "#e3f2ff", color: "#1565C0" }}>
                                {meta.vendor_category}
                              </span>
                              {meta.vendor_sub_category && (
                                <Box sx={{ display: "flex", alignItems: "center", marginLeft: "6px" }}>
                                  <Box sx={{ width: "10px", height: "10px", borderLeft: "1.5px solid #d0d5dd", borderBottom: "1.5px solid #d0d5dd", borderBottomLeftRadius: "6px", marginRight: "5px", alignSelf: "flex-start", marginTop: "-6px" }} />
                                  <span className="arcdx-v-status" style={{ background: "#f4ebff", color: "#6941C6" }}>
                                    {meta.vendor_sub_category}
                                  </span>
                                </Box>
                              )}
                            </Box>
                          );
                        })()}
                      </TableCell>
                      {/* Client — clicking it jumps to Project-Wise, drilled
                          straight into this project, same as the vendor cell
                          does for Vendor-Wise. */}
                      <TableCell
                        className="table-body-cell"
                        sx={{ fontWeight: 600, color: "#344054", cursor: hasTabProjectWise ? "pointer" : "default", "&:hover": hasTabProjectWise ? { textDecoration: "underline" } : {} }}
                        onClick={() => { if (hasTabProjectWise) { setDeskStatusTab("ProjectWise"); openProjectWiseProject(request.clientId, request.clientName); } }}
                      >
                        {request.clientName}
                      </TableCell>
                      <TableCell className="table-body-cell" sx={{ textAlign: "right", fontWeight: 700, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", fontSize: "13.5px" }}>
                        {formatCurrency(request.amount)}
                      </TableCell>
                      <TableCell
                        className="table-body-cell"
                        sx={{ cursor: canActOnStatus(getDisplayStatus(request)) ? "pointer" : "default" }}
                        onClick={() => canActOnStatus(getDisplayStatus(request)) && handleStatusCellClick(request)}
                      >
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.75,
                            px: 1.25,
                            py: "5px",
                            borderRadius: "999px",
                            background: `${getApprovalColor(getDisplayStatus(request))}2b`,
                            whiteSpace: "nowrap",
                            transition: "box-shadow 0.15s ease",
                            "&:hover": canActOnStatus(getDisplayStatus(request)) ? { boxShadow: `0 0 0 2px ${getApprovalColor(getDisplayStatus(request))}55` } : {},
                          }}
                        >
                          <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: getApprovalColor(getDisplayStatus(request)), flexShrink: 0 }} />
                          <Typography sx={{ fontSize: "11.5px", fontWeight: 700, color: "#344054", fontFamily: "Roboto, Arial, sans-serif" }}>
                            {getDisplayStatus(request)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell className="table-body-cell" sx={{ textAlign: "center" }}>
                        {String(request.userId) === String(user?._id) && (
                          <>
                            <IconButton
                              size="small"
                              onClick={() => handleEditPaymentRequest(request)}
                              sx={{ color: "#98a2b3", "&:hover": { color: "#1f6fd6" } }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDeletePaymentRequest(request)}
                              sx={{ color: "#d0d5dd", "&:hover": { color: "#d92d20" } }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {deskRows.length > 0 && (
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, padding: "14px 20px", borderTop: "1px solid #eef2f7" }}>
              <Typography sx={{ fontSize: "12.5px", color: "#98a2b3", fontWeight: 600 }}>
                Showing {deskPageStart + 1}–{Math.min(deskPageStart + DESK_PAGE_SIZE, deskRows.length)} of {deskRows.length}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Button
                  onClick={() => setDeskPage((p) => Math.max(1, p - 1))}
                  disabled={deskPageClamped <= 1}
                  sx={{ textTransform: "none", fontWeight: 700, fontSize: "12.5px", color: "#344054", background: "#f2f4f7", borderRadius: "9px", px: 1.5, py: "6px", minWidth: 0, "&.Mui-disabled": { color: "#c7ccd4", background: "#f7f8fa" } }}
                >
                  Previous
                </Button>
                <Typography sx={{ fontSize: "12.5px", fontWeight: 700, color: "#344054", px: 1 }}>
                  Page {deskPageClamped} of {deskTotalPages}
                </Typography>
                <Button
                  onClick={() => setDeskPage((p) => Math.min(deskTotalPages, p + 1))}
                  disabled={deskPageClamped >= deskTotalPages}
                  sx={{ textTransform: "none", fontWeight: 700, fontSize: "12.5px", color: "#344054", background: "#f2f4f7", borderRadius: "9px", px: 1.5, py: "6px", minWidth: 0, "&.Mui-disabled": { color: "#c7ccd4", background: "#f7f8fa" } }}
                >
                  Next
                </Button>
              </Box>
            </Box>
          )}
          </Box>
        );
      })()}

      {/* Payment Request Dialog (desktop; mobile uses the bottom sheet) */}
      <Dialog
        open={!isMobile && openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "24px", overflow: "hidden" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: "17px",
            fontWeight: 800,
            fontFamily: "Roboto, Arial, sans-serif",
            background: "linear-gradient(155deg, #4db2fc 0%, #1565C0 100%)",
            color: "#fff",
            padding: "18px 24px"
          }}
        >
          {isEditMode ? "Edit Payment Request" : "Payment Request"}
          <IconButton onClick={handleCloseDialog} size="small" sx={{ color: "#fff", background: "rgba(255,255,255,0.15)", "&:hover": { background: "rgba(255,255,255,0.25)" } }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: "24px !important", paddingTop: "24px !important" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
            {formError && (
              <Box sx={{ background: "#fef3f2", border: "1px solid #fecdca", borderRadius: "10px", px: "14px", py: "10px", fontSize: "12.5px", fontWeight: 600, color: "#b42318" }}>
                {formError}
              </Box>
            )}
            {/* Date */}
            <TextField
              label="Date"
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              sx={{
                "& .MuiInputBase-root": {
                  fontSize: "13px",
                  fontFamily: "Roboto, Arial, sans-serif",
                  height: "48px",
                  borderRadius: "12px",
                  backgroundColor: "#f9fafb",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "13px",
                  fontFamily: "Roboto, Arial, sans-serif"
                },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e4e7ec" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#39aafc" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#39aafc", borderWidth: "1.5px" },
              }}
            />

            {/* Select Client — server-searched, filterOptions disabled since
                the options array IS already the server's matching result */}
            <Autocomplete
              options={clientOptions}
              filterOptions={(x) => x}
              getOptionLabel={(option) => option.name}
              getOptionKey={(option) => option.id}
              value={selectedClient}
              loading={clientOptionsLoading}
              disabled={clientLockedForRequest}
              onInputChange={(event, newInputValue, reason) => {
                if (reason === 'input') setClientSearchInput(newInputValue);
              }}
              onChange={(event, newValue) => {
                setSelectedClient(newValue);
                if (!vendorLockedForRequest) setSelectedVendor(null); // Clear vendor when client changes — unless it's locked (Add Payments from Vendor-Wise)
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Client"
                  variant="outlined"
                  sx={{
                    "& .MuiInputBase-root": {
                      fontSize: "13px",
                      fontFamily: "Roboto, Arial, sans-serif",
                      height: "48px",
                      borderRadius: "12px",
                      backgroundColor: "#f9fafb",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "13px",
                      fontFamily: "Roboto, Arial, sans-serif"
                    },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e4e7ec" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#39aafc" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#39aafc", borderWidth: "1.5px" },
                  }}
                />
              )}
            />

            {/* Select Vendor — getOptionKey is essential here (not just belt-
                and-suspenders): MUI's Autocomplete keys each rendered option
                by getOptionLabel(option) — i.e. the vendor's NAME — whenever
                getOptionKey isn't supplied. Several vendors are legitimately
                registered under the same name as both a Material vendor and
                a Labour vendor (e.g. "Hanger Space"), so without this, two
                DIFFERENT options collide onto the SAME React key. That key
                collision is what caused the reported "vendor shows up twice"
                / "search doesn't rank the right match to the top" bugs —
                React's reconciliation gets confused about which option is
                which once two siblings share a key, especially as the
                filtered list reshapes on every keystroke. */}
            <Autocomplete
              options={categorizedVendorOptions}
              filterOptions={vendorFilterOptions}
              getOptionLabel={(option) => option.name}
              getOptionKey={(option) => option.id}
              renderOption={renderVendorOption}
              value={selectedVendor}
              onChange={(event, newValue) => setSelectedVendor(newValue)}
              disabled={vendorLockedForRequest || !selectedClient || vendorLoading}
              loading={vendorLoading}
              noOptionsText={!selectedClient ? "Please select a client first" : "No active vendors found"}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Vendor"
                  variant="outlined"
                  sx={{
                    "& .MuiInputBase-root": {
                      fontSize: "13px",
                      fontFamily: "Roboto, Arial, sans-serif",
                      height: "48px",
                      borderRadius: "12px",
                      backgroundColor: "#f9fafb",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "13px",
                      fontFamily: "Roboto, Arial, sans-serif"
                    },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e4e7ec" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#39aafc" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#39aafc", borderWidth: "1.5px" },
                  }}
                />
              )}
            />

            {/* Budget Info - Shows when vendor is selected */}
            {selectedVendor && (() => {
              const pool = resolvePool(selectedVendor);
              const poolMemberNames = getPoolMemberNames(pool);
              const { pendingSum, approvedSum } = getPoolOutstanding(pool);
              const effectivePaid = getPoolEffectivePaid(pool);
              const budgetAmount = parseFloat(pool?.budget?.budget_amount) || 0;
              const trueAvailable = getPoolBudgetAvailable(selectedVendor);
              const poolLabel = pool ? (pool.subCategory ? `${pool.category} → ${pool.subCategory}` : pool.category) : "No Category Assigned";
              return (
              <Box
                sx={{
                  backgroundColor: "#f5f5f5",
                  borderRadius: "8px",
                  padding: "16px",
                  border: "1px solid #e0e0e0"
                }}
              >
                <Box sx={{ marginBottom: "10px" }}>
                  <span style={{ fontSize: "11px", fontFamily: "Roboto, Arial, sans-serif", color: "#98a2b3", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>
                    Budget Pool{pool?.subCategory ? " · Sub-Category" : " · Category"}
                  </span>
                  <div style={{ fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", fontWeight: 600, color: "#101828" }}>{poolLabel}</div>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
                  <span style={{ fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", color: "#666" }}>
                    Total Budget
                  </span>
                  <span style={{ fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", fontWeight: 500 }}>
                    ₹{budgetAmount.toLocaleString('en-IN')}
                  </span>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
                  <span style={{ fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", color: "#666" }}>
                    Amount Already Paid{poolMemberNames.length > 1 ? ` (${poolMemberNames.length} vendors)` : ""}
                  </span>
                  <span style={{ fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", fontWeight: 500, color: "#d32f2f" }}>
                    ₹{effectivePaid.toLocaleString('en-IN')}
                  </span>
                </Box>
                {pendingSum > 0 && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
                    <span style={{ fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", color: "#b54708" }}>
                      Pending Approval (other requests)
                    </span>
                    <span style={{ fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", fontWeight: 500, color: "#b54708" }}>
                      ₹{pendingSum.toLocaleString('en-IN')}
                    </span>
                  </Box>
                )}
                {approvedSum > 0 && (
                  <Box sx={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
                    <span style={{ fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", color: "#1f6fd6" }}>
                      Approved, Awaiting Payment
                    </span>
                    <span style={{ fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", fontWeight: 500, color: "#1f6fd6" }}>
                      ₹{approvedSum.toLocaleString('en-IN')}
                    </span>
                  </Box>
                )}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    borderTop: "1px solid #e0e0e0",
                    paddingTop: "10px"
                  }}
                >
                  <span style={{ fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", fontWeight: 500 }}>
                    Budget Available
                  </span>
                  <span style={{ fontSize: "14px", fontFamily: "Roboto, Arial, sans-serif", fontWeight: 600, color: trueAvailable < 0 ? "#d32f2f" : "#2e7d32" }}>
                    ₹{trueAvailable.toLocaleString('en-IN')}
                  </span>
                </Box>
              </Box>
              );
            })()}

            {/* Amount */}
            <TextField
              label="Amount"
              variant="outlined"
              type="number"
              value={amount}
              onChange={(e) => handleAmountChange(e.target.value)}
              sx={{
                "& .MuiInputBase-root": {
                  fontSize: "13px",
                  fontFamily: "Roboto, Arial, sans-serif",
                  height: "48px",
                  borderRadius: "12px",
                  backgroundColor: "#f9fafb",
                },
                "& .MuiInputLabel-root": {
                  fontSize: "13px",
                  fontFamily: "Roboto, Arial, sans-serif"
                },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e4e7ec" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#39aafc" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#39aafc", borderWidth: "1.5px" },
              }}
            />

            {/* Over Budget Warning and Reason */}
            {isOverBudget && (
              <>
                <Box
                  sx={{
                    backgroundColor: "#fff3e0",
                    border: "1px solid #ff9800",
                    borderRadius: "8px",
                    padding: "12px",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px"
                  }}
                >
                  <span style={{ fontSize: "20px" }}>⚠️</span>
                  <span style={{ fontSize: "13px", fontFamily: "Roboto, Arial, sans-serif", color: "#e65100" }}>
                    Amount exceeds available budget. This request will require approval from your Person Incharge
                    {personIncharge ? ` (${personIncharge.name})` : ""}.
                  </span>
                </Box>
                <TextField
                  label="Reason for Over Budget Request *"
                  value={overBudgetReason}
                  onChange={(e) => setOverBudgetReason(e.target.value)}
                  multiline
                  rows={3}
                  placeholder="Please provide a reason for this over-budget request"
                  sx={{
                    "& .MuiInputBase-root": {
                      fontSize: "13px",
                      fontFamily: "Roboto, Arial, sans-serif",
                      borderRadius: "12px",
                      backgroundColor: "#f9fafb",
                    },
                    "& .MuiInputLabel-root": {
                      fontSize: "13px",
                      fontFamily: "Roboto, Arial, sans-serif"
                    },
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e4e7ec" },
                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#39aafc" },
                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#39aafc", borderWidth: "1.5px" },
                  }}
                />
              </>
            )}

            {/* Upload Documents — below the Reason box (images, PDFs, Word/Excel docs).
                Mandatory for Material, optional for Labour. */}
            <Box>
              <Box sx={{ fontSize: "12px", color: "#666", fontFamily: "Roboto, Arial, sans-serif", marginBottom: "8px" }}>
                Upload Documents {selectedVendor?.type === 'Material' ? '*' : '(optional)'}
              </Box>
              <input
                ref={requestImageInputRef}
                type="file"
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                multiple
                style={{ display: "none" }}
                onChange={(e) => { handlePickRequestImages(e.target.files); e.target.value = ""; }}
              />
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                {requestImages.map((img) => (
                  <Box key={img.id} sx={{ position: "relative", width: 72, height: 72, borderRadius: "10px", overflow: "hidden", border: "1px solid #e0e0e0", background: "#f5f5f5" }}>
                    {isImageFile(img.mimetype) ? (
                      <img src={img.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "2px", p: "4px" }}>
                        <InsertDriveFileIcon sx={{ fontSize: 24, color: "#667085" }} />
                        <Box sx={{ fontSize: "8px", color: "#667085", textAlign: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", width: "100%" }}>
                          {img.name || "Document"}
                        </Box>
                      </Box>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveRequestImage(img)}
                      sx={{ position: "absolute", top: 2, right: 2, background: "rgba(0,0,0,0.55)", color: "#fff", width: 20, height: 20, "&:hover": { background: "rgba(211,47,47,0.85)" } }}
                    >
                      <CloseIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                  </Box>
                ))}
                <Box
                  onClick={() => requestImageInputRef.current?.click()}
                  sx={{ width: 72, height: 72, borderRadius: "10px", border: documentError ? "1.5px dashed #d92d20" : "1.5px dashed #d0d5dd", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: documentError ? "#d92d20" : "#98a2b3", "&:hover": { borderColor: "#39aafc", color: "#39aafc" } }}
                >
                  <AddIcon />
                </Box>
              </Box>
              {documentError && (
                <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mt: "8px", fontSize: "12px", fontWeight: 600, color: "#d92d20" }}>
                  <span style={{ fontSize: "13px" }}>⚠</span>
                  {documentError}
                </Box>
              )}
            </Box>

            {/* Add Request Button */}
            <Button
              variant="contained"
              onClick={handleAddRequest}
              sx={{
                background: "linear-gradient(135deg, #4db2fc, #1565C0)",
                color: "white",
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 800,
                height: "44px",
                borderRadius: "14px",
                marginTop: "10px",
                boxShadow: "0 8px 20px rgba(21, 101, 192, 0.35)",
                "&:hover": {
                  background: "linear-gradient(135deg, #39aafc, #0d47a1)",
                },
              }}
            >
              {uploadingImages ? "Uploading images…" : isEditMode ? "Update Request" : "Add Request"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Delete Payment Request — Confirmation Dialog (desktop only) */}
      <Dialog
        open={!!deleteConfirmRequest}
        onClose={() => !deleteRequestLoading && setDeleteConfirmRequest(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "24px", overflow: "hidden" } }}
      >
        <Box sx={{ background: "linear-gradient(155deg, #f87171 0%, #dc2626 100%)", pt: 3.5, pb: 3, px: 3, color: "#fff", textAlign: "center" }}>
          <Box sx={{ width: 52, height: 52, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
            <DeleteIcon sx={{ fontSize: 25 }} />
          </Box>
          <Typography sx={{ fontSize: "18px", fontWeight: 800, fontFamily: "Roboto, sans-serif" }}>
            Delete Payment Request?
          </Typography>
          <Typography sx={{ fontSize: "12.5px", opacity: 0.9, fontFamily: "Roboto, sans-serif", mt: "4px" }}>
            This action cannot be undone
          </Typography>
        </Box>
        <DialogContent sx={{ pt: "20px !important", textAlign: "center" }}>
          {deleteConfirmRequest && (
            <Box sx={{ background: "#f9fafb", borderRadius: "14px", px: 2, py: 1.5, mb: 1 }}>
              <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#101828", fontFamily: "Roboto, Arial, sans-serif" }}>
                {deleteConfirmRequest.vendorName}
              </Typography>
              <Typography sx={{ fontSize: "13px", color: "#667085", fontFamily: "Roboto, Arial, sans-serif", mt: "2px" }}>
                {formatCurrency(deleteConfirmRequest.amount)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5 }}>
          <Button
            onClick={() => setDeleteConfirmRequest(null)}
            disabled={deleteRequestLoading}
            fullWidth
            sx={{ textTransform: "none", fontWeight: 700, fontSize: "14px", color: "#667085", background: "#f2f4f7", borderRadius: "14px", py: 1.2, "&:hover": { background: "#e4e7ec" } }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => executeDeletePaymentRequest(deleteConfirmRequest)}
            disabled={deleteRequestLoading}
            fullWidth
            sx={{
              textTransform: "none",
              fontWeight: 800,
              fontSize: "14px",
              color: "#fff",
              background: "linear-gradient(135deg,#f87171,#dc2626)",
              borderRadius: "14px",
              py: 1.2,
              boxShadow: "0 8px 20px rgba(220,38,38,0.35)",
              "&:hover": { background: "linear-gradient(135deg,#ef4444,#b91c1c)" },
              "&.Mui-disabled": { background: "#e4e7ec", color: "#98a2b3" },
            }}
          >
            {deleteRequestLoading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Vendor-Wise bulk Reject — requires a reason, same rule as the
          single-item Reject flow, applied to every selected request. Works
          on both desktop and mobile (a plain confirm can't collect the
          reason text, so this always uses the dialog, unlike single delete).
          Mirrors the "Delete Payment Request?" gradient-hero confirmation
          template used elsewhere in this app, tinted with the same coral
          (#FF8570) this app already uses for the Rejected status everywhere
          else, so it reads as the same design language, not a generic modal. */}
      <Dialog
        open={vendorWiseRejectDialogOpen}
        onClose={() => !vendorWiseActionLoading && setVendorWiseRejectDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "24px", overflow: "hidden" } }}
      >
        <Box sx={{ background: "linear-gradient(155deg, #FF8570 0%, #dc2626 100%)", pt: 3.5, pb: 3, px: 3, color: "#fff", textAlign: "center" }}>
          <Box sx={{ width: 52, height: 52, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
            <CancelRoundedIcon sx={{ fontSize: 25 }} />
          </Box>
          <Typography sx={{ fontSize: "18px", fontWeight: 800, fontFamily: "Roboto, sans-serif" }}>
            Reject {vendorWiseSelectedIds.size} Payment{vendorWiseSelectedIds.size === 1 ? "" : "s"}?
          </Typography>
          <Typography sx={{ fontSize: "12.5px", opacity: 0.9, fontFamily: "Roboto, sans-serif", mt: "4px" }}>
            This action cannot be undone
          </Typography>
        </Box>
        <DialogContent sx={{ pt: "20px !important" }}>
          <Box sx={{ background: "#f9fafb", borderRadius: "14px", px: 2, py: 1.5, mb: 2, textAlign: "center" }}>
            <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#101828", fontFamily: "Roboto, Arial, sans-serif" }}>
              {vendorWiseSelectedVendor}
            </Typography>
            <Typography sx={{ fontSize: "13px", color: "#667085", fontFamily: "Roboto, Arial, sans-serif", mt: "2px" }}>
              {formatCurrency(vendorWiseSelectedTotal)}
            </Typography>
          </Box>
          <TextField
            label="Reason for Rejection"
            value={vendorWiseRejectReason}
            onChange={(e) => setVendorWiseRejectReason(e.target.value)}
            multiline
            rows={3}
            fullWidth
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5 }}>
          <Button
            onClick={() => { setVendorWiseRejectDialogOpen(false); setVendorWiseRejectReason(""); }}
            disabled={vendorWiseActionLoading}
            fullWidth
            sx={{ textTransform: "none", fontWeight: 700, fontSize: "14px", color: "#667085", background: "#f2f4f7", borderRadius: "14px", py: 1.2, "&:hover": { background: "#e4e7ec" } }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleVendorWiseBulkReject}
            disabled={vendorWiseActionLoading}
            fullWidth
            sx={{
              textTransform: "none",
              fontWeight: 800,
              fontSize: "14px",
              color: "#fff",
              background: "linear-gradient(135deg,#FF8570,#dc2626)",
              borderRadius: "14px",
              py: 1.2,
              boxShadow: "0 8px 20px rgba(220,38,38,0.35)",
              "&:hover": { background: "linear-gradient(135deg,#f87171,#b91c1c)" },
              "&.Mui-disabled": { background: "#e4e7ec", color: "#98a2b3" },
            }}
          >
            {vendorWiseActionLoading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Vendor-Wise bulk Remove — identical template to the single-item
          "Delete Payment Request?" dialog above, just pluralised, so a bulk
          removal reads as the exact same action at a bigger scale, not a
          separate, less-polished flow. */}
      <Dialog
        open={vendorWiseRemoveConfirmOpen}
        onClose={() => !vendorWiseActionLoading && setVendorWiseRemoveConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "24px", overflow: "hidden" } }}
      >
        <Box sx={{ background: "linear-gradient(155deg, #f87171 0%, #dc2626 100%)", pt: 3.5, pb: 3, px: 3, color: "#fff", textAlign: "center" }}>
          <Box sx={{ width: 52, height: 52, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
            <DeleteIcon sx={{ fontSize: 25 }} />
          </Box>
          <Typography sx={{ fontSize: "18px", fontWeight: 800, fontFamily: "Roboto, sans-serif" }}>
            Remove {vendorWiseSelectedIds.size} Payment{vendorWiseSelectedIds.size === 1 ? "" : "s"}?
          </Typography>
          <Typography sx={{ fontSize: "12.5px", opacity: 0.9, fontFamily: "Roboto, sans-serif", mt: "4px" }}>
            This action cannot be undone
          </Typography>
        </Box>
        <DialogContent sx={{ pt: "20px !important", textAlign: "center" }}>
          <Box sx={{ background: "#f9fafb", borderRadius: "14px", px: 2, py: 1.5, mb: 1 }}>
            <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#101828", fontFamily: "Roboto, Arial, sans-serif" }}>
              {vendorWiseSelectedVendor}
            </Typography>
            <Typography sx={{ fontSize: "13px", color: "#667085", fontFamily: "Roboto, Arial, sans-serif", mt: "2px" }}>
              {formatCurrency(vendorWiseSelectedTotal)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5 }}>
          <Button
            onClick={() => setVendorWiseRemoveConfirmOpen(false)}
            disabled={vendorWiseActionLoading}
            fullWidth
            sx={{ textTransform: "none", fontWeight: 700, fontSize: "14px", color: "#667085", background: "#f2f4f7", borderRadius: "14px", py: 1.2, "&:hover": { background: "#e4e7ec" } }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleVendorWiseBulkRemove}
            disabled={vendorWiseActionLoading}
            fullWidth
            sx={{
              textTransform: "none",
              fontWeight: 800,
              fontSize: "14px",
              color: "#fff",
              background: "linear-gradient(135deg,#f87171,#dc2626)",
              borderRadius: "14px",
              py: 1.2,
              boxShadow: "0 8px 20px rgba(220,38,38,0.35)",
              "&:hover": { background: "linear-gradient(135deg,#ef4444,#b91c1c)" },
              "&.Mui-disabled": { background: "#e4e7ec", color: "#98a2b3" },
            }}
          >
            {vendorWiseActionLoading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Project-Wise bulk Reject/Remove — identical templates to Vendor-Wise's
          own above, just scoped to the selected project instead of vendor. */}
      <Dialog
        open={projectWiseRejectDialogOpen}
        onClose={() => !projectWiseActionLoading && setProjectWiseRejectDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "24px", overflow: "hidden" } }}
      >
        <Box sx={{ background: "linear-gradient(155deg, #FF8570 0%, #dc2626 100%)", pt: 3.5, pb: 3, px: 3, color: "#fff", textAlign: "center" }}>
          <Box sx={{ width: 52, height: 52, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
            <CancelRoundedIcon sx={{ fontSize: 25 }} />
          </Box>
          <Typography sx={{ fontSize: "18px", fontWeight: 800, fontFamily: "Roboto, sans-serif" }}>
            Reject {projectWiseSelectedIds.size} Payment{projectWiseSelectedIds.size === 1 ? "" : "s"}?
          </Typography>
          <Typography sx={{ fontSize: "12.5px", opacity: 0.9, fontFamily: "Roboto, sans-serif", mt: "4px" }}>
            This action cannot be undone
          </Typography>
        </Box>
        <DialogContent sx={{ pt: "20px !important" }}>
          <Box sx={{ background: "#f9fafb", borderRadius: "14px", px: 2, py: 1.5, mb: 2, textAlign: "center" }}>
            <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#101828", fontFamily: "Roboto, Arial, sans-serif" }}>
              {projectWiseSelectedProject?.name}
            </Typography>
            <Typography sx={{ fontSize: "13px", color: "#667085", fontFamily: "Roboto, Arial, sans-serif", mt: "2px" }}>
              {formatCurrency(projectWiseSelectedTotal)}
            </Typography>
          </Box>
          <TextField
            label="Reason for Rejection"
            value={projectWiseRejectReason}
            onChange={(e) => setProjectWiseRejectReason(e.target.value)}
            multiline
            rows={3}
            fullWidth
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5 }}>
          <Button
            onClick={() => { setProjectWiseRejectDialogOpen(false); setProjectWiseRejectReason(""); }}
            disabled={projectWiseActionLoading}
            fullWidth
            sx={{ textTransform: "none", fontWeight: 700, fontSize: "14px", color: "#667085", background: "#f2f4f7", borderRadius: "14px", py: 1.2, "&:hover": { background: "#e4e7ec" } }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleProjectWiseBulkReject}
            disabled={projectWiseActionLoading}
            fullWidth
            sx={{
              textTransform: "none",
              fontWeight: 800,
              fontSize: "14px",
              color: "#fff",
              background: "linear-gradient(135deg,#FF8570,#dc2626)",
              borderRadius: "14px",
              py: 1.2,
              boxShadow: "0 8px 20px rgba(220,38,38,0.35)",
              "&:hover": { background: "linear-gradient(135deg,#f87171,#b91c1c)" },
              "&.Mui-disabled": { background: "#e4e7ec", color: "#98a2b3" },
            }}
          >
            {projectWiseActionLoading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={projectWiseRemoveConfirmOpen}
        onClose={() => !projectWiseActionLoading && setProjectWiseRemoveConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "24px", overflow: "hidden" } }}
      >
        <Box sx={{ background: "linear-gradient(155deg, #f87171 0%, #dc2626 100%)", pt: 3.5, pb: 3, px: 3, color: "#fff", textAlign: "center" }}>
          <Box sx={{ width: 52, height: 52, borderRadius: "50%", background: "rgba(255,255,255,0.2)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
            <DeleteIcon sx={{ fontSize: 25 }} />
          </Box>
          <Typography sx={{ fontSize: "18px", fontWeight: 800, fontFamily: "Roboto, sans-serif" }}>
            Remove {projectWiseSelectedIds.size} Payment{projectWiseSelectedIds.size === 1 ? "" : "s"}?
          </Typography>
          <Typography sx={{ fontSize: "12.5px", opacity: 0.9, fontFamily: "Roboto, sans-serif", mt: "4px" }}>
            This action cannot be undone
          </Typography>
        </Box>
        <DialogContent sx={{ pt: "20px !important", textAlign: "center" }}>
          <Box sx={{ background: "#f9fafb", borderRadius: "14px", px: 2, py: 1.5, mb: 1 }}>
            <Typography sx={{ fontSize: "14px", fontWeight: 700, color: "#101828", fontFamily: "Roboto, Arial, sans-serif" }}>
              {projectWiseSelectedProject?.name}
            </Typography>
            <Typography sx={{ fontSize: "13px", color: "#667085", fontFamily: "Roboto, Arial, sans-serif", mt: "2px" }}>
              {formatCurrency(projectWiseSelectedTotal)}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5 }}>
          <Button
            onClick={() => setProjectWiseRemoveConfirmOpen(false)}
            disabled={projectWiseActionLoading}
            fullWidth
            sx={{ textTransform: "none", fontWeight: 700, fontSize: "14px", color: "#667085", background: "#f2f4f7", borderRadius: "14px", py: 1.2, "&:hover": { background: "#e4e7ec" } }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleProjectWiseBulkRemove}
            disabled={projectWiseActionLoading}
            fullWidth
            sx={{
              textTransform: "none",
              fontWeight: 800,
              fontSize: "14px",
              color: "#fff",
              background: "linear-gradient(135deg,#f87171,#dc2626)",
              borderRadius: "14px",
              py: 1.2,
              boxShadow: "0 8px 20px rgba(220,38,38,0.35)",
              "&:hover": { background: "linear-gradient(135deg,#ef4444,#b91c1c)" },
              "&.Mui-disabled": { background: "#e4e7ec", color: "#98a2b3" },
            }}
          >
            {projectWiseActionLoading ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status Change Dialog (desktop; mobile uses the bottom sheet) */}
      <Dialog
        open={!isMobile && statusDialogOpen}
        onClose={handleStatusDialogClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "24px", overflow: "hidden" } }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            background: "linear-gradient(155deg, #4db2fc 0%, #1565C0 100%)",
            color: "#fff",
            padding: "20px 26px",
          }}
        >
          <Box>
            <Typography sx={{ fontSize: "17px", fontWeight: 800, fontFamily: "Roboto, Arial, sans-serif", letterSpacing: "-0.01em" }}>
              Update Status
            </Typography>
            {selectedRequest && (
              <Typography sx={{ fontSize: "12.5px", fontWeight: 600, color: "rgba(255,255,255,0.85)", mt: "2px" }}>
                {selectedRequest.vendorName} · {formatCurrency(selectedRequest.amount)}
              </Typography>
            )}
          </Box>
          <IconButton onClick={handleStatusDialogClose} size="small" sx={{ color: "#fff", background: "rgba(255,255,255,0.15)", "&:hover": { background: "rgba(255,255,255,0.25)" } }}>
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
        <DialogContent sx={{ padding: "24px !important", paddingTop: "24px !important", background: "#fbfcfe" }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "18px" }}>
            {/* Request Details */}
            {selectedRequest && (() => {
              const displayStatus = getDisplayStatus(selectedRequest);
              const accent = getApprovalColor(displayStatus);
              return (
                <Box sx={{ background: "#fff", border: "1px solid #eef1f5", borderRadius: "18px", boxShadow: "0 1px 3px rgba(16,24,40,0.05)", overflow: "hidden" }}>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: "16px 18px", borderBottom: "1px solid #f1f4f9" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "12px", minWidth: 0 }}>
                      <Box sx={{ width: "38px", height: "38px", borderRadius: "12px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "#eff4ff" }}>
                        <StorefrontOutlinedIcon sx={{ fontSize: "19px", color: "#1565C0" }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography noWrap sx={{ fontSize: "14.5px", fontWeight: 800, color: "#101828" }}>{selectedRequest.vendorName}</Typography>
                        <Typography noWrap sx={{ fontSize: "11.5px", color: "#98a2b3", fontWeight: 600, display: "flex", alignItems: "center", gap: "3px" }}>
                          <ApartmentOutlinedIcon sx={{ fontSize: "12px" }} /> {selectedRequest.clientName}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: "inline-flex", alignItems: "center", gap: "6px", px: "10px", py: "5px", borderRadius: "999px", background: `${accent}2b`, flexShrink: 0 }}>
                      <Box sx={{ width: 7, height: 7, borderRadius: "50%", background: accent }} />
                      <Typography sx={{ fontSize: "11px", fontWeight: 800, color: "#344054" }}>{displayStatus}</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: "14px 18px" }}>
                    <Typography sx={{ fontSize: "12px", color: "#667085", fontWeight: 600 }}>Amount Requested</Typography>
                    <Typography sx={{ fontSize: "16px", fontWeight: 800, color: "#101828", fontVariantNumeric: "tabular-nums" }}>{formatCurrency(selectedRequest.amount)}</Typography>
                  </Box>
                  {selectedRequest.isOverBudget && selectedRequest.approval !== "Pending" && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mx: "18px", mb: "16px", p: "10px 14px", borderRadius: "12px", background: "#ecfdf3", border: "1px solid #d5f0e1" }}>
                      <CheckCircleIcon sx={{ fontSize: "16px", color: "#059669" }} />
                      <Typography sx={{ fontSize: "12px", color: "#067647", fontWeight: 700 }}>
                        Approved by {selectedRequest.approvedByName || "Auto-approved"}
                      </Typography>
                    </Box>
                  )}
                </Box>
              );
            })()}

            {/* Documents Uploaded — images, PDFs, Word/Excel docs attached when the request was raised */}
            {selectedRequest && selectedRequest.images && selectedRequest.images.length > 0 && (
              <Box>
                <Typography sx={{ fontSize: "11.5px", fontWeight: 800, color: "#98a2b3", textTransform: "uppercase", letterSpacing: "0.04em", mb: "10px" }}>
                  Documents ({selectedRequest.images.length})
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                  {selectedRequest.images.map((img) => (
                    <Box
                      key={img.key}
                      component="a"
                      href={img.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={img.name || "Document"}
                      sx={{
                        width: 76,
                        height: 76,
                        borderRadius: "14px",
                        overflow: "hidden",
                        border: "1px solid #eef1f5",
                        display: "block",
                        background: "#f8fafc",
                        textDecoration: "none",
                        boxShadow: "0 1px 2px rgba(16,24,40,0.05)",
                        transition: "transform 0.15s ease, box-shadow 0.15s ease",
                        "&:hover": { transform: "translateY(-2px)", boxShadow: "0 6px 16px rgba(16,24,40,0.12)" },
                      }}
                    >
                      {isImageFile(img.mimetype) ? (
                        <img src={img.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      ) : (
                        <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "3px", p: "6px" }}>
                          <InsertDriveFileIcon sx={{ fontSize: 26, color: "#667085" }} />
                          <Box sx={{ fontSize: "9px", color: "#667085", textAlign: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", width: "100%" }}>
                            {img.name || "Document"}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Budget Context — which category/sub-category pool this vendor
                draws from, its total budget, what's been paid so far against
                it, what's still available, and the other payments already
                made against that same pool for this client. */}
            {statusBudgetLoading ? (
              <Box sx={{ background: "#fff", border: "1px solid #eef1f5", borderRadius: "18px", p: "16px 18px", textAlign: "center", fontSize: "12.5px", color: "#98a2b3" }}>
                Loading budget context…
              </Box>
            ) : statusBudgetInfo && (
              <Box sx={{ background: "#fff", border: "1px solid #eef1f5", borderRadius: "18px", boxShadow: "0 1px 3px rgba(16,24,40,0.05)", overflow: "hidden" }}>
                <Box sx={{ p: "14px 18px", borderBottom: "1px solid #f1f4f9" }}>
                  <Typography sx={{ fontSize: "10.5px", fontWeight: 800, color: "#98a2b3", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Budget Pool{statusBudgetInfo.poolLabel.includes('→') ? " · Sub-Category" : " · Category"}
                  </Typography>
                  <Typography sx={{ fontSize: "13.5px", fontWeight: 800, color: "#101828", mt: "2px" }}>{statusBudgetInfo.poolLabel}</Typography>
                </Box>
                <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", p: "14px 18px", gap: 1 }}>
                  <Box>
                    <Typography sx={{ fontSize: "9.5px", color: "#98a2b3", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.03em" }}>Total Budget</Typography>
                    <Typography sx={{ fontSize: "14px", fontWeight: 800, color: "#101828", mt: "2px" }}>{formatCurrency(statusBudgetInfo.budgetAmount)}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: "9.5px", color: "#98a2b3", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.03em" }}>Paid Till Date</Typography>
                    <Typography sx={{ fontSize: "14px", fontWeight: 800, color: "#d92d20", mt: "2px" }}>{formatCurrency(statusBudgetInfo.paidTillDate)}</Typography>
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: "9.5px", color: statusBudgetInfo.available < 0 ? "#d92d20" : "#059669", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.03em" }}>Balance Available</Typography>
                    <Typography sx={{ fontSize: "14px", fontWeight: 800, color: statusBudgetInfo.available < 0 ? "#d92d20" : "#059669", mt: "2px" }}>{formatCurrency(statusBudgetInfo.available)}</Typography>
                  </Box>
                </Box>
                {(statusBudgetInfo.pendingSum > 0 || statusBudgetInfo.approvedSum > 0) && (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", px: "18px", pb: "12px" }}>
                    {statusBudgetInfo.pendingSum > 0 && (
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "11.5px" }}>
                        <Box sx={{ color: "#b54708", fontWeight: 600 }}>⏳ Other Pending (same pool)</Box>
                        <Box sx={{ color: "#b54708", fontWeight: 800 }}>{formatCurrency(statusBudgetInfo.pendingSum)}</Box>
                      </Box>
                    )}
                    {statusBudgetInfo.approvedSum > 0 && (
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "11.5px" }}>
                        <Box sx={{ color: "#1f6fd6", fontWeight: 600 }}>✓ Approved, Awaiting Payment (same pool)</Box>
                        <Box sx={{ color: "#1f6fd6", fontWeight: 800 }}>{formatCurrency(statusBudgetInfo.approvedSum)}</Box>
                      </Box>
                    )}
                  </Box>
                )}
                {statusBudgetInfo.previousPayments.length > 0 && (
                  <Box sx={{ borderTop: "1px solid #f1f4f9" }}>
                    <Typography sx={{ fontSize: "10.5px", fontWeight: 800, color: "#98a2b3", textTransform: "uppercase", letterSpacing: "0.05em", px: "18px", pt: "12px", pb: "6px" }}>
                      Previous Payments — Same Category ({statusBudgetInfo.previousPayments.length})
                    </Typography>
                    <Box sx={{ maxHeight: "180px", overflowY: "auto" }}>
                      {statusBudgetInfo.previousPayments.map((p, i) => (
                        <Box key={p.id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: "18px", py: "8px", borderTop: i > 0 ? "1px solid #f8fafc" : "none" }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography noWrap sx={{ fontSize: "12.5px", fontWeight: 700, color: "#101828" }}>{p.vendorName}</Typography>
                            <Typography sx={{ fontSize: "10.5px", color: "#98a2b3", fontWeight: 600 }}>{p.date ? new Date(p.date).toLocaleDateString("en-IN") : "—"}</Typography>
                          </Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
                            <Typography sx={{ fontSize: "12.5px", fontWeight: 800, color: "#101828" }}>{formatCurrency(p.amount)}</Typography>
                            <Box sx={{ display: "inline-flex", alignItems: "center", gap: "5px", px: "8px", py: "3px", borderRadius: "999px", background: `${getApprovalColor(p.status)}2b` }}>
                              <Box sx={{ width: 6, height: 6, borderRadius: "50%", background: getApprovalColor(p.status) }} />
                              <Typography sx={{ fontSize: "10px", fontWeight: 700, color: "#344054" }}>{p.status}</Typography>
                            </Box>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}

            {/* Status Selection */}
            <Box>
              <Typography sx={{ fontSize: "11.5px", fontWeight: 800, color: "#98a2b3", textTransform: "uppercase", letterSpacing: "0.04em", mb: "10px" }}>
                Select Status
              </Typography>
              <Box sx={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                {(() => {
                  // Check if current user is the request creator
                  const isRequestCreator = selectedRequest &&
                    (selectedRequest.userId === user?._id ||
                     String(selectedRequest.userId) === String(user?._id));

                  // Check if current user is the person incharge assigned to
                  // THIS SPECIFIC request (legacy rule — preserved as-is)
                  const isPersonIncharge = selectedRequest &&
                    (selectedRequest.personInchargeId === user?._id ||
                     String(selectedRequest.personInchargeId) === String(user?._id));

                  // Being a Person Incharge for this one request is no longer the
                  // only way in — Vendor Rights > Tabs Access can now explicitly
                  // grant Full Access per status, independent of the Approval
                  // Matrix assignment. Someone with Full Access on Approved/Paid
                  // must be able to act on ANY request in that status, not just
                  // ones they personally were assigned to approve.
                  const canSetApproved = isMainUser || isPersonIncharge || vendorAccessOf('vendorTabApproved') === 'full_access';
                  const canSetPaid = isMainUser || isPersonIncharge || vendorAccessOf('vendorTabPaid') === 'full_access';
                  const canSetRejected = isMainUser || isPersonIncharge || isRequestCreator || vendorAccessOf('vendorTabRejected') === 'full_access';
                  const canSetCorrection = isMainUser || isPersonIncharge || isRequestCreator || vendorAccessOf('vendorTabCorrection') === 'full_access';

                  let availableStatuses = [];
                  if (canSetCorrection) availableStatuses.push('Pending');
                  if (canSetApproved) availableStatuses.push('Approved');
                  if (canSetRejected) availableStatuses.push('Rejected');
                  if (canSetPaid) availableStatuses.push('Paid');
                  if (availableStatuses.length === 0) availableStatuses = ['Pending', 'Rejected']; // safety fallback

                  const statusIcon = (status) => {
                    const sx = { fontSize: "17px" };
                    if (status === "Approved") return <CheckCircleIcon sx={sx} />;
                    if (status === "Rejected") return <CancelRoundedIcon sx={sx} />;
                    if (status === "Paid") return <PaymentsRoundedIcon sx={sx} />;
                    return <AutorenewRoundedIcon sx={sx} />; // Pending -> shown as "Correction"
                  };

                  return availableStatuses.map((status) => {
                    const active = newStatus === status;
                    const accent = getApprovalColor(status === "Pending" ? "Correction" : status);
                    return (
                      <Box
                        key={status}
                        onClick={() => setNewStatus(status)}
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: "8px",
                          px: "16px",
                          py: "10px",
                          borderRadius: "14px",
                          border: `1.5px solid ${active ? accent : "#e4e7ec"}`,
                          background: active ? `${accent}22` : "#fff",
                          color: active ? "#101828" : "#667085",
                          fontSize: "13px",
                          fontWeight: 700,
                          fontFamily: "Roboto, Arial, sans-serif",
                          cursor: "pointer",
                          userSelect: "none",
                          transition: "all 0.15s ease",
                          "& .MuiSvgIcon-root": { color: active ? accent : "#98a2b3" },
                          "&:hover": { borderColor: accent, background: `${accent}15` },
                        }}
                      >
                        {statusIcon(status)}
                        {status === "Pending" ? "Correction" : status}
                      </Box>
                    );
                  });
                })()}
              </Box>
            </Box>

            {/* Date of Payment is captured via the "Mark as Paid" confirmation popup instead */}

            {newStatus === "Rejected" && (
              <TextField
                label="Reason for Rejection"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                multiline
                rows={3}
                sx={{
                  "& .MuiInputBase-root": {
                    fontSize: "13px",
                    fontFamily: "Roboto, Arial, sans-serif",
                    borderRadius: "14px",
                    backgroundColor: "#fff",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "13px",
                    fontFamily: "Roboto, Arial, sans-serif"
                  },
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#fecdca" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#FF8570" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#FF8570", borderWidth: "1.5px" },
                }}
              />
            )}

            {newStatus === "Pending" && (
              <TextField
                label="What needs to be corrected? *"
                value={pendingReason}
                onChange={(e) => setPendingReason(e.target.value)}
                multiline
                rows={3}
                sx={{
                  "& .MuiInputBase-root": {
                    fontSize: "13px",
                    fontFamily: "Roboto, Arial, sans-serif",
                    borderRadius: "14px",
                    backgroundColor: "#fff",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "13px",
                    fontFamily: "Roboto, Arial, sans-serif"
                  },
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#fedf89" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#F79009" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#F79009", borderWidth: "1.5px" },
                }}
              />
            )}

            {newStatus === "Approved" && (
              <TextField
                label="Date of Approval"
                type="date"
                value={approvalDate}
                onChange={(e) => setApprovalDate(e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    fontSize: "13px",
                    fontFamily: "Roboto, Arial, sans-serif",
                    height: "48px",
                    borderRadius: "14px",
                    backgroundColor: "#fff",
                  },
                  "& .MuiInputLabel-root": {
                    fontSize: "13px",
                    fontFamily: "Roboto, Arial, sans-serif"
                  },
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#bbf0d1" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#59AC77" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#59AC77", borderWidth: "1.5px" },
                }}
              />
            )}

            {/* Save Button */}
            <Button
              variant="contained"
              onClick={() => {
                if (newStatus === "Paid") {
                  if (!paymentDate) setPaymentDate(new Date().toISOString().split('T')[0]);
                  setMarkPaidDialogOpen(true);
                } else {
                  handleStatusSave();
                }
              }}
              startIcon={newStatus === "Paid" ? <PaymentsRoundedIcon /> : newStatus === "Pending" ? <AutorenewRoundedIcon /> : <CheckCircleIcon />}
              sx={{
                background: "linear-gradient(135deg, #4db2fc, #1565C0)",
                color: "white",
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 800,
                height: "46px",
                borderRadius: "14px",
                marginTop: "4px",
                boxShadow: "0 8px 20px rgba(21, 101, 192, 0.35)",
                "&:hover": {
                  background: "linear-gradient(135deg, #39aafc, #0d47a1)",
                },
              }}
            >
              {newStatus === "Paid" ? "Mark as Paid" : newStatus === "Pending" ? "Send for Correction" : "Save Changes"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* ===== MOBILE SHEET: Add Vendor Payment ===== */}
      {isMobile && openDialog && (() => {
        const vIn = { "& .MuiOutlinedInput-root": { borderRadius: "14px", backgroundColor: "#f9fafb", fontSize: "15px", "& fieldset": { borderColor: "#e4e7ec" }, "&.Mui-focused fieldset": { borderColor: "#39aafc", borderWidth: "1.5px" } }, "& .MuiInputBase-input": { fontSize: "15px", padding: "13px 14px", fontWeight: 600, color: "#101828", fontFamily: "Roboto, Arial, sans-serif" }, "& .MuiInputLabel-root": { fontSize: "13px" } };
        // Inside a position:fixed bottom sheet, the mobile keyboard opening
        // doesn't push the sheet's layout up the way normal page scroll does —
        // the focused field (and its Autocomplete dropdown) can end up sitting
        // behind the keyboard, invisible, which also makes filtering-as-you-
        // type look broken since you can't see the list update. React's
        // synthetic onFocus bubbles (unlike native focus), so one handler on
        // the fields' wrapping Box catches focus on any of them. The delay
        // waits out the keyboard's open animation before scrolling, otherwise
        // the field settles right back underneath it once the keyboard finishes.
        const scrollFieldIntoView = (e) => {
          const el = e.target;
          setTimeout(() => { el.scrollIntoView({ block: "center", behavior: "smooth" }); }, 300);
        };
        const mPool = selectedVendor ? resolvePool(selectedVendor) : null;
        const { pendingSum: mPendingSum, approvedSum: mApprovedSum } = mPool
          ? getPoolOutstanding(mPool)
          : { pendingSum: 0, approvedSum: 0 };
        const mEffectivePaid = mPool ? getPoolEffectivePaid(mPool) : 0;
        const mBudgetAmount = parseFloat(mPool?.budget?.budget_amount) || 0;
        const mPoolLabel = mPool ? (mPool.subCategory ? `${mPool.category} → ${mPool.subCategory}` : mPool.category) : "No Category Assigned";
        const budgetAvailable = getPoolBudgetAvailable(selectedVendor);
        return (
          <>
            <div className="arcdx-sheet-back" onClick={handleCloseDialog} />
            <div className="arcdx-sheet">
              <div className="arcdx-sheet-grip" />
              <div className="arcdx-v-sheet-head">
                <span className="arcdx-v-sheet-title">{isEditMode ? "Edit Vendor Payment" : "Vendor Payment"}</span>
                <IconButton onClick={handleCloseDialog} size="small" sx={{ background: "#f2f4f7" }}>
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </div>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "14px" }} onFocus={scrollFieldIntoView}>
                {formError && (
                  <Box sx={{ background: "#fef3f2", border: "1px solid #fecdca", borderRadius: "10px", px: "14px", py: "10px", fontSize: "12.5px", fontWeight: 600, color: "#b42318" }}>
                    {formError}
                  </Box>
                )}
                <TextField label="Date" type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} InputLabelProps={{ shrink: true }} sx={vIn} fullWidth />
                <Autocomplete
                  options={clientOptions}
                  filterOptions={(x) => x}
                  getOptionLabel={(o) => o.name}
                  getOptionKey={(o) => o.id}
                  value={selectedClient}
                  loading={clientOptionsLoading}
                  disabled={clientLockedForRequest}
                  onInputChange={(e, newInputValue, reason) => {
                    if (reason === 'input') setClientSearchInput(newInputValue);
                  }}
                  onChange={(e, v) => { setSelectedClient(v); if (!vendorLockedForRequest) setSelectedVendor(null); }}
                  renderInput={(p) => <TextField {...p} label="Client" sx={vIn} />}
                />
                <Autocomplete
                  options={categorizedVendorOptions}
                  filterOptions={vendorFilterOptions}
                  getOptionLabel={(o) => o.name}
                  getOptionKey={(o) => o.id}
                  renderOption={renderVendorOption}
                  value={selectedVendor}
                  onChange={(e, v) => setSelectedVendor(v)}
                  disabled={vendorLockedForRequest || !selectedClient || vendorLoading}
                  loading={vendorLoading}
                  noOptionsText={!selectedClient ? "Select a client first" : "No active vendors found"}
                  renderInput={(p) => <TextField {...p} label="Vendor" sx={vIn} />}
                />
                {selectedVendor && (
                  <>
                  <Box sx={{ fontSize: "10.5px", color: "#98a2b3", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.03em" }}>
                    Budget Pool{mPool?.subCategory ? " · Sub-Category" : " · Category"}
                  </Box>
                  <Box sx={{ fontSize: "13px", fontWeight: 700, color: "#101828", mt: "-8px" }}>{mPoolLabel}</Box>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", background: "linear-gradient(135deg, #f8fafc 0%, #eef2f7 100%)", border: "1px solid #e4e7ec", borderRadius: "14px", p: "10px 4px" }}>
                    <Box sx={{ textAlign: "center", px: "4px" }}>
                      <Box sx={{ fontSize: "8.5px", color: "#98a2b3", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.03em" }}>Total Budget</Box>
                      <Box sx={{ fontSize: "12px", fontWeight: 800, color: "#101828", mt: "3px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>₹{mBudgetAmount.toLocaleString("en-IN")}</Box>
                    </Box>
                    <Box sx={{ textAlign: "center", px: "4px", borderLeft: "1px solid #e4e7ec", borderRight: "1px solid #e4e7ec" }}>
                      <Box sx={{ fontSize: "8.5px", color: "#98a2b3", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.03em" }}>Already Paid</Box>
                      <Box sx={{ fontSize: "12px", fontWeight: 800, color: "#d92d20", mt: "3px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>₹{mEffectivePaid.toLocaleString("en-IN")}</Box>
                    </Box>
                    <Box sx={{ textAlign: "center", px: "4px" }}>
                      <Box sx={{ fontSize: "8.5px", color: budgetAvailable < 0 ? "#d92d20" : "#059669", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.03em" }}>Available</Box>
                      <Box sx={{ fontSize: "13px", fontWeight: 800, color: budgetAvailable < 0 ? "#d92d20" : "#059669", mt: "3px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>₹{budgetAvailable.toLocaleString("en-IN")}</Box>
                    </Box>
                  </Box>
                  {/* Why the available budget is lower than Total − Paid: other
                      outstanding requests for this same vendor already claim part of it */}
                  {(mPendingSum > 0 || mApprovedSum > 0) && (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", mt: "6px" }}>
                      {mPendingSum > 0 && (
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "11px" }}>
                          <Box sx={{ color: "#b54708", fontWeight: 600 }}>⏳ Pending Approval (other requests)</Box>
                          <Box sx={{ color: "#b54708", fontWeight: 800 }}>₹{mPendingSum.toLocaleString("en-IN")}</Box>
                        </Box>
                      )}
                      {mApprovedSum > 0 && (
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "11px" }}>
                          <Box sx={{ color: "#1f6fd6", fontWeight: 600 }}>✓ Approved, Awaiting Payment</Box>
                          <Box sx={{ color: "#1f6fd6", fontWeight: 800 }}>₹{mApprovedSum.toLocaleString("en-IN")}</Box>
                        </Box>
                      )}
                    </Box>
                  )}
                  </>
                )}
                <TextField
                  label="Amount"
                  type="number"
                  value={amount}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  inputProps={{ inputMode: "decimal" }}
                  InputProps={{ startAdornment: <span style={{ color: "#98a2b3", marginRight: 6, fontSize: 15 }}>₹</span> }}
                  sx={vIn}
                  fullWidth
                />
                {isOverBudget && (
                  <>
                    <Box sx={{ background: "#fff7e6", border: "1px solid #f4c790", borderRadius: "14px", p: "12px", fontSize: "12.5px", color: "#b54708", lineHeight: 1.5 }}>
                      ⚠️ Amount exceeds available budget — this request will need approval
                      {personIncharge ? ` from ${personIncharge.name}` : " from your Person Incharge"}.
                    </Box>
                    <TextField label="Reason for over-budget request *" value={overBudgetReason} onChange={(e) => setOverBudgetReason(e.target.value)} multiline minRows={1} maxRows={6} sx={vIn} fullWidth />
                  </>
                )}

                {/* Upload Documents — below the Reason box (images, PDFs, Word/Excel docs).
                    Mandatory for Material, optional for Labour. */}
                <Box>
                  <Box sx={{ fontSize: "11px", color: "#98a2b3", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", mb: "8px" }}>
                    Upload Documents {selectedVendor?.type === 'Material' ? '*' : '(optional)'}
                  </Box>
                  <input
                    ref={requestImageInputRef}
                    type="file"
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                    multiple
                    style={{ display: "none" }}
                    onChange={(e) => { handlePickRequestImages(e.target.files); e.target.value = ""; }}
                  />
                  <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px" }}>
                    {requestImages.map((img) => (
                      <Box key={img.id} sx={{ position: "relative", aspectRatio: "1", borderRadius: "12px", overflow: "hidden", border: "1px solid #e4e7ec", background: "#fafbfc" }}>
                        {isImageFile(img.mimetype) ? (
                          <img src={img.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                        ) : (
                          <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "2px", p: "4px" }}>
                            <InsertDriveFileIcon sx={{ fontSize: 22, color: "#667085" }} />
                            <Box sx={{ fontSize: "8px", color: "#667085", textAlign: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", width: "100%" }}>
                              {img.name || "Document"}
                            </Box>
                          </Box>
                        )}
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveRequestImage(img)}
                          sx={{ position: "absolute", top: 3, right: 3, background: "rgba(0,0,0,0.55)", color: "#fff", width: 20, height: 20, "&:active": { background: "rgba(211,47,47,0.85)" } }}
                        >
                          <CloseIcon sx={{ fontSize: 13 }} />
                        </IconButton>
                      </Box>
                    ))}
                    {/* Trailing "add more" tile — always present so you can keep adding beyond 3 */}
                    <Box
                      onClick={() => requestImageInputRef.current?.click()}
                      sx={{ aspectRatio: "1", border: documentError ? "1.5px dashed #d92d20" : "1.5px dashed #d0d5dd", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", background: documentError ? "#fef3f2" : "#fafbfc", cursor: "pointer" }}
                    >
                      <span style={{ fontSize: "22px", color: documentError ? "#d92d20" : "#d0d5dd" }}>+</span>
                    </Box>
                  </Box>
                  {documentError && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mt: "8px", fontSize: "12px", fontWeight: 600, color: "#d92d20" }}>
                      <span style={{ fontSize: "13px" }}>⚠</span>
                      {documentError}
                    </Box>
                  )}
                </Box>

                <Button
                  onClick={handleAddRequest}
                  sx={{ py: 1.5, borderRadius: "14px", background: "linear-gradient(135deg,#39aafc,#1f8fe8)", color: "#fff", fontWeight: 700, textTransform: "none", fontSize: "16px", boxShadow: "0 8px 20px rgba(31,143,232,0.4)" }}
                  fullWidth
                >
                  {uploadingImages ? "Uploading images…" : isEditMode ? "Update Payment Request" : "Raise Payment Request"}
                </Button>
              </Box>
            </div>
          </>
        );
      })()}

      {/* ===== MOBILE SHEET: Change Status ===== */}
      {isMobile && statusDialogOpen && selectedRequest && (() => {
        const vIn = { "& .MuiOutlinedInput-root": { borderRadius: "14px", backgroundColor: "#f9fafb", fontSize: "15px", "& fieldset": { borderColor: "#e4e7ec" }, "&.Mui-focused fieldset": { borderColor: "#39aafc", borderWidth: "1.5px" } }, "& .MuiInputBase-input": { fontSize: "15px", padding: "13px 14px" }, "& .MuiInputLabel-root": { fontSize: "13px" } };
        // Same role rules as the desktop dialog — being the Approval Matrix's
        // assigned Person Incharge for THIS request is no longer the only way
        // in. Vendor Rights > Tabs Access can explicitly grant Full Access per
        // status, so someone with Full Access on Approved/Paid can act on any
        // request in that status, not just ones they personally were assigned.
        const isRequestCreator = String(selectedRequest.userId) === String(user?._id);
        const isPersonInchargeFor = String(selectedRequest.personInchargeId) === String(user?._id);
        const canSetApproved = isMainUser || isPersonInchargeFor || vendorAccessOf('vendorTabApproved') === 'full_access';
        const canSetPaid = isMainUser || isPersonInchargeFor || vendorAccessOf('vendorTabPaid') === 'full_access';
        const canSetRejected = isMainUser || isPersonInchargeFor || isRequestCreator || vendorAccessOf('vendorTabRejected') === 'full_access';
        const canSetCorrection = isMainUser || isPersonInchargeFor || isRequestCreator || vendorAccessOf('vendorTabCorrection') === 'full_access';
        // Approving a Pending request is a yes/no decision — only Approve or
        // Reject make sense here; Correction/Paid belong to later steps.
        const isStillPending = selectedRequest.approval === "Pending";
        let availableStatuses = [];
        if (!isStillPending && canSetCorrection) availableStatuses.push("Pending");
        if (canSetApproved) availableStatuses.push("Approved");
        if (canSetRejected) availableStatuses.push("Rejected");
        if (!isStillPending && canSetPaid) availableStatuses.push("Paid");
        if (availableStatuses.length === 0) availableStatuses = ["Pending", "Rejected"];
        const statusLabel = (s) => (s === "Approved" ? "Approve" : s === "Rejected" ? "Reject" : s === "Pending" ? "Correction" : s);
        const submitLabel = newStatus === "Approved" ? "Approve" : newStatus === "Rejected" ? "Reject" : newStatus === "Paid" ? "Mark as Paid" : newStatus === "Pending" ? "Send for Correction" : "Save Changes";
        return (
          <>
            <div className="arcdx-sheet-back" onClick={handleStatusDialogClose} />
            <div className="arcdx-sheet">
              <div className="arcdx-sheet-grip" />
              <div className="arcdx-v-sheet-head">
                <span className="arcdx-v-sheet-title">Update Status</span>
                <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  {/* Only the person who raised this request can edit/delete it */}
                  {isRequestCreator && (
                    <>
                      <IconButton
                        onClick={() => handleEditPaymentRequest(selectedRequest)}
                        size="small"
                        sx={{ background: "#eff4ff", color: "#1f6fd6" }}
                      >
                        <EditIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeletePaymentRequest(selectedRequest)}
                        size="small"
                        sx={{ background: "#fef3f2", color: "#d92d20" }}
                      >
                        <DeleteIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </>
                  )}
                  <IconButton onClick={handleStatusDialogClose} size="small" sx={{ background: "#f2f4f7" }}>
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>
              </div>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <div className="arcdx-v-budget">
                  <Box sx={{ fontSize: "14px", fontWeight: 800, color: "#101828" }}>{selectedRequest.vendorName}</Box>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mt: "8px" }}>
                    <Box>
                      <Box sx={{ fontSize: "9.5px", color: "#98a2b3", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>Available Budget</Box>
                      <Box sx={{ fontSize: "15px", fontWeight: 800, color: "#101828" }}>{formatCurrency(selectedRequest.budgetAvailable)}</Box>
                    </Box>
                    <Box>
                      <Box sx={{ fontSize: "9.5px", color: "#98a2b3", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>Amount Requested</Box>
                      <Box sx={{ fontSize: "15px", fontWeight: 800, color: selectedRequest.isOverBudget ? "#d92d20" : "#101828" }}>{formatCurrency(selectedRequest.amount)}</Box>
                    </Box>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: "10px" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "4px", minWidth: 0 }}>
                      <Box sx={{ fontSize: "10.5px", color: "#98a2b3", fontWeight: 600 }}>Client:</Box>
                      <Box sx={{ fontSize: "12px", fontWeight: 700, color: "#4f46e5", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{selectedRequest.clientName}</Box>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "4px", minWidth: 0 }}>
                      <Box sx={{ fontSize: "10.5px", color: "#98a2b3", fontWeight: 600 }}>By:</Box>
                      <Box sx={{ fontSize: "12px", fontWeight: 700, color: "#101828", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{selectedRequest.requestedByName || "—"}</Box>
                    </Box>
                  </Box>
                </div>

                {/* Budget Context — live Total Budget / Paid Till Date /
                    Balance Available for this vendor's category/sub-category
                    pool, plus other payments already made against it. */}
                {statusBudgetLoading ? (
                  <Box sx={{ background: "#fff", border: "1px solid #eef1f5", borderRadius: "14px", p: "12px 14px", textAlign: "center", fontSize: "12px", color: "#98a2b3" }}>
                    Loading budget context…
                  </Box>
                ) : statusBudgetInfo && (
                  <Box sx={{ background: "#fff", border: "1px solid #eef1f5", borderRadius: "14px", overflow: "hidden" }}>
                    <Box sx={{ p: "10px 14px", borderBottom: "1px solid #f1f4f9" }}>
                      <Box sx={{ fontSize: "9.5px", color: "#98a2b3", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.03em" }}>
                        Budget Pool{statusBudgetInfo.poolLabel.includes('→') ? " · Sub-Category" : " · Category"}
                      </Box>
                      <Box sx={{ fontSize: "13px", fontWeight: 800, color: "#101828", mt: "2px" }}>{statusBudgetInfo.poolLabel}</Box>
                    </Box>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", p: "10px 14px", gap: 1 }}>
                      <Box>
                        <Box sx={{ fontSize: "8.5px", color: "#98a2b3", fontWeight: 700, textTransform: "uppercase" }}>Total Budget</Box>
                        <Box sx={{ fontSize: "12.5px", fontWeight: 800, color: "#101828" }}>{formatCurrency(statusBudgetInfo.budgetAmount)}</Box>
                      </Box>
                      <Box>
                        <Box sx={{ fontSize: "8.5px", color: "#98a2b3", fontWeight: 700, textTransform: "uppercase" }}>Paid Till Date</Box>
                        <Box sx={{ fontSize: "12.5px", fontWeight: 800, color: "#d92d20" }}>{formatCurrency(statusBudgetInfo.paidTillDate)}</Box>
                      </Box>
                      <Box>
                        <Box sx={{ fontSize: "8.5px", color: statusBudgetInfo.available < 0 ? "#d92d20" : "#059669", fontWeight: 700, textTransform: "uppercase" }}>Available</Box>
                        <Box sx={{ fontSize: "12.5px", fontWeight: 800, color: statusBudgetInfo.available < 0 ? "#d92d20" : "#059669" }}>{formatCurrency(statusBudgetInfo.available)}</Box>
                      </Box>
                    </Box>
                    {(statusBudgetInfo.pendingSum > 0 || statusBudgetInfo.approvedSum > 0) && (
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "4px", px: "14px", pb: "10px" }}>
                        {statusBudgetInfo.pendingSum > 0 && (
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "11px" }}>
                            <Box sx={{ color: "#b54708", fontWeight: 600 }}>⏳ Other Pending</Box>
                            <Box sx={{ color: "#b54708", fontWeight: 800 }}>{formatCurrency(statusBudgetInfo.pendingSum)}</Box>
                          </Box>
                        )}
                        {statusBudgetInfo.approvedSum > 0 && (
                          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "11px" }}>
                            <Box sx={{ color: "#1f6fd6", fontWeight: 600 }}>✓ Approved, Awaiting Payment</Box>
                            <Box sx={{ color: "#1f6fd6", fontWeight: 800 }}>{formatCurrency(statusBudgetInfo.approvedSum)}</Box>
                          </Box>
                        )}
                      </Box>
                    )}
                    {statusBudgetInfo.previousPayments.length > 0 && (
                      <Box sx={{ borderTop: "1px solid #f1f4f9" }}>
                        <Box sx={{ fontSize: "9.5px", fontWeight: 800, color: "#98a2b3", textTransform: "uppercase", letterSpacing: "0.03em", px: "14px", pt: "10px", pb: "4px" }}>
                          Previous Payments — Same Category ({statusBudgetInfo.previousPayments.length})
                        </Box>
                        <Box sx={{ maxHeight: "160px", overflowY: "auto" }}>
                          {statusBudgetInfo.previousPayments.map((p, i) => (
                            <Box key={p.id} sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: "14px", py: "7px", borderTop: i > 0 ? "1px solid #f8fafc" : "none" }}>
                              <Box sx={{ minWidth: 0 }}>
                                <Box sx={{ fontSize: "12px", fontWeight: 700, color: "#101828", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{p.vendorName}</Box>
                                <Box sx={{ fontSize: "10px", color: "#98a2b3", fontWeight: 600 }}>{p.date ? new Date(p.date).toLocaleDateString("en-IN") : "—"}</Box>
                              </Box>
                              <Box sx={{ display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 }}>
                                <Box sx={{ fontSize: "12px", fontWeight: 800, color: "#101828" }}>{formatCurrency(p.amount)}</Box>
                                <Box sx={{ display: "inline-flex", alignItems: "center", gap: "4px", px: "7px", py: "2px", borderRadius: "999px", background: `${getApprovalColor(p.status)}2b` }}>
                                  <Box sx={{ width: 5, height: 5, borderRadius: "50%", background: getApprovalColor(p.status) }} />
                                  <Box sx={{ fontSize: "9px", fontWeight: 700, color: "#344054" }}>{p.status}</Box>
                                </Box>
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Over-Budget Reason — compact warning card */}
                {selectedRequest.reason && (
                  <Box sx={{ background: "linear-gradient(135deg, #fff9ec 0%, #fff1d6 100%)", border: "1.5px solid #f4c790", borderRadius: "14px", p: "10px 12px", boxShadow: "0 3px 10px rgba(180,83,9,0.1)" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "6px", mb: "3px" }}>
                      <span style={{ fontSize: "13px" }}>⚠️</span>
                      <Box sx={{ fontSize: "11px", fontWeight: 800, color: "#b54708", letterSpacing: "0.02em" }}>Over-Budget Reason</Box>
                    </Box>
                    <Box sx={{ fontSize: "12px", color: "#92400e", lineHeight: 1.4 }}>{selectedRequest.reason}</Box>
                  </Box>
                )}

                {/* Approved By — its own prominent card, only for over-budget requests that already went through a human approval */}
                {selectedRequest.isOverBudget && selectedRequest.approval !== "Pending" && (
                  <Box sx={{ background: "linear-gradient(155deg, #ecfdf5 0%, #d1fae5 100%)", border: "1px solid #a7f3d0", borderRadius: "14px", p: "12px 14px", boxShadow: "0 5px 16px rgba(4,120,87,0.12)" }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <Box sx={{ width: 34, height: 34, borderRadius: "50%", background: "rgba(255,255,255,0.6)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        <CheckCircleIcon sx={{ fontSize: 20, color: "#059669" }} />
                      </Box>
                      <Box sx={{ minWidth: 0 }}>
                        <Box sx={{ fontSize: "10px", fontWeight: 800, color: "#067647", textTransform: "uppercase", letterSpacing: "0.05em" }}>Approved By</Box>
                        <Box sx={{ fontSize: "15px", fontWeight: 800, color: "#065f46", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {selectedRequest.approvedByName || "Auto-approved"}
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                )}

                {/* Project Financial Insight — Approvals (Pending) view only */}
                {selectedRequest.approval === "Pending" && (insightLoading ? (
                  <Box sx={{ background: "#f5f6fb", borderRadius: "14px", p: "10px", textAlign: "center", fontSize: "11.5px", color: "#98a2b3" }}>
                    Loading project insight…
                  </Box>
                ) : insight && (
                  <Box sx={{ background: "linear-gradient(155deg, #eef2ff 0%, #e0e7ff 100%)", border: "1px solid #c7d2fe", borderRadius: "14px", p: "10px 12px", boxShadow: "0 5px 16px rgba(79,70,229,0.12)" }}>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: "8px" }}>
                      <Box sx={{ fontSize: "10px", fontWeight: 800, color: "#4338ca", textTransform: "uppercase", letterSpacing: "0.05em" }}>Financial Insight</Box>
                      <Box sx={{ fontSize: "11px", color: "#4338ca", fontWeight: 700 }}>Expenses: {formatCurrency(insight.totalExpenses)}</Box>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "rgba(255,255,255,0.55)", borderRadius: "11px", p: "8px 12px" }}>
                      <Box>
                        <Box sx={{ fontSize: "9px", fontWeight: 800, color: "#6366f1", textTransform: "uppercase", letterSpacing: "0.05em" }}>Current Margin</Box>
                        <Box sx={{ fontSize: "17px", fontWeight: 800, color: "#1e1b4b" }}>{insight.currentMarginPct.toFixed(1)}%</Box>
                      </Box>
                      {newStatus === "Approved" && (
                        <>
                          <Box sx={{ fontSize: "15px", color: "#818cf8" }}>→</Box>
                          <Box sx={{ textAlign: "right" }}>
                            <Box sx={{ fontSize: "9px", fontWeight: 800, color: "#6366f1", textTransform: "uppercase", letterSpacing: "0.05em" }}>If Approved</Box>
                            <Box sx={{ fontSize: "17px", fontWeight: 800, color: insight.projectedMarginPct < insight.currentMarginPct - 5 ? "#dc2626" : "#1e1b4b" }}>
                              {insight.projectedMarginPct.toFixed(1)}%
                            </Box>
                          </Box>
                        </>
                      )}
                    </Box>
                  </Box>
                ))}

                {/* Documents Uploaded — real files (images, PDFs, docs) if any were attached */}
                <Box sx={{ background: "#fff", border: "1px solid #eef1f5", borderRadius: "16px", p: "14px 16px" }}>
                  <Box sx={{ fontSize: "11px", color: "#98a2b3", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", mb: "10px" }}>Documents Uploaded</Box>
                  {selectedRequest.images && selectedRequest.images.length > 0 ? (
                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px" }}>
                      {selectedRequest.images.map((img) => (
                        <Box
                          key={img.key}
                          component="a"
                          href={img.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ aspectRatio: "1", borderRadius: "12px", overflow: "hidden", border: "1px solid #e4e7ec", display: "block", background: "#fafbfc" }}
                        >
                          {isImageFile(img.mimetype) ? (
                            <img src={img.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                          ) : (
                            <Box sx={{ width: "100%", height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "2px", p: "4px" }}>
                              <InsertDriveFileIcon sx={{ fontSize: 22, color: "#667085" }} />
                              <Box sx={{ fontSize: "8px", color: "#667085", textAlign: "center", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", width: "100%" }}>
                                {img.name || "Document"}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "8px" }}>
                      {[0, 1, 2].map((i) => (
                        <Box key={i} sx={{ aspectRatio: "1", border: "1.5px dashed #d0d5dd", borderRadius: "12px", display: "flex", alignItems: "center", justifyContent: "center", background: "#fafbfc" }}>
                          <span style={{ fontSize: "22px", color: "#d0d5dd" }}>+</span>
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>

                <Box>
                  <Box sx={{ fontSize: "11px", fontWeight: 700, color: "#98a2b3", textTransform: "uppercase", letterSpacing: "0.06em", mb: "8px" }}>
                    Set status
                  </Box>
                  <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
                    {availableStatuses.map((status) => (
                      <Box
                        key={status}
                        onClick={() => {
                          setNewStatus(status);
                          // Approval date is silent/automatic — no picker shown
                          if (status === "Approved") setApprovalDate(new Date().toISOString().split('T')[0]);
                        }}
                        sx={{
                          textAlign: "center",
                          py: "11px",
                          borderRadius: "12px",
                          fontSize: "13.5px",
                          fontWeight: 700,
                          cursor: "pointer",
                          userSelect: "none",
                          border: `1.5px solid ${newStatus === status ? getApprovalColor(status) : "#e4e7ec"}`,
                          background: newStatus === status ? getApprovalColor(status) + "40" : "#fff",
                          color: "#101828",
                          transition: "all 0.15s ease",
                        }}
                      >
                        {statusLabel(status)}
                      </Box>
                    ))}
                  </Box>
                </Box>
                {newStatus === "Rejected" && (
                  <TextField label="Reason for Rejection" value={rejectionReason} onChange={(e) => setRejectionReason(e.target.value)} multiline rows={3} sx={vIn} fullWidth />
                )}
                {newStatus === "Pending" && (
                  <TextField label="What needs to be corrected? *" value={pendingReason} onChange={(e) => setPendingReason(e.target.value)} multiline rows={3} sx={vIn} fullWidth />
                )}
                {/* Approval date is captured silently as today's date — no field shown */}
                <Button
                  onClick={() => {
                    if (newStatus === "Paid") {
                      if (!paymentDate) setPaymentDate(new Date().toISOString().split('T')[0]);
                      setMarkPaidDialogOpen(true);
                    } else {
                      handleStatusSave();
                    }
                  }}
                  sx={{ py: 1.5, borderRadius: "14px", background: "linear-gradient(135deg,#39aafc,#1f8fe8)", color: "#fff", fontWeight: 700, textTransform: "none", fontSize: "16px", boxShadow: "0 8px 20px rgba(31,143,232,0.4)" }}
                  fullWidth
                >
                  {submitLabel}
                </Button>
              </Box>
            </div>
          </>
        );
      })()}

      {/* "Mark as Paid" confirmation — separate deliberate final step, date defaults to today */}
      <Dialog
        open={markPaidDialogOpen}
        onClose={() => setMarkPaidDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "24px", overflow: "hidden" } }}
      >
        <Box sx={{ background: "linear-gradient(155deg, #34d399 0%, #059669 100%)", pt: 4, pb: 3, px: 3, textAlign: "center", color: "#fff" }}>
          <Box sx={{ width: 60, height: 60, borderRadius: "50%", background: "rgba(255,255,255,0.22)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
            <CurrencyRupeeRoundedIcon sx={{ fontSize: 30 }} />
          </Box>
          <Box sx={{ fontSize: "19px", fontWeight: 800, fontFamily: "Roboto, Arial, sans-serif" }}>Confirm Payment</Box>
          {selectedRequest && (
            <Box sx={{ fontSize: "13px", opacity: 0.9, mt: "4px", fontFamily: "Roboto, Arial, sans-serif" }}>{selectedRequest.vendorName}</Box>
          )}
        </Box>
        <DialogContent sx={{ pt: "24px !important", pb: "8px !important", px: "28px" }}>
          {selectedRequest && (
            <Box sx={{ textAlign: "center", mb: "20px" }}>
              <Box sx={{ fontSize: "11px", color: "#98a2b3", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", fontFamily: "Roboto, Arial, sans-serif" }}>Amount</Box>
              <Box sx={{ fontSize: "28px", fontWeight: 800, color: "#101828", fontFamily: "Roboto, Arial, sans-serif" }}>{formatCurrency(selectedRequest.amount)}</Box>
            </Box>
          )}
          <TextField
            label="Date of Payment"
            type="date"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
          />
        </DialogContent>
        <DialogActions sx={{ flexDirection: "column", gap: "10px", px: "28px", pb: "24px", pt: "16px" }}>
          <Button
            fullWidth
            variant="contained"
            onClick={async () => { await handleStatusSave(); setMarkPaidDialogOpen(false); }}
            disabled={!paymentDate}
            sx={{
              textTransform: "none",
              fontSize: "15px",
              fontWeight: 700,
              py: "12px",
              borderRadius: "12px",
              background: "linear-gradient(135deg,#34d399,#059669)",
              boxShadow: "0 8px 20px rgba(5,150,105,0.35)",
              "&:hover": { background: "linear-gradient(135deg,#10b981,#047857)" },
            }}
          >
            Confirm Paid
          </Button>
          <Button
            fullWidth
            onClick={() => setMarkPaidDialogOpen(false)}
            sx={{ textTransform: "none", fontSize: "14px", fontWeight: 600, color: "#667085" }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VendorManagement;
